import { createSelector } from "@reduxjs/toolkit";
import createCachedSelector from "re-reselect";
import { getAreaState, getCurrentValue2, getAreaBoxes, getAreaGrid, getAreaFineGrid } from "reduxModules/selectors/areaSelectors";
import toolIndex from "toolConstants";
import { closest, side, calculateAimingAngles, calculateSpeakerRotation, calcMaxTilesFit } from "utilities/mathematic";
import { calculateCoordinates, getCollisions, getOverlaps, isInside, getDistanceBetween, getSpeakerAxisCount } from "utilities/geometric";
import { resolveExpansionSpeakerBox, calcExpansionCeilingMicPlacement, getExpansionCeilingIsFocused, getExpansionSpeakerIsFocused, MIN_MIC_SPKR_DISTANCE_M } from "toolConstants/conferenceroomDesigner/logic";
import { evaluateTileLanding, TileLandingOutcome } from "reduxModules/calculations/evaluateTileLanding";
import { Device as GeometricDevice } from "types/utilities/geometric";
import { DeviceError } from "types/middleware/calculateDevicesMiddleware";
import { Customized } from "types/redux/container";
import { RootState } from "types/redux/state";
import { clamp, isEqual, mapValues, max, min, values, indexOf, findKey, round, pick, mapKeys, ceil, inRange, find, every, some, minBy, sortBy, trimEnd, omit, isEmpty, floor } from "lodash";
import MuiTableConstants from "config/tables/tableConstants";
import { getDrawingBox, getIsMeter, getScale } from "reduxModules/ducks/ui";
import { pluralize } from "utilities/string";
import { familyMicModelName } from "utilities/micFamily";
import { isDevioUcxDesign } from "utilities/treeFrogBundleMatching";


/**
 * A ceiling-tile grid center, as produced by `getCenters`. Square tiles emit a
 * single center (mask 0); rectangular tiles emit one per half (mask -1 / +1),
 * sharing the same rx/ry as their sibling half.
 */
export interface TileCenter {
  x: number;
  y: number;
  rx?: number;
  ry?: number;
  mask?: number;
  blocked?: boolean;
  id?: string;
}

/**
 * A rectangular placement box in room coordinates, as produced by `getCoverageBoxes`
 * — either the full room or the "focused" area around the table.
 */
export interface CoverageBox {
  length: number;
  width: number;
  left: number;
  top: number;
  spkrAdj: number;
}

export interface CoverageBoxes {
  full_room: CoverageBox;
  focused: CoverageBox;
}

/** Ceiling-tile size lookup, keyed by tile-type name (e.g. "2x2", "2x4"). */
export type CeilingTileSizeMap = Record<string, { x: number; y: number }>;

/**
 * Result of a tile-snap search: the chosen tile center plus its true Euclidean
 * distance from the origin (so callers can apply the "only move if meaningful"
 * 0.01 threshold without recomputing).
 */
export interface NearestTileResult {
  tile: TileCenter;
  distance: number;
}

/**
 * Find the nearest unblocked tile center to `origin` within `maxMovementDistance`.
 *
 * Shared by the BMA360D mic, expansion-mic, expansion-speaker, and ceiling-speaker
 * snap loops, which all previously inlined the same
 * `filter(blocked/finite/within-radius) → minBy(hypot) → distance` sequence (the
 * Euclidean distance was computed up to five times per device). Returns the chosen
 * tile and its distance from `origin`, or `null` when no unblocked tile is reachable.
 *
 * @param centers              Candidate tile centers (square + both rectangular halves).
 * @param origin               The device's current location.
 * @param maxMovementDistance  Snap radius (per spec, min(tile.x, tile.y) × √2).
 * @param containmentTest      Optional predicate; when it returns true for a candidate,
 *                             that candidate wins outright (ranked as distance 0). Used by
 *                             BMA360D to prefer the (sub)tile that actually contains the
 *                             drop point over a marginally closer neighbor or the other
 *                             half of a rectangular tile. The returned `distance` is still
 *                             the true Euclidean distance, so the move-threshold is unaffected.
 */
export function findNearestUnblockedTile(
  centers: TileCenter[] | null | undefined,
  origin: { x: number; y: number },
  maxMovementDistance: number,
  containmentTest?: (center: TileCenter) => boolean,
): NearestTileResult | null {
  if (!centers || centers.length === 0) return null;

  // Per-center blocked filter: each half of a rectangular (2'×4' / 4'×2') tile is its own
  // center with its own blocked flag, so a half-blocked tile's free half remains available —
  // the device simply snaps to that free half (the same way the ceiling speaker does).
  const available = centers.filter((center: TileCenter) => {
    if (!center || center.blocked) return false;
    if (typeof center.x !== 'number' || typeof center.y !== 'number') return false;
    return getDistanceBetween(center, origin) <= maxMovementDistance;
  });
  if (available.length === 0) return null;

  const tile = minBy(available, (center: TileCenter) =>
    containmentTest && containmentTest(center) ? 0 : getDistanceBetween(center, origin),
  );
  if (!tile) return null;

  return { tile, distance: getDistanceBetween(tile, origin) };
}

/**
 * Marks the outermost, wall-touching ring of ceiling tiles as `blocked` so an AUTO-placed ceiling
 * speaker snaps to an INTERIOR tile instead of sitting right against a wall. A center is "perimeter"
 * when it lies within one tile of any wall on its own axis — the outer-ring centers sit ~½ tile from
 * the wall and the next ring ~1½ tiles, so a one-tile threshold catches exactly the outer ring (and
 * both halves of a rectangular tile, whose sub-centers are only ±¼ tile off-centre). `x`/`y` already
 * include `ceilingTransform`, so the test is room-relative.
 *
 * ORs with any existing `blocked` (never un-blocks a user-checked / mic-coincident tile) and returns
 * a NEW array (never mutates the input). If excluding the ring would leave NO unblocked tile (a room
 * too small to have an interior tile), the rule disables itself and the input is returned unchanged
 * so speakers are never stranded.
 *
 * @param centers   Candidate tile centers (from getCenters, already room-clipped).
 * @param room      Room box in room coordinates: { left, top, length, width }.
 * @param tileSize  Full ceiling-tile size { x, y }.
 * @returns Centers with the perimeter ring blocked, or the input unchanged (fallback/guards).
 */
export function blockPerimeterTilesForSpeakers(
  centers: TileCenter[] | null | undefined,
  room: { left: number; top: number; length: number; width: number },
  tileSize: { x: number; y: number } | null | undefined,
): TileCenter[] {
  if (!centers || centers.length === 0) return centers ?? [];
  if (!tileSize || !(tileSize.x > 0) || !(tileSize.y > 0)) return centers;

  const isPerimeter = (c: TileCenter): boolean =>
    (c.x - room.left) < tileSize.x ||
    (room.left + room.length - c.x) < tileSize.x ||
    (c.y - room.top) < tileSize.y ||
    (room.top + room.width - c.y) < tileSize.y;

  const result = centers.map((c: TileCenter) =>
    (c.blocked !== true && isPerimeter(c)) ? { ...c, blocked: true } : c,
  );

  // Fallback: if the ring exclusion left no free tile, don't apply the rule.
  return result.some((c: TileCenter) => c.blocked !== true) ? result : centers;
}

/**
 * Pulls placed ceiling expansion-speaker coords INTO the interior tile band (≥ ~1½ tiles from every
 * wall) before tile-snapping. The anchored grid places its far column right at the far wall, and the
 * tile-snap move budget (≈ √2 × tile) is too small to relocate that speaker to the interior tile — so
 * blocking the perimeter ring alone strands it on the wall. Clamping the coords into the interior
 * band first lands them on an interior tile centre. Mutates `coords` in place. No-op (leaves coords
 * as-is) when the room is too small to have an interior band, so speakers are never over-pulled.
 *
 * @param coords    Placed speaker coords (mutated).
 * @param room      Room box { left, top, length, width }.
 * @param tileSize  Full ceiling-tile size { x, y }.
 */
export function clampSpeakerCoordsToInteriorTiles(
  coords: GeometricDevice[] | null | undefined,
  room: { left: number; top: number; length: number; width: number },
  tileSize: { x: number; y: number } | null | undefined,
): void {
  if (!coords || coords.length === 0) return;
  if (!tileSize || !(tileSize.x > 0) || !(tileSize.y > 0)) return;
  const xLo = room.left + 1.5 * tileSize.x, xHi = room.left + room.length - 1.5 * tileSize.x;
  const yLo = room.top + 1.5 * tileSize.y, yHi = room.top + room.width - 1.5 * tileSize.y;
  if (!(xHi > xLo) || !(yHi > yLo)) return; // no interior band → leave coords untouched
  coords.forEach((d: GeometricDevice) => {
    if (!d?.location) return;
    d.location.x = clamp(d.location.x, xLo, xHi);
    d.location.y = clamp(d.location.y, yLo, yHi);
  });
}

/**
 * Tile-relocation gate (PIG-389). A device the user has already placed (customised layout)
 * may only relocate to a DIFFERENT ceiling tile when the tile size changes — i.e. on a
 * `setCeilingTiles` recalc. On every other recalc it stays on its current tile. Auto/initial
 * placement (non-customised) is unaffected and always snaps to the grid.
 *
 * This is a module-level flag (set by calculateDevicesMiddleware just before it invokes
 * getDeviceLayouts) because getDeviceLayouts is a memoised selector and can't take a
 * transient argument without polluting its cache key. Defaults to false so the safe
 * behaviour (no relocation) holds unless a tile-size change explicitly enables it.
 */
let tileRelocationAllowed = false;
export const setTileRelocationAllowed = (allowed: boolean): void => {
  tileRelocationAllowed = allowed;
};

/**
 * Whole-tile centring for ceiling mics that occupy an ENTIRE tile (TCM-X, CBC
 * CM/PM, DCM-X). BMA360D does NOT use this — it sits in one half and snaps to the
 * side that fits.
 *
 * On a square tile the snap target (mask 0) is already the tile centre, so it's
 * returned unchanged. On a rectangular tile getCenters only emits the two
 * half-centres (mask -1 / +1); the tile's true centre is their midpoint, so we
 * average the chosen half with its sibling (same rx/ry, opposite mask). Falls back
 * to the tile's own coords if no sibling is found (whole-tile centring).
 */
export function getFullTileCenter(
  tile: TileCenter | null,
  centers: TileCenter[] | null | undefined,
): { x: number; y: number } {
  if (!tile) return { x: 0, y: 0 };
  if (!tile.mask || !centers) return { x: tile.x, y: tile.y };
  const sibling = centers.find(
    (c: TileCenter) => c && c.rx === tile.rx && c.ry === tile.ry && c.mask !== tile.mask,
  );
  if (!sibling) return { x: tile.x, y: tile.y };
  return { x: (tile.x + sibling.x) / 2, y: (tile.y + sibling.y) / 2 };
}

/**
 * Computes a microphone's horizontal coverage radius from its acoustic-level
 * max slant distance and its mounting height, then applies the coverage
 * safeguards. Extracted from calculateMicPlacement (PIG-487) so the radius
 * reactivity — it must change with both acoustic level (via `micMaxDist`) and
 * mounting height — can be unit-tested directly.
 *
 * Geometry: `side(c, a) = √(c² − a²)`, the horizontal leg of the coverage cone
 * whose hypotenuse is the max slant distance and whose vertical leg is the
 * mic-to-talker height. A higher mic (larger `micHeight − talkerHeight`)
 * therefore yields a SMALLER horizontal radius for a fixed slant distance;
 * a better acoustic level yields a LARGER `micMaxDist` and thus a larger radius.
 *
 * Safeguards (unchanged from the original inline logic):
 *  - invalid (≤ 0 or NaN) radius → 1.0 m floor (keeps mics inside the room)
 *  - poor acoustics with a sub-0.8 m radius → 0.8 m floor (boundary issues)
 *  - pendant mics with good acoustics and a sub-1.5 m radius → 1.5 m floor
 *
 * @param micMaxDist   Max slant coverage distance for the area's acoustic level.
 * @param micHeight    Microphone mounting height.
 * @param talkerHeight Reference talker height.
 * @param areaAcoustics The area's acoustic level ('good' | 'average' | 'poor' | …).
 * @param isPendantMic Whether the mic is a pendant model (affects the 1.5 m floor).
 * @returns The safeguarded horizontal coverage radius, rounded to 4 dp.
 */
export const computeMicRadius = (
  micMaxDist: number,
  micHeight: number,
  talkerHeight: number,
  areaAcoustics: string,
  isPendantMic = false,
): number => {
  let micRadius = round(side(micMaxDist, (micHeight - talkerHeight)), 4);

  // Safeguard: ensure minimum radius to prevent positioning issues.
  // PIG-295 AC2/AC3: BMA360D is auto-lowered upstream so it always reaches here
  // with a valid radius; the 1.0 m fallback remains for non-tile mics (e.g. TCM-X).
  if (micRadius <= 0 || isNaN(micRadius)) {
    console.warn('Microphone radius calculation resulted in invalid value:', { micRadius, micMaxDist, micHeight, talkerHeight, acoustics: areaAcoustics });
    micRadius = 1.0;
  } else if (areaAcoustics === 'poor' && micRadius < 0.8) {
    console.warn('Poor acoustics: Adjusting small radius to prevent boundary issues:', { originalRadius: micRadius, acoustics: areaAcoustics });
    micRadius = 0.8;
  }

  // Pendant mics with good acoustics need reasonable coverage.
  if (isPendantMic && areaAcoustics === 'good' && micRadius < 1.5) {
    console.warn('Pendant mic with good acoustics: Adjusting radius for better coverage:', { originalRadius: micRadius });
    micRadius = Math.max(1.5, micRadius);
  }

  return micRadius;
};

// fixme this doesn't need to export - exporting just for testing in Devices
/**
 * Selector to get coverage boxes for the current area.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Object} An object containing coverage boxes for 'full_room' and 'focused' scenarios.
 */
/**
 * Pure combiner for `getCoverageBoxes` — extracted so a caller with a plain `area` object (no
 * Redux state), e.g. a reducer running a dry-run placement preview, can compute the same
 * coverage boxes without duplicating this math. `getCoverageBoxes` itself stays a thin selector
 * wrapper around this.
 */
export const computeCoverageBoxesForArea = (area: any, constants: any) => {
  const { dimensions, location, table } = area;
  const { area: { table2wall }, tables } = constants;
  const endTable = tables[table?.shape]?.endTable ?? false;
  return {
    full_room: {
      length: dimensions.length,
      width: dimensions.width,
      left: location.left,
      top: location.top,
      spkrAdj: 0
    },
    focused: {
      // When there's no table, use 70% of room dimensions for focused coverage
      length: table.shape ? table.size.length + table2wall * (endTable ? 1 : 2) : dimensions.length * 0.7,
      width: table.shape ? table.size.width + table2wall * 2 : dimensions.width * 0.7,
      // Center the focused area when there's no table
      left: location.left + (table.shape ? (dimensions.length - table.size.length) * .5 - table2wall * (endTable ? 0 : 1) : dimensions.length * 0.15),
      top: location.top + (table.shape ? (dimensions.width - table.size.width) * .5 - table2wall : dimensions.width * 0.15),
      spkrAdj: table.shape ? 0 : 1
    }
  }
};

export const getCoverageBoxes = createSelector(
  state => getAreaState(state),
  state => toolIndex[state.container.currentTool].constants,
  computeCoverageBoxesForArea
);

/**
 * Creates a coordinate transformer function based on the current area and table transform.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Function} A function that transforms coordinates based on the current area and table position.
 */
const createCoordinateTransformer = createSelector(
  state => getAreaBoxes(state),
  state => getCurrentValue2(state, "table.transform"),
  (areaBoxes, tableTransform) => {
    const areaBox = areaBoxes[0];
    if (!areaBox) {
      return ({ x, y }) => ({ x, y }); // Return identity function if no areaBox
    }
    
    const angle = tableTransform.a * Math.PI / 180;
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);

    const transformPoint = ({ x, y }) => {
      const dx = x - areaBox.cx;
      const dy = y - areaBox.cy;
      return {
        x: areaBox.cx + (dx * cosAngle - dy * sinAngle) + tableTransform.x,
        y: areaBox.cy + (dx * sinAngle + dy * cosAngle) + tableTransform.y
      };
    };
    return transformPoint;
  }
);


/**
* @function createDeviceAdjutmentAndAim
* @description Creates a function that calculates device position adjustments and aim points.
* 
* @param {Object} state - Redux state
* @returns {Function} Device adjustment function
* 
* @callback adjustAndAim
* @param {string} deviceType - Type of device ('cameras', 'speakers', 'microphones')
* @param {Object} device - Device object with location and model information
* @param {boolean} [render=false] - Whether adjustment is for rendering purposes
* @returns {Object} Adjusted device data
* @property {string} model - Device model identifier
* @property {string} modelName - Full model name
* @property {Object} location - Adjusted device location
* @property {number} location.x - Adjusted x coordinate
* @property {number} location.y - Adjusted y coordinate
* @property {Object} aimPoint - Point where device should aim
* @property {number} [horAngle] - Horizontal angle for cameras (0-360°)
* 
* @description
* Creates a function that:
* 1. Adjusts device positions based on type and mounting location:
*    - Bar-mounted devices get wall offset adjustments
*    - Positions are clamped to valid room coordinates
* 2. Calculates aim points:
*    - Cameras aim at table center if present, room center otherwise
*    - Bar devices aim based on their wall position
* 3. Computes angles:
*    - Camera horizontal angles based on aim point
*    - Angles normalized to 0-360° range
* 
* @example
* const adjustDevice = createDeviceAdjutmentAndAim(state);
* const adjusted = adjustDevice('cameras', {
*   model: 'Camera1',
*   modelName: 'PTZ Camera',
*   location: { x: 10, y: 20 }
* });
* // Returns {
* //   model: 'Camera1',
* //   modelName: 'PTZ Camera',
* //   location: { x: 10, y: 20 },
* //   aimPoint: { x: 15, y: 25 },
* //   horAngle: 45
* // }
* 
* @note
* - Bar fence offset is defined in constants.equipment.wallMountedBarOffset
* - Render mode skips position adjustments but keeps aim calculations
* - Uses room diagonal for determining wall positions
*/
export const createDeviceAdjutmentAndAim = createSelector(
  (state) => getDrawingBox(state), // wrapped to avoid TDZ from ui.ts ↔ container.ts ↔ room.ts ↔ roomCalculations.ts ↔ ui.ts cycle
  state => getCurrentValue2(state, 'table'),
  state => toolIndex[state.container.currentTool].constants.equipment,
  //(state, deviceType, device, render) => ({ deviceType, device, render: render ?? false }),
  (drawingBox, table, equipment) => {
    const { bbox, scale } = drawingBox;
    const { x0, y0, cx, cy } = bbox;
    const { shape, transform: tableCenter } = table;
    const hasTable = Boolean(shape);
    const barFence = .134;

    const adjustAndAim = (deviceType, device, render = false) => {
      const { model, modelName, location, aimPoint: existingAimPoint } = device;
      const isCamera = deviceType === 'cameras';
      const adjustment = { x: 0, y: 0 };
      const { isBarSpkr = false, isWallSpkr = false } = equipment?.[deviceType]?.[model] ?? {};
      const isBarMic = equipment?.micCategories?.BAR.has(model) ?? false;
      // Calculate adjustment 
      // For cameras, always calculate aim point fresh (ignore existing as it may be wrong)
      // For other devices, default to their location
      let aimPoint = isCamera ? { x: 0, y: 0 } : { ...location };
      if (isBarMic || isBarSpkr || isWallSpkr || isCamera) {
        const { x, y } = mapValues(location, (value, key) => value * scale + bbox[`${key}0`]);
        // Only set aimPoint to location for non-camera devices
        if (!isCamera) {
          aimPoint = { ...location };
        }

        // Calculate the angle of the device in degrees
        let angle = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 180;
        angle = angle % 360;

        // Calculate the room diagonal angle
        const roomDiagonal = Math.atan2(y0 - cy, x0 - cx) * (180 / Math.PI) + 180;
        // Determine barSide and calculate adjustments
        // For cameras, we only adjust position, not aim point (since we'll calculate aim point later)
        if (angle <= roomDiagonal) { // left side
          adjustment.x = -barFence;
          if (!isCamera) aimPoint.x += barFence;
        } else if (angle <= 180 - roomDiagonal) { // top side
          adjustment.y = -barFence;
          if (!isCamera) aimPoint.y += barFence;
        } else if (angle <= 180 + roomDiagonal) { // right side
          adjustment.x = barFence;
          if (!isCamera) aimPoint.x -= barFence;
        } else if (angle <= 360 - roomDiagonal) { // bottom side
          adjustment.y = barFence;
          if (!isCamera) aimPoint.y -= barFence;
        } else { // left side (wrap aroound)
          adjustment.x = -barFence;
          if (!isCamera) aimPoint.x += barFence;
        };
      };
      const adjustedLocation = {
        x: Math.max(0, (location.x + (render ? 0 : adjustment.x))),
        y: Math.max(0, (location.y + (render ? 0 : adjustment.y))),
        ...omit(location, ['x', 'y']),
        // Include z from device.height if location.z is not already set (for speakers)
        ...(!location.z && isFinite(device.height) && { z: device.height })
      };

      let horAngle;
      if (isCamera) {
        // For cameras, use the existing horAngle if it exists (from calculateCameraPlacement)
        // This avoids coordinate system mismatches between room coords and drawing box coords
        // Check if aimPoint has valid x and y coordinates (not just an empty object)
        const hasValidAimPoint = existingAimPoint &&
                                  typeof existingAimPoint.x === 'number' &&
                                  typeof existingAimPoint.y === 'number';

        if (hasValidAimPoint && device.horAngle !== undefined) {
          horAngle = device.horAngle;
          aimPoint = { ...existingAimPoint };
        } else {
          // Fallback: calculate aim point using drawing box coordinates (legacy behavior)
          const roomCenterX = (cx - x0) / scale;
          const roomCenterY = (cy - y0) / scale;
          
          aimPoint.x = roomCenterX + (hasTable && tableCenter ? tableCenter.x : 0);
          aimPoint.y = roomCenterY + (hasTable && tableCenter ? tableCenter.y : 0);
          
          const deltaX = aimPoint.x - adjustedLocation.x;
          const deltaY = aimPoint.y - adjustedLocation.y;
          
          const mathAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
          horAngle = round((mathAngle + 360) % 360);
          if (horAngle === 360) horAngle = 0;
        }
      };
      return {
        model,
        modelName,
        location: adjustedLocation,
        aimPoint,
        // Add flat properties for compatibility with Canvas component
        x: adjustedLocation.x,
        y: adjustedLocation.y,
        aimX: aimPoint.x,
        aimY: aimPoint.y,
        ...isFinite(horAngle) && { horAngle },
        // Preserve original device angles if not calculated above (for speakers)
        ...(!isFinite(horAngle) && isFinite(device.horAngle) && { horAngle: device.horAngle }),
        ...isFinite(device.verAngle) && { verAngle: device.verAngle }
      };
    };
    return adjustAndAim
  }
);



/**
 * Shared output shape for the two expansion-device helpers below.
 *   - `coords`: final list of placed devices, with z-height and tile snapping already applied
 *   - `placementError`: count of devices that couldn't be snapped to an unblocked tile
 */
interface ExpansionComputeResult {
  coords: GeometricDevice[];
  placementError: number;
}

/**
 * Returns true when ANY device on the area has been manually customised. Once the user
 * touches any device, auto-placement freezes across the entire design — moving a primary
 * mic shouldn't shift expansion speakers via the proximity filter, etc.
 *
 * Applied today at the two expansion helpers below (the place where the cross-device
 * recalc was demonstrable). The main-body branches in `getDeviceLayouts` continue to
 * use per-device customised flags; in practice their non-customised recalc produces
 * identical output because primary placement is mic-position-independent. Broaden
 * usage if a new path surfaces a recalc difference.
 */
export const isAnyDeviceCustomized = (customized: Partial<Customized>): boolean => Boolean(
  customized?.microphones
  || customized?.speakers
  || customized?.cameras
  || customized?.expansionMicrophones
  || customized?.expansionSpeakers,
);

/**
 * Snap a set of expansion CEILING device coords to ceiling-tile centres — the same rule the
 * primary ceiling mics / speakers follow: each device moves to its nearest unblocked tile centre
 * (so it sits centred in the tile) and reserves that tile; a device whose tile is blocked relocates
 * to the nearest free neighbour (≤ √2 tiles) and, if none is reachable, is counted as a placement
 * error. Mutates `coords` in place and returns the placement-error count. Used by both the
 * auto-placement and the customised (frozen) branches so a manually-dragged expansion device is
 * centred too — not left at its raw drop point.
 */
export const snapExpansionCoordsToTiles = (
  coords: GeometricDevice[],
  centers: TileCenter[],
  currentTileSize: { x: number; y: number },
): number => {
  const maxMovementDistance = Math.min(currentTileSize.x, currentTileSize.y) * 1.4142;
  let availableCenters = centers.map((c: TileCenter) => ({ ...c }));
  let placementError = 0;
  for (const dev of coords) {
    const originalLocation = { ...dev.location };
    const nearest = findNearestUnblockedTile(availableCenters, originalLocation, maxMovementDistance);
    if (nearest) {
      if (nearest.distance > 0.01) {
        dev.location = { ...dev.location, x: nearest.tile.x, y: nearest.tile.y };
      }
      availableCenters = blockTiles(availableCenters, nearest.tile);
    } else {
      placementError++;
    }
  }
  return placementError;
};


/**
 * Computes expansion ceiling-mic positions for the bar + expansion configuration.
 *
 * Three branches, mirroring the user-facing flows:
 *   1. Customised — preserve the stored layout. Triggered by `customized.expansionMicrophones`
 *      (set by addExpansionMic / removeExpansionMic / placeExpansionMicAtLocation).
 *   2. Expansion REQUIRED (bar leaves a gap) — delegate Steps 1–7 to
 *      `calcExpansionCeilingMicPlacement` (logic.js), the single shared implementation of the
 *      spec's expansion-box + jump-point-cap + CENTRED GRID algorithm. See
 *      Documentation/specs/tree-frog-product-specifications.md §5.5.
 *   3. Expansion NOT required (edge case A or B) but the user opted in via the menu — the spec
 *      (§5.2) says this path runs *standard standalone ceiling mic placement on the full base
 *      box*, NOT the expansion box. That's the shared `calculateMicPlacement` core, which for
 *      round/keystone/vshape focused coverage traces the table's perimeter path exactly as a
 *      standalone TCM-X would.
 *
 * The distinction between 2 and 3 matters. Branch 2 previously also went through the perimeter
 * path with a `restrictToBox` confined to the residual arc; because that arc is centred on a
 * keystone/vshape table's narrow end, an even mic count placed a mirror-image pair on the two
 * converging legs — physically ~0.4 m apart despite a ~2.5 m coverage radius. The spec has never
 * specified path tracing for the expansion box; Step 7 is a rectangular centred grid.
 * See Documentation/BAR_EXPANSION_CEILING_MIC_CHANGES_VS_SPEC.md §A2.
 *
 * Returns `{ coords: [], placementError: 0 }` for non-ceiling expansion types — table
 * expansion is driven by the middleware's `tableExpansionStrategy` (DOM-coupled, needs
 * the rendered micPath).
 */
const computeExpansionCeilingMicCoords = (input: {
  area: any;
  customized: Partial<Customized>;
  coverageBoxes: CoverageBoxes;
  acoustics: string[];
  micModel: string;
  equipment: any;
  constants: any;
  hasTiles: boolean;
  centers: TileCenter[] | null | undefined;
  tile: string;
  ceilingTiles: CeilingTileSizeMap;
  scale: number;
  talkerHeight: number;
}): ExpansionComputeResult => {
  const { area, coverageBoxes, acoustics, micModel, equipment, constants, hasTiles, centers, tile, ceilingTiles, scale, talkerHeight } = input;
  // Defensive normalisation — legacy persisted designs predating the `customized` shape
  // can land here with `customized === undefined`. Coerce once at the top so the rest of
  // the helper doesn't have to worry about it.
  const customized = input.customized ?? {};
  if (area.expansionMicType !== 'ceiling') return { coords: [], placementError: 0 };

  // Design-wide manual-edit lock: if the user has customised ANY device, freeze the
  // expansion mic layout too. Without this, customising a primary device and then
  // tripping a recalc would re-run the placement against the moved mic positions and
  // shift the result. The customised branch reads from the stored layout — which is
  // either the most recent auto-placement or the user's manual edits.
  const isExpansionCustom = isAnyDeviceCustomized(customized);
  const storedMicLayout = (area.expansionMicrophones?.layout ?? []) as GeometricDevice[];
  // Freeze only when there's an EXISTING layout to preserve. When the user has just enabled
  // ceiling expansion mics (empty layout) the design-wide lock must NOT block the initial
  // auto-placement — otherwise enabling them adds nothing while any other device is customised.
  if (isExpansionCustom && storedMicLayout.length > 0) {
    // Preserve the stored layout, but still snap it to the tile grid so a manually-dragged
    // expansion ceiling mic ends up centred in its tile (idempotent once centred). Same rule as
    // the primary ceiling mics.
    const coords = storedMicLayout.map(d => ({ ...d, location: { ...d.location } }));
    let placementError = 0;
    if (hasTiles && centers) {
      const currentTileSize = ceilingTiles?.[tile];
      if (currentTileSize && typeof currentTileSize.x === 'number') {
        placementError = snapExpansionCoordsToTiles(coords, centers, currentTileSize);
      }
    }
    return { coords, placementError };
  }

  const isFocused = getExpansionCeilingIsFocused(area);
  const baseBox = coverageBoxes[isFocused ? 'focused' : 'full_room'];
  const acousticIndex = indexOf(acoustics, area.acoustics);
  // Ceiling expansion mic mounting height: the group's own configured height (user-editable,
  // defaults to flush ceiling height — see setExpansionMicType/placeExpansionMicAtLocation in
  // room.ts), falling back to raw ceiling height. NOT `area.microphones.height` — the primary
  // mic here is always a non-CBC bar (table/wall mounted), so its height is unrelated to a
  // ceiling mount.
  const expansionMicHeight = (area.expansionMicrophones as any)?.height
    ?? area.ceilingHeight?.min
    ?? area.ceilingHeight
    ?? 2.8956;
  // Branch-3 (standalone opt-in) inputs only. The shared `calculateMicPlacement` core is called
  // with model: 'tcm_x' for the math — dcm_x has identical MIC_CATEGORIES flags (ceiling, not
  // tile/pendant/bar) so this hits the exact same branch tcm_x gets, and it can't drift from
  // tcm_x's own equipment config the way dcm_x's separate catalog entry already has. Relabelled
  // to dcm_x immediately, before `coords` is fanned out to both `microphones.layout` (the
  // mirrored copy) and `expansionMicrophones.layout` — see MouseHandler.tsx's dedup key, which
  // needs both copies of the same physical mic to agree on `model`.
  const expMicCoverage = area.expansionMicCoverage ?? { full_room: !isFocused, focused: isFocused };
  const dcmMicData = equipment.microphones?.dcm_x?.micData;
  const dcmModelName = equipment.microphones?.dcm_x?.modelName ?? constants.expansionMicPlaceholders.ceiling;
  const dcmRadius = computeMicRadius(dcmMicData?.[acousticIndex], expansionMicHeight, talkerHeight, area.acoustics, false);
  const isEndTable = constants.tables?.[area.table?.shape]?.endTable ?? false;

  // Branch 3 helper — STANDARD STANDALONE ceiling mic placement over the full base box, used
  // only when the bar already covers the area and the user opted in anyway (spec §5.2). No
  // `restrictToBox`: this is deliberately the same placement a standalone TCM-X would get,
  // including round/keystone/vshape perimeter-path tracing.
  const placeStandaloneOverBaseBox = (): GeometricDevice[] => {
    const overlaps = getOverlaps(baseBox, dcmRadius, expMicCoverage, area.table, isEndTable);
    const result = calculateMicPlacement(
      area, overlaps, baseBox, dcmRadius, scale, constants, 0,
      { model: 'tcm_x', coverage: expMicCoverage, height: expansionMicHeight, isCustomized: false }
    );
    return (result.coords || []).map((d: any) => ({ ...d, model: 'dcm_x', modelName: dcmModelName }));
  };

  let coords: GeometricDevice[] = [];
  let placementError = 0;

  // Steps 1–7 (bar reach, edge cases A/C, expansion box + pull-back, minimum residual, capped
  // jump points, centred grid) live in ONE place — `calcExpansionCeilingMicPlacement`. The
  // recommendation dialog (`calculateBarCoverageGap`) and the `MuiExpansionBox` visualiser call
  // the same function, so what is recommended, what is drawn, and what is placed cannot disagree.
  const specResult: any = calcExpansionCeilingMicPlacement({
    room: { left: area.location.left },
    baseBox,
    acousticIndex,
    isFocused,
    barModel: micModel,
    // The expansion group's own configured mounting height, NOT area.microphones.height — the
    // primary here is always a non-CBC bar at table/wall height.
    ceilingHeight: expansionMicHeight,
  });

  if (specResult && specResult.qty > 0) {
    // `calcExpansionCeilingMicPlacement` already labels these dcm_x / DCM-X; only the mount
    // height needs applying (the grid core works in 2D).
    coords = (specResult.coords as GeometricDevice[]).map(d => ({
      ...d,
      location: { ...d.location, z: expansionMicHeight },
    }));
  } else if (area.expansionMicManualOptIn) {
    // Edge case A or B — the bar covers the area, so there is no residual to fill. The user
    // explicitly asked for expansion mics anyway, so run standard standalone placement across
    // the full base box (spec §5.2, "Menu + / coverage not required" row).
    coords = placeStandaloneOverBaseBox();
  }
  // No mics placed: expansion not required AND user did not opt in. Spec-compliant empty layout.
  // When that emptying is caused by an ACOUSTICS change that closed the gap outright (edge case
  // A), the middleware additionally resets `expansionMicType` to null and toasts — see the
  // ceiling branch of the acoustics-transition block in calculateDevicesMiddleware. Emptying from
  // any other cause leaves the type alone and is silent.

  // Tile snapping (Section 5.4) — applies to BOTH the gap-aware grid and the manual opt-in
  // fallback so a DCM-X always centres on a ceiling tile, never lands between tiles.
  if (hasTiles && centers && coords.length > 0) {
    const currentTileSize = ceilingTiles?.[tile];
    if (currentTileSize && typeof currentTileSize.x === 'number') {
      placementError += snapExpansionCoordsToTiles(coords, centers, currentTileSize);
    }
  }
  return { coords, placementError };
};

/**
 * Computes expansion ceiling/pendant speaker positions for the bar + expansion config.
 *
 * Runs only when `area.expansionSpeakerType` is 'ceiling' or 'pendant'. Resolves the residual
 * coverage box beyond the bar's own reach via `resolveExpansionSpeakerBox` (Case A/B), then
 * places against it using the SAME shared `calculateSpeakerPlacement` core the primary
 * ceiling/pendant speaker uses (centered grid, shape dispatch, even-pairing), fed an explicit
 * speakerConfig instead of area.speakers. The maxExpansionSpeakers cap — the one behavior the
 * primary path has no equivalent for — is applied here as a post-step. There is no more
 * mic-collision row-sliding; non-tile expansion speakers get the same (no) active mic-avoidance
 * a primary pendant speaker gets today.
 *
 * Z-height comes from the speaker equipment's `height(area)` function (defaults to
 * ceiling height) so pendant variants land at their configured drop.
 *
 * Returns `{ coords: [], placementError: 0 }` when no type is selected or no equipment
 * matches the model key.
 */
/**
 * Keep grid-placed expansion speakers at least a coverage-radius clear of the room walls. The
 * bar-expansion grid's far column can otherwise land right on the far wall, wasting that speaker's
 * coverage past the wall. Applies to pendant AND non-tiled ceiling expansion speakers (tile-snapped
 * ceilings position from the tile grid instead).
 *
 * PIG-611: this must move the grid as a RIGID BODY, not clamp each speaker independently. At tall
 * ceilings the coverage radius is large, so the valid band `[radius, dim-radius]` can be narrower
 * than the row/column pitch. The old per-speaker clamp then pulled the outer rows toward each other
 * until they nearly overlapped (e.g. 15' ceiling: two rows 5.46 m apart collapsed to 0.64 m).
 *
 * So per axis: if the room can't fit any gap (`hi <= lo`) or the placed grid's span exceeds the band
 * (`max-min > hi-lo`), leave that axis exactly as placed — the grid is already centred, and there is
 * no way to clear both walls without compressing it. Otherwise translate the whole grid by the
 * smallest amount that pushes it back inside the band, preserving every inter-speaker distance.
 */
export const clampExpansionCoordsToWallGap = (
  coords: GeometricDevice[],
  area: any,
  radius: number,
): GeometricDevice[] => {
  if (coords.length === 0) return coords;
  const shiftForAxis = (get: (d: GeometricDevice) => number, lo: number, hi: number): number => {
    if (hi <= lo) return 0;                 // room too narrow/shallow for any gap
    const vals = coords.map(get);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    if (max - min > hi - lo) return 0;      // grid span exceeds the band — can't clear both walls
    if (min < lo) return lo - min;          // whole grid too close to the low wall — shift up
    if (max > hi) return hi - max;          // whole grid too close to the high wall — shift down
    return 0;                               // already clear of both walls
  };
  const dx = shiftForAxis(d => d.location.x, area.location.left + radius, area.location.left + area.dimensions.length - radius);
  const dy = shiftForAxis(d => d.location.y, area.location.top + radius, area.location.top + area.dimensions.width - radius);
  if (dx === 0 && dy === 0) return coords;
  return coords.map(dev => ({
    ...dev,
    location: { ...dev.location, x: dev.location.x + dx, y: dev.location.y + dy },
  }));
};

/**
 * Nudge a centered expansion-speaker grid off nearby mics. Restores, for the current centered-grid
 * core, the spirit of the retired anchored-layout's mic-avoidance re-space (the `nearbyMics`
 * slide-and-retry that used to live in `calcBarExpansionPlacement`'s now-dead 'anchored' branch) —
 * that avoidance was lost when expansion speakers moved onto the shared centered-grid core, and
 * pendant speakers (smaller radius, pulled further toward the box centre) are the ones that
 * actually land close enough to a mic for it to show up as PIG proximity warnings.
 *
 * Moves the grid as a RIGID BODY, same reasoning as clampExpansionCoordsToWallGap above (PIG-611 —
 * shifting speakers independently can collapse rows into each other): tries a handful of candidate
 * whole-grid translations and keeps whichever leaves the fewest speakers within
 * MIN_MIC_SPKR_DISTANCE_M of any mic, clamped so the shifted grid stays inside `box`. Doesn't
 * guarantee zero violations — calculateDevicesMiddleware's proximity toast still fires on any that
 * remain, same as before this was added.
 */
export const avoidNearbyMics = (
  coords: GeometricDevice[],
  nearbyMics: GeometricDevice[],
  box: { left: number; top: number; length: number; width: number },
): GeometricDevice[] => {
  if (coords.length === 0 || nearbyMics.length === 0) return coords;

  const countViolations = (cds: GeometricDevice[]): number => cds.reduce((n, s) => {
    const tooClose = nearbyMics.some((m) => {
      const dx = (m.location?.x ?? 0) - s.location.x;
      const dy = (m.location?.y ?? 0) - s.location.y;
      return Math.hypot(dx, dy) < MIN_MIC_SPKR_DISTANCE_M;
    });
    return tooClose ? n + 1 : n;
  }, 0);

  let best = coords;
  let bestViolations = countViolations(coords);
  if (bestViolations === 0) return coords;

  const xs = coords.map(d => d.location.x);
  const ys = coords.map(d => d.location.y);

  // Shift step scales with the spacing BETWEEN ADJACENT speakers (the grid's own pitch), not the
  // grid's total span — span grows with speaker count, so for a 4+ speaker row it produced wildly
  // oversized candidate shifts (e.g. a whole-grid jump of over a meter to fix a violation that was
  // only centimeters over the threshold), which either got clamped away at the box edge or
  // overshot into a different violation without ever trying the small, precise nudge that would
  // actually clear it. Falls back to the proximity threshold itself for a single-point grid.
  const adjacentGap = (vals: number[]): number => {
    const sorted = [...new Set(vals)].sort((a, b) => a - b);
    if (sorted.length < 2) return 0;
    let min = Infinity;
    for (let i = 1; i < sorted.length; i++) min = Math.min(min, sorted[i] - sorted[i - 1]);
    return min;
  };
  const pitchX = adjacentGap(xs) || MIN_MIC_SPKR_DISTANCE_M;
  const pitchY = adjacentGap(ys) || MIN_MIC_SPKR_DISTANCE_M;

  // Clamp a candidate translation so every shifted coordinate stays inside `box` — same rigid-body
  // approach as the wall-gap clamp: adjust dx/dy uniformly rather than per-point.
  const clampDelta = (dx: number, dy: number) => {
    const minX = Math.min(...xs) + dx, maxX = Math.max(...xs) + dx;
    const minY = Math.min(...ys) + dy, maxY = Math.max(...ys) + dy;
    let cdx = dx, cdy = dy;
    if (minX < box.left) cdx += box.left - minX;
    if (maxX > box.left + box.length) cdx -= maxX - (box.left + box.length);
    if (minY < box.top) cdy += box.top - minY;
    if (maxY > box.top + box.width) cdy -= maxY - (box.top + box.width);
    return { dx: cdx, dy: cdy };
  };

  // Fractions of the pitch, smallest first — the search stops at the first candidate (in this
  // order) that clears every violation, so a small sufficient nudge is always preferred over a
  // larger one. x-axis candidates come before y-axis ones since expansion grids are typically
  // wider than tall, but both are tried in full.
  const fractions = [0.1, 0.15, 0.2, 0.25, 0.3, 0.4, 0.5, 0.65, 0.8, 1.0];
  const candidates: Array<[number, number]> = [
    ...fractions.flatMap((f): Array<[number, number]> => [[pitchX * f, 0], [-pitchX * f, 0]]),
    ...fractions.flatMap((f): Array<[number, number]> => [[0, pitchY * f], [0, -pitchY * f]]),
  ];
  for (const [rawDx, rawDy] of candidates) {
    if (bestViolations === 0) break;
    const { dx, dy } = clampDelta(rawDx, rawDy);
    if (dx === 0 && dy === 0) continue;
    const shifted = coords.map(d => ({ ...d, location: { ...d.location, x: d.location.x + dx, y: d.location.y + dy } }));
    const v = countViolations(shifted);
    if (v < bestViolations) { bestViolations = v; best = shifted; }
  }
  return best;
};

// Exported for direct unit testing (see barExpansionSpeaker.test.js) — this is the wrapper that
// owns Case A/B, the maxCount cap, and tile-snap/customization handling around the shared
// calculateSpeakerPlacement core.
export const computeExpansionSpeakerCoords = (input: {
  area: any;
  customized: Partial<Customized>;
  micCoords: GeometricDevice[];
  expansionMicCoords: GeometricDevice[];
  coverageBoxes: CoverageBoxes;
  equipment: any;
  talkerHeight: number;
  table: any;
  constants: any;
  hasTiles: boolean;
  centers: TileCenter[] | null | undefined;
  tile: string;
  ceilingTiles: CeilingTileSizeMap;
  scale: number;
}): ExpansionComputeResult => {
  // micCoords (primary mic layout) feeds avoidNearbyMics below, alongside expansionMicCoords, so
  // the non-tile mic-avoidance nudge sees every mic the middleware's own proximity check does.
  const { area, micCoords, expansionMicCoords, coverageBoxes, equipment, talkerHeight, table, constants, hasTiles, centers, tile, ceilingTiles, scale } = input;
  // Defensive normalisation — see note on the mic helper above. Legacy designs without
  // the `customized` shape land here as undefined; coerce so the lock check is uniform.
  const customized = input.customized ?? {};
  const isExpansionSpkrActive = area.expansionSpeakerType === 'ceiling' || area.expansionSpeakerType === 'pendant';
  if (!isExpansionSpkrActive) return { coords: [], placementError: 0 };

  // Manual-edit branch — parallels the customised mic path. Triggered when ANY device
  // is customised (design-wide lock), not just expansion speakers. This is what closes
  // the demonstrable cross-device recalc: moving a primary or expansion mic must not
  // shift expansion speakers via the proximity filter. The customised branch reads from
  // the stored layout, which is the most recent auto-placement or the user's edits.
  const isExpansionSpkrCustom = isAnyDeviceCustomized(customized);
  const storedSpkrLayout = (area.expansionSpeakers?.layout ?? []) as GeometricDevice[];
  // Freeze only when there's an EXISTING layout to preserve. When the user has just enabled
  // ceiling/pendant expansion speakers (empty layout) the design-wide lock must NOT block the
  // initial auto-placement — otherwise "Add expansion speakers" adds nothing while any other
  // device is customised. Once placed, the non-empty layout is preserved on later recalcs.
  if (isExpansionSpkrCustom && storedSpkrLayout.length > 0) {
    // Preserve the stored layout, but snap CEILING expansion speakers to the tile grid so a
    // manually-dragged unit ends up centred in its tile (idempotent once centred). Pendant
    // expansion speakers are not tile-centred here. Same rule as the primary ceiling speakers.
    const coords = storedSpkrLayout.map(d => ({ ...d, location: { ...d.location } }));
    let placementError = 0;
    if (area.expansionSpeakerType === 'ceiling' && hasTiles && centers) {
      const currentTileSize = ceilingTiles?.[tile];
      if (currentTileSize && typeof currentTileSize.x === 'number') {
        // Keep expansion ceiling speakers off the wall-touching tile on re-snap too (the stored
        // layout is the last auto-placement; re-centre it to interior tiles). Clamp coords into the
        // interior band first so the far-column speaker can reach an interior tile, then block the
        // perimeter ring so the snap lands there.
        const room = {
          left: area.location.left, top: area.location.top,
          length: area.dimensions.length, width: area.dimensions.width,
        };
        clampSpeakerCoordsToInteriorTiles(coords, room, currentTileSize);
        const centersForSnap = blockPerimeterTilesForSpeakers(centers, room, currentTileSize);
        placementError = snapExpansionCoordsToTiles(coords, centersForSnap, currentTileSize);
      }
    }
    return { coords, placementError };
  }

  // Prefer the user's chosen model from `area.expansionSpeakers.model` so LP / LP-TAA
  // variants pick up their own equipment config (coverageAngle, sensitivity, polarData).
  // Falls back to the type-default when no model is set yet (e.g. first ceiling-type
  // selection before the reducer's seed runs) — same behaviour as the legacy code path.
  const storedModel = area.expansionSpeakers?.model;
  const typeDefault = area.expansionSpeakerType === 'pendant' ? 'pendantspkr' : 'ceilingspkr';
  const expSpkrModel = storedModel && equipment.speakers[storedModel] ? storedModel : typeDefault;
  const expSpkrEquipment = equipment.speakers[expSpkrModel];
  if (!expSpkrEquipment) return { coords: [], placementError: 0 };

  // PIG-612: the placed z must reflect the user-configured hang height when set (mirrors the primary
  // speaker path); fall back to the model default only when no height is stored. PIG-610: this same
  // height now also drives the grid pitch via expSpkrRadius below (spec §6 Step 2).
  const expSpkrHeight = area.expansionSpeakers?.height
    ?? (typeof expSpkrEquipment.height === 'function'
      ? expSpkrEquipment.height(area)
      : area.ceilingHeight.min);

  // PIG-610 / Bar + Expansion Speaker Placement spec §6 Step 2: the coverage radius that sets the
  // grid pitch derives from the speaker's OWN mounting height and OWN coverage angle. For a pendant
  // that's the user-adjustable hang height (expSpkrHeight), NOT the ceiling — so changing the pendant
  // height re-spaces the grid and recomputes the quantity, as reported. This supersedes PIG-601's
  // ceiling-radius borrow, which locked the pendant grid to the ceiling grid and thereby made the
  // pendant-height field inert (a deviation from the placement spec).
  const expSpkrRadius = round(
    (expSpkrHeight - talkerHeight) * Math.tan(expSpkrEquipment.coverageAngle * Math.PI / 180),
    4
  );

  const hasTable = Boolean(table?.shape);
  // Coverage mode from the user's toggle (`expansionSpeakerCoverage`) or the table default when
  // unset. Shared resolver with the PDF report so placement and report can't disagree.
  const expSpkrIsFocused = getExpansionSpeakerIsFocused(area);
  const spkrBaseBox = coverageBoxes[expSpkrIsFocused ? 'focused' : 'full_room'];
  // refDistance = distance from bar wall to nearest listener (spec Step 1a).
  // Table present: table's actual near-edge position = table.location.left - room.left
  // (accounts for transform, not the coverage-box left which subtracts table2wall and
  // ignores transform.x). Per spec there is NO 1.524 m floor when a table is present —
  // barSpeakerNoTableRefDistance is the no-table default only. A small epsilon guards
  // against degenerate (zero/negative) geometry when a table abuts the bar wall.
  const refDistance = hasTable
    ? Math.max(
        0.01,
        (area.dimensions.length - (table?.size?.length ?? 0)) / 2 + (table?.transform?.x ?? 0)
      )
    : constants.barSpeakerNoTableRefDistance;

  const isCeiling = area.expansionSpeakerType === 'ceiling';
  const useTileSnap = isCeiling && hasTiles && centers;

  // Case A/B: the residual coverage box beyond the bar's own SPL-decay reach, or null when no
  // expansion speakers are warranted (bar already covers the area, or the residual band is too
  // shallow to matter). Same edge-case logic the recommendation dialog uses (resolveExpansionSpeakerBox),
  // so the two can't disagree about when zero speakers would be placed.
  const expansionBox = resolveExpansionSpeakerBox({
    room: { left: area.location.left },
    baseBox: spkrBaseBox,
    isFocused: expSpkrIsFocused,
    radius: expSpkrRadius,
    refDistance,
  });

  let coords: GeometricDevice[] = [];
  let placementError = 0;

  if (expansionBox) {
    // Shared grid/shape-dispatch core — the SAME centered-grid + round/keystone/vshape
    // path-sampling logic as the primary ceiling/pendant speaker, pointed at the residual
    // expansion box and the expansion speaker's own model/coverage/height instead of
    // area.speakers. micQty has no natural analogue for a bar mic + scattered expansion mics, so
    // this always passes the single-mic shape ({ x: 1, y: 1 }) — the shared core's rule for that
    // case simply guarantees at least 2 speakers rather than a lone 1, which is a reasonable
    // default here too.
    const expSpkrCoverage = area.expansionSpeakerCoverage ?? { full_room: !expSpkrIsFocused, focused: expSpkrIsFocused };
    const expSpkrOverlaps = getOverlaps(expansionBox, expSpkrRadius, expSpkrCoverage);
    const expSpkrOverlap = expSpkrOverlaps.overlaps[0];

    let rawCoords: GeometricDevice[] = calculateSpeakerPlacement(
      area, expSpkrOverlap, expansionBox, expSpkrRadius, { x: 1, y: 1 }, scale, constants,
      {
        model: expSpkrModel, coverage: expSpkrCoverage, height: expSpkrHeight, isCustomized: false,
        // spkrBaseBox is the primary speaker's own coverage box, before the bar-reach carve-out —
        // expansionBox is always <= it in length. Passing it lets the shared core scale spacing by
        // this same box-length ratio, so the expansion pair keeps a margin from the box edges
        // proportional to the primary speaker's own margin, instead of being pushed flush to fit.
        referenceBox: { length: spkrBaseBox.length, width: spkrBaseBox.width },
        // The round/keystone/vshape perimeter-path branches sample the #spkrPath DOM element for
        // the WHOLE table by default (same physical path the primary speaker uses). Passing
        // expansionBox here confines the qty/spacing math and the actual sampled arc-length range
        // to the residual area beyond the bar's own coverage, so expansion speakers stay put in
        // that area instead of being spaced/sampled as if the whole table were available and
        // drifting whenever qty changes (e.g. an acoustics change altering the auto ceiling
        // height, which feeds expSpkrRadius/spacing above).
        restrictToBox: { left: expansionBox.left, length: expansionBox.length },
      }
    );

    // maxCount cap: degrade to a near-square grid if the shared core's count exceeds the
    // amp/bundle capacity (constants.maxExpansionSpeakers). The primary ceiling/pendant path has
    // no equivalent cap, so this stays here as an expansion-only post-step rather than in the
    // shared core.
    const maxCount = constants.maxExpansionSpeakers;
    if (maxCount && rawCoords.length > maxCount) {
      const colCount = Math.max(1, Math.round(Math.sqrt(maxCount)));
      const rowCount = Math.max(1, Math.round(maxCount / colCount));
      const capSpacing = { x: expansionBox.length / colCount, y: expansionBox.width / rowCount };
      rawCoords = calculateCoordinates(expSpkrModel, expSpkrEquipment.modelName, expansionBox, { x: colCount, y: rowCount }, capSpacing);
    }

    // PIG-626 parity: shape-dispatch (round/keystone/vshape path sampling) can, like the primary
    // speaker, place a coordinate outside the room on some table/room combinations. Drop any that
    // land outside, falling back to a single room-centre speaker if everything gets filtered.
    const expSpkrCentreFallback = () => ({
      model: expSpkrModel,
      modelName: expSpkrEquipment?.modelName,
      location: {
        x: area.location.left + area.dimensions.length / 2,
        y: area.location.top + area.dimensions.width / 2,
        z: expSpkrHeight,
      },
    });
    rawCoords = keepSpeakersInsideRoom(rawCoords, coverageBoxes['full_room'], expSpkrCentreFallback);

    // Set z-height from speaker equipment height
    coords = rawCoords.map(dev => ({ ...dev, location: { ...dev.location, z: expSpkrHeight } }));

    // keep grid-placed expansion speakers at least a coverage-radius clear of the
    // room walls. The grid's far column can otherwise land right on the far wall (colClampMax = box
    // far edge in full-room), wasting that speaker's coverage past the wall. Push any speaker closer
    // than `expSpkrRadius` to a wall back to exactly a radius away — applies to pendant AND ceiling
    // expansion speakers laid out on the grid. Tile-snapped ceiling speakers are excluded (their
    // positions come from the tile grid below). Skipped when the room is too shallow/narrow for a
    // full gap on both sides (no valid range).
    // Both post-steps below move the ENTIRE set of coords as a single rigid body (PIG-611: per-
    // speaker independent movement risks collapsing grid rows into each other). That reasoning
    // holds for the `default:` rectangular grid this pair was designed for, but not for the
    // round/keystone/vshape perimeter-path branch: those points are a single sequence of beads
    // strung along the table's edge, not rows that can collapse. A rigid x/y shift there drags
    // every bead off the diagonal/curved line it was sampled onto — including ones that were
    // never near a mic or wall — which is exactly what made expansion speakers on a keystone/
    // vshape table look "out of position" after a mic (near the table's point) or a wall (the
    // point itself, close to the far wall) triggered a shift. Path-sampled points are already
    // centered within `expansionBox` by construction (see calculateSpeakerPlacement's
    // restrictToBox handling), so they don't need this rigid-body correction in the first place.
    const isPathSampledShape = ['round', 'keystone', 'vshape'].includes(table?.shape) && Boolean(expSpkrCoverage?.focused);
    if (!useTileSnap && !isPathSampledShape) {
      // Non-tile paths (pendant, or ceiling without a tile grid): nudge the grid off nearby mics
      // first (rigid-body shift, see avoidNearbyMics above), THEN enforce the wall gap — so the
      // final positions never violate the wall margin even if the mic-avoidance shift moved
      // something toward a wall. calculateDevicesMiddleware's proximity toast still fires on any
      // violation avoidNearbyMics couldn't clear.
      const nearbyMics = [...micCoords, ...expansionMicCoords];
      coords = avoidNearbyMics(coords, nearbyMics, expansionBox);
      coords = clampExpansionCoordsToWallGap(coords, area, expSpkrRadius);
    }

    if (useTileSnap) {
      // Ceiling + tiles: snap to tile centres, blocking every tile within the fence of any mic so
      // speakers land on the nearest FREE non-mic tile. If nothing free is in range the speaker
      // stays and is counted unplaced (→ warning).
      const currentTileSize = ceilingTiles?.[tile];
      if (currentTileSize && typeof currentTileSize.x === 'number') {
        const centersWithMics = centers.map((c: TileCenter) => ({
          ...c,
          blocked: c.blocked || expansionMicCoords.some((m: GeometricDevice) =>
            m?.location && Math.abs(m.location.x - c.x) < 0.01 && Math.abs(m.location.y - c.y) < 0.01),
        }));
        // Keep auto expansion ceiling speakers off the outermost (wall-touching) tile — snap them to
        // an interior tile instead. Clamp coords into the interior band first (so a speaker the grid
        // placed near the box edge can still reach an interior tile, out of the snap move budget),
        // then block the perimeter ring. Both disable themselves for a room with no interior tile.
        const room = {
          left: area.location.left, top: area.location.top,
          length: area.dimensions.length, width: area.dimensions.width,
        };
        clampSpeakerCoordsToInteriorTiles(coords, room, currentTileSize);
        const centersForSnap = blockPerimeterTilesForSpeakers(centersWithMics, room, currentTileSize);
        placementError += snapExpansionCoordsToTiles(coords, centersForSnap, currentTileSize);
      }
    }
  }

  return { coords, placementError };
};

/**
* @function getDeviceLayouts
* @description Creates comprehensive device layouts including microphones, speakers, and cameras with coverage calculations.
* Optimized for performance, especially for bar microphone switching.
* 
* @param {Object} state - Redux state
* @returns {Object} Complete device layout information
* @property {Object} microphones - Microphone layout and coverage data
* @property {Object} speakers - Speaker layout and coverage data
* @property {Object} cameras - Camera layout and coverage data
* 
* @note
* - Includes fast-path optimizations for bar microphones
* - Minimizes expensive calculations when possible
* - Uses early returns and cached values for better performance
*/
/**
 * PIG-535: Resolve the coverage-density (ceiling/pendant grid mic-count seed) for one
 * calculation.
 *
 * The auto count must RECOMPUTE from the current coverage box whenever the room, coverage
 * mode, or acoustics change. Each of those changes the box or radius and therefore the set of
 * achievable counts (`jumpPoints`). The reliable signal that the geometry changed is that
 * `jumpPoints` itself changed since the previous calculation, so we compare the freshly
 * computed jumpPoints against the ones stored on the last result.
 *
 * A coverage-density slider drag does NOT change the geometry, so it leaves `jumpPoints`
 * unchanged. That is how a deliberate user pick (`persistedValue`) is told apart from a stale
 * auto-count carried over in state: the pick is honoured only while the geometry is unchanged,
 * and is otherwise dropped in favour of the box-appropriate default. This is what stops a count
 * from a larger room sticking when the room shrinks (a stale higher count is almost always
 * still a member of the smaller room's jumpPoints, so a membership-only check is not enough).
 *
 * PIG-290: the jumpPoints-match test is NOT enough on its own. Different mic MODELS can produce
 * the same jumpPoints for the same room (e.g. ceiling TCM-X and pendant TCM-1 both yield [1,2] in
 * the default room), so a value auto-computed under one model would be silently honoured after a
 * switch to another — making the count path-dependent (pendant showed 1 arriving from ceiling but
 * its true default 2 arriving via bar). The persisted value is therefore only reused when it was
 * produced under the SAME model context (`contextMatches`). A previous model's seed is meaningless
 * for a different device, so on a model change we always fall back to the new model's default. This
 * is the model-agnostic, any-entry-point form of the fix: it holds no matter which action drove the
 * switch (type toggle, product-family swap, load, undo). Legacy state with no stored context passes
 * `contextMatches = true` so existing designs keep honouring a saved slider pick on first recalc.
 *
 * Matching jumpPoints is also not enough to prove the GEOMETRY is unchanged: two different
 * coverage boxes / radii can yield the same achievable counts with different overlap factors.
 * Repro: 35'x14' room, TCM-X, Good acoustics. Full-room coverage gives jumpPoints [2,3] /
 * overlaps [1.7242,1.4142] (default 2); adding a rectangle table switches to Focused, which gives
 * the same jumpPoints [2,3] but overlaps [1.4642,1.4142] (default 3). Comparing jumpPoints alone
 * kept the stale full-room 2 as if it were a slider pick, until an acoustics round-trip
 * (Good -> Fair -> Good) changed jumpPoints and re-derived 3. A slider drag leaves BOTH arrays
 * untouched, so `prevOverlaps` is compared too. It is optional: callers that don't pass it keep the
 * jumpPoints-only comparison.
 *
 * @param jumpPoints      Achievable counts for the CURRENT coverage box (from getOverlaps).
 * @param overlaps        Overlap factor paired with each jump point (from getOverlaps).
 * @param prevJumpPoints  jumpPoints stored on the previous calculation (coverageDensity.jumpPoints).
 * @param persistedValue  Persisted density (coverageDensity.value) — a slider pick or last default.
 * @param coverageOverlap Default overlap for the active coverage mode (defOverlap[full_room|focused]).
 * @param contextMatches  Whether the persisted value was produced under the current mic model (see above).
 * @param prevOverlaps    overlaps stored on the previous calculation (coverageDensity.overlaps). Optional.
 * @returns The density to seed placement with and to persist back as coverageDensity.value.
 */
export const resolveCeilingMicDensity = (
  jumpPoints: number[] = [],
  overlaps: number[] = [],
  prevJumpPoints: number[] = [],
  persistedValue = 0,
  coverageOverlap = 1.6,
  contextMatches = true,
  prevOverlaps?: number[],
): number => {
  const defaultDensity = jumpPoints[overlaps.indexOf(closest(overlaps, coverageOverlap))] ?? jumpPoints[0] ?? 1;
  const sameArray = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);
  const geometryUnchanged = sameArray(prevJumpPoints, jumpPoints)
    && (prevOverlaps === undefined || sameArray(prevOverlaps, overlaps));
  return (contextMatches && geometryUnchanged && persistedValue > 0 && jumpPoints.includes(persistedValue))
    ? persistedValue
    : defaultDensity;
};

export const getDeviceLayouts = createCachedSelector(
  state => getAreaState(state),
  getCoverageBoxes,
  createCoordinateTransformer,
  state => getCenters(state),
  state => getScale(state),
  state => getIsMeter(state),
  state => getAreaGrid(state),
  state => getAreaFineGrid(state),
  createDeviceAdjutmentAndAim,
  state => toolIndex[state.container.currentTool].constants,
  state => getTileQuantities(state),

  (area, coverageBoxes, transformer, centers, scale, isMeter, grid, fineGrid, adjustAndAim, constants, tileQuantities) => {
    // Early return for missing data - comprehensive null checks
    if (!area || !area.microphones || !area.speakers || !area.cameras) return null;
    if (isEmpty(area.microphones) || !area.microphones.model) return null;
    if (isEmpty(area.speakers) || !area.speakers.model) return null;
    
    const { microphones, speakers, cameras, table, customized, ceilingTiles: { tile } } = area;
    const { transform: tableTransform = { x: 0, y: 0, a: 0 } } = table;
    const { microphones: isMicCustom, speakers: isSpkrCustom, cameras: isCameraCustom } = customized;
    const hasTiles = tile !== "no_ceiling_tiles";
    const { equipment, acoustics, area: { talkerHeight }, tables, ceilingTiles } = constants;
    const isEndTable = tables[table.shape]?.endTable ?? false;
    let movedWarningActive = false;

    // Get microphone model info - double check for safety
    if (!microphones?.model || !speakers?.model) return null;
    const { BAR, CEILING_MOUNTED, CEILING_TILE, TABLETOP, CBC, HAS_CAMERA, PENDANT } = equipment.micCategories;
    const micModel = microphones.model;
    const micEquipment = equipment.microphones[micModel];
    const isBarMic = BAR.has(micModel);
    const isCeilingMic = CEILING_MOUNTED.has(micModel);
    const isCeilingTileMic = CEILING_TILE?.has(micModel);
    const isTableMic = TABLETOP.has(micModel);

    // Fast path for bar microphones - they have the simplest logic
    // Exception: For ABC 2500a with camera customized, skip this path to avoid moving mic/speaker
    const isABC = micModel === 'abc_2500a';
    const isCBC = CBC.has(micModel);
    const isABCWithCameraCustom = isABC && isCameraCustom;

    if (isBarMic && !isMicCustom && !isSpkrCustom && !isABCWithCameraCustom) {
      // Calculate bar position and rotation
      let barLocation: { x: number; y: number };
      let barRotation = 0;

      if (isCBC) {
        // CBC 2500 placement logic: center of table (or room if no table)
        const hasTable = Boolean(table?.shape);

        if (hasTable) {
          // Place at center of table with table rotation
          const roomCenterX = area.location.left + area.dimensions.length / 2;
          const roomCenterY = area.location.top + area.dimensions.width / 2;
          barLocation = {
            x: roomCenterX + (tableTransform.x || 0),
            y: roomCenterY + (tableTransform.y || 0)
          };
          // Align with table's long edge (table rotation)
          // Parse the rotation angle from tableTransform.a (format: "angle centerX centerY")
          barRotation = tableTransform.a ? parseFloat(tableTransform.a.toString().split(' ')[0]) : 0;
        } else {
          // Place at center of room
          barLocation = {
            x: area.location.left + area.dimensions.length / 2,
            y: area.location.top + area.dimensions.width / 2
          };
          barRotation = 0; // Default rotation for room center
        }
      } else {
        // ABC/VBC placement logic: fixed position near front wall
        barLocation = {
          x: area.location.left + equipment.wallMountedBarOffset,
          y: area.location.top + area.dimensions.width / 2
        };
        barRotation = 0;
      }

      // For CBC bars: use area.microphones.height if it exists (set by user adjusting height slider)
      // Otherwise calculate from ceiling height
      const calculatedHeightFromCeiling = micEquipment.height(area, microphones.modelName);
      const userAdjustedHeight = microphones.height; // This is set when user moves the height slider
      const micHeight = userAdjustedHeight !== undefined ? userAdjustedHeight : calculatedHeightFromCeiling;

      const micCoords = [{
        model: microphones.model,
        modelName: microphones.modelName || micEquipment.modelName,
        location: {
          ...barLocation,
          z: micHeight
        },
        horAngle: barRotation
      }];

      // For bar mics, speakers are bar-mounted at the same height as the microphone —
      // reuse `micHeight` (which already honors a manually-adjusted mic height, see above)
      // instead of recomputing fresh from the ceiling formula. Recomputing here was the bug:
      // it silently ignored any manual height edit, so the linked bar speaker's Z stayed
      // pinned to the ceiling-calculated height even after the user changed the mic height.
      const correctHeight = micHeight;
      const spkrCoords = isSpkrCustom
        ? speakers.layout.map(spkr => ({
            ...spkr,
            location: {
              ...spkr.location,
              z: correctHeight  // Match the mic's height (manual override or ceiling-calculated)
            }
          }))
        : [{
            model: speakers.model,
            // For bar speakers, use the same model name as the microphone
            modelName: speakers.model === 'barspkr'
              ? (microphones.modelName || micEquipment.modelName)
              : equipment.speakers[speakers.model].modelName,
            location: {
              ...barLocation,
              z: correctHeight
            },
            horAngle: barRotation
            // NOTE: verAngle is intentionally NOT set here for bar speakers.
            // The HearSpace API service will calculate it based on height (-90° straight down).
            // This ensures the angle is always fresh and correct, not cached from old state.
          }];

      // Expansion ceiling mics — delegated to the helper above the selector. Behaviour
      // is identical to the inline branches (gap-aware + manual-opt-in fallback +
      // customised-layout preservation) but lives in one named function for testability
      // and to keep getDeviceLayouts from growing further.
      const expansionMicResult = computeExpansionCeilingMicCoords({
        area, customized, coverageBoxes, acoustics, micModel, equipment, constants,
        hasTiles, centers, tile, ceilingTiles, scale, talkerHeight,
      });
      const expansionCoords = expansionMicResult.coords;
      const expansionPlacementError = expansionMicResult.placementError;
      const isExpansionCustom = Boolean(customized?.expansionMicrophones); // still consumed by table-expansion path below

      // Expansion ceiling/pendant speakers — delegated to the helper above.
      // Mic-proximity filtering is applied inside via `nearbyMics`.
      const isExpansionSpkrActive = area.expansionSpeakerType === 'ceiling' || area.expansionSpeakerType === 'pendant';
      const expansionSpkrResult = computeExpansionSpeakerCoords({
        area, customized, micCoords, expansionMicCoords: expansionCoords, coverageBoxes,
        equipment, talkerHeight, table, constants, hasTiles, centers, tile, ceilingTiles, scale,
      });
      const expansionSpkrCoords = expansionSpkrResult.coords;
      const expansionSpkrPlacementError = expansionSpkrResult.placementError;

      // Previously trimmed here to match the UCC port-budget cap that
      // `setExpansionMicLayout` (ducks/room.ts) applied to the authoritative
      // layout, so everything derived in this pass (collision detection,
      // heatmap) wouldn't disagree with what actually got placed. That cap is
      // gone — superseded by the Devio UCX capacity fallback (spec §2.2/§2.3,
      // isUCXViable) rather than silent truncation — so the full, uncapped
      // `expansionCoords` is used directly; it always matches what the
      // reducer now keeps.
      const cappedExpansionCoords = expansionCoords;
      // Collision detection: expansion mics vs speakers.
      // Note: expansion mics vs bar mic is not checked — the expansion box starts at/beyond the bar's
      // reach, so overlap is only possible in degenerate rooms that already fail edge case B.
      // allMicCoords is constructed after all mutations (z-height + tile snapping) so it reflects final positions.
      //
      // allMicCoords becomes `microphones.layout`/`qty`, a mirrored copy of the expansion mics
      // that several consumers read (HearSpace, hover hit-testing, and the report/equipment
      // views). It's built from `cappedExpansionCoords`, which (as of the Devio UCX capacity
      // fallback, spec §2.2/§2.3) is just `expansionCoords` — the full count, matching what
      // `setExpansionMicLayout` now keeps unconditionally. `expansionMicCalcResult` below stays
      // the same value for the same reason, not because it's a separate "requested" count.
      const allMicCoords = [...micCoords, ...cappedExpansionCoords];
      const { collisions: expansionCollisions } = cappedExpansionCoords.length > 0
        ? getCollisions(cappedExpansionCoords, spkrCoords)
        : { collisions: 0 };

      const expansionErrors: DeviceError[] = [];
      if (expansionPlacementError > 0) {
        expansionErrors.push({ code: 1200, msg: `Could not center ${expansionPlacementError} expansion microphone(s) to ceiling tiles.`, reset: true });
      } else {
        expansionErrors.push({ code: 1200, msg: '' });
      }
      expansionErrors.push({ code: 1300, msg: '', ...(expansionCollisions > 0 && { msg: `${expansionCollisions} expansion microphone(s) are closer to speakers than recommended.`, reset: true }) });
      expansionErrors.push({ code: 1400, msg: '' }); // Expansion mics are never auto-moved, so 1400 is always a no-error stub here
      if (expansionSpkrPlacementError > 0) {
        expansionErrors.push({ code: 1500, msg: `Could not center ${expansionSpkrPlacementError} expansion speaker(s) to ceiling tiles.`, reset: true });
      } else {
        expansionErrors.push({ code: 1500, msg: '' });
      }

      // Skip expensive collision detection for bar devices
      const cameraCoords = calculateCameraPlacement(area, constants, micCoords);

      // For bar cameras, override horAngle to match table rotation.
      // Standalone cameras (Vidi 250/280) keep their own aim angle independent of furniture rotation.
      const isBarCamera = constants?.equipment?.cameras?.[cameras.model]?.isBarCamera;
      if (isBarCamera && cameraCoords && cameraCoords.length > 0) {
        cameraCoords[0].horAngle = barRotation;
      }

      // Get camera configuration for accurate coverage angles
      const cameraConfig = constants?.equipment?.cameras?.[cameras.model];
      const isDualImagerCamera = cameraConfig?.dualImager === true;
      const configCoverageAngle = isDualImagerCamera
        ? cameraConfig?.wideImager?.coverageAngle
        : cameraConfig?.coverageAngle;

      // expansionCoords already computed above (ceiling path). Reuse for equipment list / qty.
      // Table expansion non-custom: handled entirely by `tableExpansionStrategy` in the
      // middleware (it has access to ttm_x params + table geometry and always emits at
      // least one mic anchored to the table's far edge). roomCalculations no longer
      // dispatches a table-centre placeholder — the strategy is the single source of truth.
      let expansionMicCalcResult: { qty: number; layout: GeometricDevice[] } | null = null;
      if (area.expansionMicType === 'ceiling') {
        // Always produce a result (even {qty:0, layout:[]}) when ceiling expansion is active, not
        // only when expansionCoords is non-empty. calculateDevicesMiddleware only dispatches
        // `setExpansionMicLayout` — the reducer that actually writes `expansionMicrophones.layout`
        // — when this is truthy; a bare `null` (the old behavior whenever the gap-check computed 0)
        // reads as "nothing to update", so mics placed before an acoustics/geometry change that
        // closes the gap (e.g. the bar's own reach now covers the table) were never cleared. The
        // heatmap, built fresh from expansionCoords each pass, already reflected 0 mics correctly —
        // only the stale device markers/layout were left behind.
        expansionMicCalcResult = { qty: expansionCoords.length, layout: expansionCoords };
      } else if (area.expansionMicType === 'table' && isExpansionCustom) {
        // User has manually added table expansion mics — preserve the stored layout so the
        // middleware's recalculation doesn't overwrite their positions.
        const storedLayout = (area.expansionMicrophones?.layout ?? []) as GeometricDevice[];
        expansionMicCalcResult = { qty: storedLayout.length, layout: storedLayout };
      }

      // Always produce a result when active so state is authoritative:
      // - null  → expansion speakers not configured (type not selected)
      // - { qty:0, layout:[] } → active but bar fully covers room (edge case A/B)
      // - { qty:N, layout:[…] } → speakers placed
      const expansionSpkrCalcResult: { qty: number; layout: GeometricDevice[] } | null = isExpansionSpkrActive
        ? { qty: expansionSpkrCoords.length, layout: expansionSpkrCoords }
        : null;

      // Combined expansion mic layout used to drive the heatmap (microphones.plotData).
      // Ceiling expansion is fully computed here, so use expansionCoords directly. Table expansion
      // positions come from the middleware (calcExpansionTableMicPlacement needs the DOM micPath),
      // so prefer the latest stored layout in state — falling back to expansionMicCalcResult.layout
      // (the placeholder at table centre) before the middleware has populated real positions.
      const storedExpansionLayout = (area.expansionMicrophones?.layout ?? []) as GeometricDevice[];
      // Ceiling expansion: `cappedExpansionCoords` is the full, uncapped `expansionCoords` (see
      // above) — the heatmap always shows coverage for exactly the mics `setExpansionMicLayout`
      // (reduxModules/ducks/room.ts) keeps, since neither trims anymore (spec §2.2/§2.3).
      const expansionPlotLayout: GeometricDevice[] = area.expansionMicType === 'ceiling'
        ? cappedExpansionCoords
        : area.expansionMicType === 'table'
          ? (storedExpansionLayout.length > 0 ? storedExpansionLayout : (expansionMicCalcResult?.layout ?? []))
          : [];

      // Clamp the expansion-mic plot at its own model's effective radius (ttm_x for table,
      // dcm_x for ceiling). Without the clamp, the combined heatmap's min(barDist, expansionDist)
      // merge pulls coverage across the whole room because a 5m point is still "5m from the
      // expansion mic" even though the expansion mic's real reach is much shorter (e.g. TTM-X
      // ~1.7m, DCM-X ~1.8-5.0m depending on acoustics). calculateMicPlotData uses this radius to
      // both rescale the raw distance into the bar's reference frame (5.6m = good acoustics) and
      // cut off points beyond 2x the radius, so the expansion mic's green coverage reads at the
      // same size it would as a standalone ceiling/table mic instead of ballooning out to the
      // bar's hardcoded micMaxRadius/micDistanceLegendMax defaults (5m / 12m — see below).
      // PIG: expansion mic coverage rendered larger than normal — ceiling (dcm_x) previously
      // had no clamp at all here; fixed by applying the same clamp table expansion already had.
      //
      // ...but the TABLE clamp is the wrong quantity for a CEILING mic, which is what the
      // follow-up below corrects. `micData` is the mic's MAX PICKUP DISTANCE — a slant range from
      // the capsule to the talker's mouth:
      //   ttm_x (table) sits ON the table, essentially in the talker plane, so the slant range IS
      //     the horizontal radius. Using micData raw is correct.
      //   dcm_x (ceiling) hangs ~1.7 m ABOVE the talker plane, so its horizontal reach is the
      //     projected leg √(micData² − verticalDrop²) — the exact formula placement uses
      //     (`computeMicRadius`) and the one spec §5.2 mandates, warning in as many words that
      //     "the raw slant distance would overstate coverage".
      //
      // Drawing the raw slant value made the heatmap disagree with the placement engine by ~2x.
      // At Poor, dcm_x micData[0] = 1.8 against a real projected radius of √(1.8² − 1.6947²)
      // = 0.607 (floored to 0.8): calculateMicPlotData's 5.6/maxRadius rescale then painted solid
      // green out to ~1.61 m per mic. Three mics read as covering a 9.15 m racetrack table that
      // actually needs ~6, so a design looked fully covered while the engine knew it was not.
      const acousticIdxForPlot = Math.max(0, Math.min(4, acoustics.indexOf(area.acoustics ?? 'good')));
      const expansionMicDataKey = area.expansionMicType === 'table'
        ? 'ttm_x'
        : area.expansionMicType === 'ceiling'
          ? 'dcm_x'
          : null;
      const expansionSlantDist = expansionMicDataKey
        ? equipment.microphones[expansionMicDataKey]?.micData?.[acousticIdxForPlot]
        : undefined;
      // Same height resolution `computeExpansionCeilingMicCoords` uses for placement, so the two
      // cannot drift: the expansion group's own configured mounting height, NOT area.microphones
      // .height (the primary here is always a bar at table/wall height).
      const expansionPlotMicHeight = (area.expansionMicrophones as any)?.height
        ?? area.ceilingHeight?.min
        ?? area.ceilingHeight
        ?? 2.8956;
      const expansionPlotClampRadius = (area.expansionMicType === 'ceiling' && typeof expansionSlantDist === 'number')
        ? computeMicRadius(expansionSlantDist, expansionPlotMicHeight, talkerHeight, area.acoustics, false)
        : expansionSlantDist;

      return {
        microphones: {
          micDistanceLegendMax: 12, // Default for bar mics (3'-39' range)
          micMaxRadius: 5, // Default for bar mics (effective pickup radius)
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1],
          },
          layout: allMicCoords,
          qty: allMicCoords.length, // this path is only reached when !isMicCustom
          plotData: combinePlotData(
            calculateBarMicPlotData(area, micCoords, grid, constants),
            expansionPlotLayout.length ? calculateMicPlotData(area.id, expansionPlotLayout, grid, expansionPlotClampRadius) : null
          ),
        },
        speakers: {
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1]
          },
          layout: spkrCoords,
          qty: isSpkrCustom ? speakers.qty : 1,
          plotData: speakers.plotData || null // HearSpace only - no local calculations
        },
        cameras: {
          layout: isCameraCustom ? cameras.layout : cameraCoords,
          qty: isCameraCustom ? cameras.qty : cameraCoords.length,
          // Include same properties as normal cameras
          hasCamera: cameras.hasCamera,
          model: cameras.model,
          coverageAngle: configCoverageAngle || cameras.coverageAngle, // Use config value first (source of truth)
          coverageRadius: cameras.coverageRadius,
          plotData: (cameras.hasCamera && cameras.model !== 'none')
            ? calculateCameraPlotData(area.id, isCameraCustom ? cameras.layout : cameraCoords, grid, adjustAndAim, cameras.model, constants)
            : {}
        },
        expansionMicrophones: expansionMicCalcResult,
        expansionSpeakers: expansionSpkrCalcResult,
        collisions: { microphones: [], speakers: [] },
        errors: expansionErrors,
      };
    }
    
    // Handle camera-driven bar movement for VBC 2500a
    if (isBarMic && isCameraCustom && HAS_CAMERA.has(micModel) && cameras.layout && cameras.layout.length > 0) {
      // For VBC, when camera is moved, all bar devices follow the camera position
      const cameraPosition = cameras.layout[0].location;

      const micCoords = [{
        model: microphones.model,
        modelName: microphones.modelName || micEquipment.modelName,
        location: {
          ...cameraPosition,
          z: micEquipment.height(area, microphones.modelName)
        },
        horAngle: cameras.layout[0].horAngle ?? 0,
      }];

      const spkrCoords = [{
        model: speakers.model,
        modelName: speakers.model === 'barspkr'
          ? (microphones.modelName || micEquipment.modelName)
          : equipment.speakers[speakers.model].modelName,
        location: {
          ...cameraPosition,
          z: micEquipment.height(area, microphones.modelName)
        },
        horAngle: cameras.layout[0].horAngle ?? 0,
        // NOTE: verAngle is intentionally NOT set for bar speakers (calculated by HearSpace API)
      }];

      const cameraCoords = cameras.layout; // Use the customized camera position

      // Get camera configuration for accurate coverage angles
      const cameraConfig = constants?.equipment?.cameras?.[cameras.model];
      const isDualImagerCamera = cameraConfig?.dualImager === true;
      const configCoverageAngle = isDualImagerCamera
        ? cameraConfig?.wideImager?.coverageAngle
        : cameraConfig?.coverageAngle;

      return {
        microphones: {
          micDistanceLegendMax: 12,
          micMaxRadius: 5,
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1],
          },
          layout: micCoords,
          qty: microphones.qty,
          plotData: calculateBarMicPlotData(area, micCoords, grid, constants),
        },
        speakers: {
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1]
          },
          layout: spkrCoords,
          qty: speakers.qty,
          plotData: speakers.plotData || null // HearSpace only - no local calculations
        },
        cameras: {
          layout: cameraCoords,
          qty: cameras.qty,
          coverageAngle: configCoverageAngle || cameras.coverageAngle,
          coverageRadius: cameras.coverageRadius,
          plotData: (cameras.hasCamera && cameras.model !== 'none')
            ? calculateCameraPlotData(area.id, cameraCoords, grid, adjustAndAim, cameras.model, constants)
            : {}
        },
        collisions: { microphones: [], speakers: [] },
        errors: [{ code: 1200 }, { code: 1300 }, { code: 1400 }],
      };
    }
    
    // Handle ABC 2500a with camera customized - return early to avoid any mic/speaker changes
    if (isABCWithCameraCustom && !isMicCustom) {
      // For ABC 2500a with only camera customized, return existing mic/speaker layout with only camera updated
      // Get camera configuration for accurate coverage angles
      const cameraConfig = constants?.equipment?.cameras?.[cameras.model];
      const isDualImagerCamera = cameraConfig?.dualImager === true;
      const configCoverageAngle = isDualImagerCamera
        ? cameraConfig?.wideImager?.coverageAngle
        : cameraConfig?.coverageAngle;

      return {
        microphones: {
          // Keep existing microphone data completely unchanged
          micDistanceLegendMax: microphones.micDistanceLegendMax || 40,
          micMaxRadius: microphones.micMaxRadius || 10,
          coverageDensity: microphones.coverageDensity || { value: 1, jumpPoints: [1], overlaps: [1] },
          layout: microphones.layout, // Keep existing layout
          qty: microphones.qty,
          plotData: microphones.plotData || {}, // Keep existing plot data
        },
        speakers: {
          // Keep existing speaker data completely unchanged
          coverageDensity: speakers.coverageDensity || { value: 1, jumpPoints: [1], overlaps: [1] },
          layout: speakers.layout, // Keep existing layout
          qty: speakers.qty,
          plotData: speakers.plotData || {}, // Keep existing plot data
        },
        cameras: {
          // Only update camera with customized position
          layout: cameras.layout,
          qty: cameras.qty,
          coverageAngle: configCoverageAngle || cameras.coverageAngle,
          coverageRadius: cameras.coverageRadius,
          plotData: (cameras.hasCamera && cameras.model !== 'none')
            ? calculateCameraPlotData(area.id, cameras.layout, grid, adjustAndAim, cameras.model, constants)
            : {}
        },
        collisions: { microphones: [], speakers: [] },
        errors: [],
      };
    }
    
    // Handle ABC 2500a with both microphone and camera customized - camera is independent
    if (isABC && isMicCustom && isCameraCustom) {
      // Use the customized microphone position for mic and speaker
      const micCoords = microphones.layout || [{
        model: microphones.model,
        modelName: microphones.modelName || micEquipment.modelName,
        location: {
          x: area.location.left + .134,
          y: area.location.top + area.dimensions.width / 2,
          z: micEquipment.height(area, microphones.modelName)
        }
      }];

      // For bar mics, speakers follow the same position
      // IMPORTANT: Always recalculate height from equipment config, even for customized speakers
      const correctHeight = micEquipment.height(area, microphones.modelName);
      const spkrCoords = isSpkrCustom
        ? speakers.layout.map(spkr => ({
            ...spkr,
            location: {
              ...spkr.location,
              z: correctHeight  // Override height with correct ceiling height
            }
          }))
        : [{
            model: speakers.model,
            modelName: speakers.model === 'barspkr'
              ? (microphones.modelName || micEquipment.modelName)
              : equipment.speakers[speakers.model].modelName,
            location: {
              ...micCoords[0].location,
              z: correctHeight
            }
          }];

      // Camera is independent for ABC - use its own customized position
      const cameraCoords = cameras.layout || calculateCameraPlacement(area, constants);

      // Get camera configuration for accurate coverage angles
      const cameraConfig = constants?.equipment?.cameras?.[cameras.model];
      const isDualImagerCamera = cameraConfig?.dualImager === true;
      const configCoverageAngle = isDualImagerCamera
        ? cameraConfig?.wideImager?.coverageAngle
        : cameraConfig?.coverageAngle;

      return {
        microphones: {
          micDistanceLegendMax: 12,
          micMaxRadius: 5,
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1],
          },
          layout: micCoords,
          qty: microphones.qty,
          plotData: calculateBarMicPlotData(area, micCoords, grid, constants),
        },
        speakers: {
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1]
          },
          layout: spkrCoords,
          qty: isSpkrCustom ? speakers.qty : 1,
          plotData: speakers.plotData || null // HearSpace only - no local calculations
        },
        cameras: {
          layout: cameraCoords,
          qty: cameras.qty,
          coverageAngle: configCoverageAngle || cameras.coverageAngle,
          coverageRadius: cameras.coverageRadius,
          plotData: (cameras.hasCamera && cameras.model !== 'none')
            ? calculateCameraPlotData(area.id, cameraCoords, grid, adjustAndAim, cameras.model, constants)
            : {}
        },
        collisions: { microphones: [], speakers: [] },
        errors: [{ code: 1200 }, { code: 1300 }, { code: 1400 }],
      };
    }
    
    // Handle customized bar microphones (VBC or ABC without camera customized)
    if (isBarMic && isMicCustom) {
      // Use the customized microphone position
      const micCoords = microphones.layout || [{
        model: microphones.model,
        modelName: microphones.modelName || micEquipment.modelName,
        location: {
          x: area.location.left + .134,
          y: area.location.top + area.dimensions.width / 2,
          z: micEquipment.height(area, microphones.modelName)
        }
      }];

      // For bar mics, speakers follow the same position
      // IMPORTANT: Always recalculate height from equipment config, even for customized speakers
      const correctHeight = micEquipment.height(area, microphones.modelName);
      const spkrCoords = isSpkrCustom
        ? speakers.layout.map(spkr => ({
            ...spkr,
            location: {
              ...spkr.location,
              z: correctHeight  // Override height with correct ceiling height
            }
          }))
        : [{
            model: speakers.model,
            modelName: speakers.model === 'barspkr'
              ? (microphones.modelName || micEquipment.modelName)
              : equipment.speakers[speakers.model].modelName,
            location: {
              ...micCoords[0].location,
              z: correctHeight
            }
          }];

      // Camera should follow the microphone position unless camera is also customized
      const cameraCoords = isCameraCustom ? cameras.layout : calculateCameraPlacement(area, constants, micCoords);

      // Get camera configuration for accurate coverage angles
      const cameraConfig = constants?.equipment?.cameras?.[cameras.model];
      const isDualImagerCamera = cameraConfig?.dualImager === true;
      const configCoverageAngle = isDualImagerCamera
        ? cameraConfig?.wideImager?.coverageAngle
        : cameraConfig?.coverageAngle;

      return {
        microphones: {
          micDistanceLegendMax: 12,
          micMaxRadius: 5,
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1],
          },
          layout: micCoords,
          qty: microphones.qty,
          plotData: calculateBarMicPlotData(area, micCoords, grid, constants),
        },
        speakers: {
          coverageDensity: {
            value: 1,
            jumpPoints: [1],
            overlaps: [1]
          },
          layout: spkrCoords,
          qty: isSpkrCustom ? speakers.qty : 1,
          plotData: speakers.plotData || null // HearSpace only - no local calculations
        },
        cameras: {
          layout: cameraCoords,
          qty: isCameraCustom ? cameras.qty : cameraCoords.length,
          coverageAngle: configCoverageAngle || cameras.coverageAngle,
          coverageRadius: cameras.coverageRadius,
          plotData: (cameras.hasCamera && cameras.model !== 'none')
            ? calculateCameraPlotData(area.id, cameraCoords, grid, adjustAndAim, cameras.model, constants)
            : {}
        },
        collisions: { microphones: [], speakers: [] },
        errors: [{ code: 1200 }, { code: 1300 }, { code: 1400 }],
      };
    }

    // Continue with original logic for ceiling and table microphones.
    // micMaxDist is the acoustic-level-dependent max slant distance; it also feeds
    // the coverage legend max below (micDistanceLegendMax), so it stays here.
    // computeMicRadius applies the geometry + coverage safeguards — see its docblock.
    // PIG-295 AC2/AC3: BMA360D is auto-lowered upstream, so it reaches here with a
    // valid radius; the safeguard floors remain for non-tile mics (e.g. TCM-X).
    const micMaxDist = micEquipment.micData[indexOf(acoustics, area.acoustics)];
    const isPendantMic = PENDANT.has(micModel);
    const micRadius = computeMicRadius(micMaxDist, microphones.height, talkerHeight, area.acoustics, isPendantMic);
    const micCoverageBox = coverageBoxes[findKey(microphones.coverage)];
    const micOverlaps = getOverlaps(micCoverageBox, micRadius, microphones.coverage, table, isEndTable);

    // PIG-535: resolve the ceiling/pendant grid density once, here, so the same value drives
    // the placement seed AND the coverageDensity.value we persist (the slider position). See
    // resolveCeilingMicDensity for why this recomputes on a geometry change yet preserves a
    // deliberate slider pick — that is the fix for the count sticking when the room shrinks.
    const micCoverageKey = findKey(microphones.coverage);
    // PIG-290: the persisted density seed is only trustworthy if it was produced under the current
    // mic model. `context` is stamped onto coverageDensity on write-back (below); when it is absent
    // (legacy/loaded state) treat it as a match so saved slider picks still apply on first recalc.
    const prevDensityContext = microphones.coverageDensity?.context;
    const densityContextMatches = prevDensityContext == null || prevDensityContext === micModel;
    const micDensity = resolveCeilingMicDensity(
      micOverlaps.jumpPoints,
      micOverlaps.overlaps,
      microphones.coverageDensity?.jumpPoints ?? [],
      microphones.coverageDensity?.value ?? 0,
      constants.defOverlap[micCoverageKey],
      densityContextMatches,
      microphones.coverageDensity?.overlaps ?? [],
    );

    // Calculate initial microphone placement, but use existing layout for custom microphones
    let micCoords, micQty, updatedJumpPoints = null;

    if (isMicCustom && microphones.layout && microphones.layout.length > 0) {
      // Use existing custom layout for microphones
      // Deep clone the layout to avoid modifying frozen Redux state
      micCoords = microphones.layout.map(mic => ({
        ...mic,
        location: { ...mic.location }
      }));
      micQty = microphones.qty || microphones.layout.length;
    } else {
      // Calculate fresh placement for non-custom microphones
      const placementResult = calculateMicPlacement(area, micOverlaps, micCoverageBox, micRadius, scale, constants, micDensity);
      micCoords = placementResult.coords;
      micQty = placementResult.qty;
      updatedJumpPoints = placementResult.updatedJumpPoints;
    }
    
    // For pendant mics in poor acoustics, ensure at least one mic in center if all were filtered
    if (isPendantMic && area.acoustics === 'poor' && micCoords.length === 0) {
      console.warn('Poor acoustics: No valid pendant mic positions, placing in room center');
      const centerX = area.location.left + (area.dimensions.length / 2);
      const centerY = area.location.top + (area.dimensions.width / 2);
      micCoords = [{
        model: microphones.model,
        modelName: microphones.modelName || 'Parlé TCM-1',
        location: { x: centerX, y: centerY }
      }];
    }
    // Translate and/or Rotate microphone coordinates to align with the center of the table if microphone coverage is set to focused and table has been moved
    const isMicFocused = microphones.coverage.focused;
    const isTableTransformed = some(tableTransform);

    // PIG-456: only apply the table-rotation transform during *auto* placement. Custom layouts
    // come from a user drag and are already in their final untransformed room coords (set by
    // MuiDevices.onPointerUp via screen→room conversion); re-applying `transformer` here would
    // rotate the drop point a second time, moving the snap target onto a different tile.
    const micCoordsTransformed = (isMicFocused || isTableMic) && isTableTransformed && !isMicCustom;
    if (micCoordsTransformed) {
      micCoords = transformCoordinates(micCoords, transformer, 'location');
      micCoords = micCoords.filter(mic => isInside(values(mic.location), coverageBoxes['full_room']));
    } else if (!isMicCustom) {
      // Only filter for room boundaries if microphones are not custom positioned
      // This prevents rendering issues when acoustic settings change but preserves custom positions
      micCoords = micCoords.filter(mic => {
        if (!mic || !mic.location) return false;
        const isValid = isInside(values(mic.location), coverageBoxes['full_room']);
        if (!isValid) {
          console.warn('Microphone positioned outside room boundary:', mic, 'Room:', coverageBoxes['full_room']);
        }
        return isValid;
      });
    }
    
    // CRITICAL: If all mics were filtered out (e.g., poor acoustics), place at least one in center
    const totalMicQty = typeof micQty === 'object' && micQty?.x ? micQty.x * micQty.y : (typeof micQty === 'number' ? micQty : 0);
    if (micCoords.length === 0 && totalMicQty > 0) {
      console.warn('All microphones filtered out, placing one in room center as fallback');
      // Use the actual area center coordinates
      const centerX = area.location.left + (area.dimensions.length / 2);
      const centerY = area.location.top + (area.dimensions.width / 2);
      micCoords = [{
        model: microphones.model,
        modelName: microphones.modelName || 'Parlé TCM-1',
        location: { x: centerX, y: centerY }
      }];
    }

    // If there are ceiling tiles and microphone is in the ceiling, attempt to center in tiles 
    // This applies regardless of customization state to handle tile blocking/unblocking
    let copyOfCenters = centers ? centers.map(center => ({ ...center })) : null;
    let micPlacementError = 0
    // PIG-295 AC13: count how many BMA360D mics actually land on an unblocked tile that
    // sits *inside the coverage box* (table for focused, room for full_room). If every
    // BMA360D mic fails that test, we block placement entirely (clear coords) and surface
    // a clear message instead of leaving mics on the canvas at un-snapped positions or
    // snapped to tiles outside the coverage area.
    //
    // Note on flags: the outer snap loop guard intentionally uses the broader
    // `isCeilingMic` (CEILING_MOUNTED — TCM-X, CBC, BMA360D) so TCM-X mics still snap to
    // tiles when present. The AC13 trigger (`bma360dPlacementBlocked` below) narrows to
    // `isCeilingTileMic` (CEILING_TILE — BMA360D only); validPlacementCount is incremented
    // for every ceiling-mounted mic, but the gate only fires placement-blocked behavior
    // for BMA360D. See `MIC_CATEGORIES` in types/models/microphone.ts.
    let validPlacementCount = 0;

    // PIG-457: the focused coverage box (coverageBoxes['focused']) is centred on the table's
    // DEFAULT (room-centred) position and carries no table transform. When the table has been
    // moved, the mics above were transformed to follow it (micCoordsTransformed) while the ceiling
    // tiles stay in fixed room coords. Testing a tile now sitting under the moved table against the
    // untransformed coverage box (the AC13 tileInsideCoverage check below) reports every such tile
    // as "outside coverage", so validPlacementCount stays 0 and bma360dPlacementBlocked trips —
    // which clears micCoords, making the ceiling mic AND its plot disappear in Microphones after a
    // table move (reported on PR #536). Transform the coverage box with the same transformer so the
    // AC13 test runs in the same frame as the (transformed) mics and (fixed) tiles. A pure move is
    // exact; under table rotation this is the axis-aligned bound of the rotated box (slightly
    // permissive), which is preferable to spuriously blocking placement.
    let snapCoverageBox = micCoverageBox;
    if (micCoordsTransformed && micCoverageBox) {
      const { left = 0, top = 0, length = 0, width = 0 } = micCoverageBox;
      const corners = [
        transformer({ x: left, y: top }),
        transformer({ x: left + length, y: top }),
        transformer({ x: left, y: top + width }),
        transformer({ x: left + length, y: top + width }),
      ];
      snapCoverageBox = [
        min(corners.map(c => c.x)),
        min(corners.map(c => c.y)),
        max(corners.map(c => c.x)),
        max(corners.map(c => c.y)),
      ];
    }

    if (hasTiles && isCeilingMic && copyOfCenters) {
      // hasTiles is guaranteed true by the outer guard, so tile (the actual selected key)
      // is always the correct lookup; the prior `hasTiles ? tile : "2'_x_2'"` ternary was
      // a copy-paste leftover from a different scope where the fallback was needed.
      const tileKey = tile;
      const currentTileSize = ceilingTiles?.[tileKey];
      
      // Safety check for tile size
      if (!currentTileSize || typeof currentTileSize.x !== 'number' || typeof currentTileSize.y !== 'number') {
        console.warn('Invalid tile size for key:', tileKey, currentTileSize);
      } else {
        // PIG-527: constrain the BMA360D's snap target to the ENTERED (visible) tile grid.
        // getCenters emits tile centres for the whole room whenever coverage is PARTIAL (it pads
        // the drawn grid out to maxFit and clips to the room), but MuiCeiling only draws the
        // `drawTileQty` block the user entered. Snapping against the full-room set let a BMA360D land
        // on a tile OUTSIDE the visible grid — and after the grid is dragged the mic re-snapped to
        // the nearest whole-room tile near its OLD position, leaving it stranded off the moved grid
        // (reported on PR #560: "landing outside the ceiling tile grid"). The entered grid is
        // getCenters' own tile index (rx,ry) — the same key the block/`checked` logic uses — where
        // the visible tiles are 0 ≤ rx < drawX, 0 ≤ ry < drawY. At FULL coverage drawTileQty includes
        // the +2 buffer ring so the range spans the whole room and nothing is excluded.
        //
        // Applies to BOTH auto and customised BMA360D placement: a BMA360D physically lives IN a
        // ceiling tile, so it must sit on the entered grid the user drew and follow that grid when it
        // moves — the grid defines where the device can go, and its coverage follows it. (An earlier
        // cut exempted auto placement to keep coverage spread, but that left the mic off the grid the
        // user entered — the real "not snapping into the grid" report.)
        //
        // Applied as a per-search FILTER (micSnapCenters below), NOT by mutating the shared
        // `copyOfCenters`: that array is also consumed by the ceiling-speaker loop further down and
        // carries the running mic-tile blocking. Narrowing it here would drag ceiling speakers onto
        // the mic's entered-grid subset too (regressed speaker placement observed after the first
        // cut of this fix). Keep copyOfCenters whole-room; only the mic's own snap is constrained.
        const draw = tileQuantities?.drawTileQty;
        const constrainToEnteredGrid = isCeilingTileMic
          && !!draw && typeof draw.x === 'number' && typeof draw.y === 'number';
        const inEnteredGrid = (c: TileCenter): boolean =>
          typeof c.rx === 'number' && typeof c.ry === 'number' &&
          c.rx >= 0 && c.rx < (draw as { x: number }).x && c.ry >= 0 && c.ry < (draw as { y: number }).y;

        // Snap radius / relocation gate. Only AUTO-PLACED mics (not user-moved) may relocate to
        // a DIFFERENT tile — e.g. to escape a blocked tile or cluster. A mic the user has
        // manually moved (customised) is normally left where they put it: it may only re-centre
        // within its own tile (~0.72×min, covering square + 2:1 rectangular tiles). For auto-placed
        // mics the search is UNCAPPED so the mic can escape a multi-tile blocked region to the
        // nearest unblocked tile (a finite √2 cap only reaches an immediate neighbour, stranding a
        // mic at the centre of a 2×2 blocked cluster — PIG-389). Uncapping is safe: when the mic's
        // own tile is free it is the nearest match regardless of the cap, so only the
        // blocked-cluster case changes.
        //
        // PIG-527: also allow a customised mic to relocate while `tileRelocationAllowed` is set —
        // i.e. during a `setCeilingTiles` recalc (grid move / count change / tile-type change /
        // block). When the user drags the ceiling grid, the tile centres shift (getCenters applies
        // ceilingTransform); a customised mic capped at 0.72×min would find no tile within range
        // and be left floating off the moved grid. Consulting the flag (mirroring the ceiling
        // speaker gate below) lets the mic RE-SNAP to the nearest tile of the moved grid instead.
        const allowRelocation = tileRelocationAllowed || !isMicCustom;
        const maxMovementDistance = allowRelocation ? Infinity : Math.min(currentTileSize.x, currentTileSize.y) * 0.72;

      for (const mic of micCoords) {
        if (!every(mic.location, objValue => objValue != null)) continue

        const originalLocation = { ...mic.location };

        // PIG-447: All eligible tile centers — square (mask=0) and BOTH halves of
        // rectangular tiles (mask=-1, mask=1) — are valid snap targets for BMA360D.
        // getCenters already emits both halves and blockTiles supports half-tile
        // granularity, so the snap radius is the only constraint on the back half.
        //
        // For ceiling tile mics the containment test prefers the (sub)tile that CONTAINS
        // the mic's current position. Without it, pure Euclidean distance can pick a
        // neighbor when the mic sits in the far portion of its region — and on rectangular
        // tiles it can flip the mic to the OTHER half rather than stay where it was dropped.
        // The containing region depends on the mask:
        //   mask=0       — square tile, half-extent = currentTileSize / 2
        //   mask=±1, x>y — rectangular split along x; half-extent = (x/4) × (y/2)
        //   mask=±1, x<y — rectangular split along y; half-extent = (x/2) × (y/4)
        const containmentTest = isCeilingTileMic
          ? (center: TileCenter) => {
              let halfX, halfY;
              if (center.mask === 0) {
                halfX = currentTileSize.x / 2;
                halfY = currentTileSize.y / 2;
              } else if (currentTileSize.x > currentTileSize.y) {
                halfX = currentTileSize.x / 4;
                halfY = currentTileSize.y / 2;
              } else {
                halfX = currentTileSize.x / 2;
                halfY = currentTileSize.y / 4;
              }
              return Math.abs(originalLocation.x - center.x) <= halfX &&
                     Math.abs(originalLocation.y - center.y) <= halfY;
            }
          : undefined;

        // Constrain the mic's own snap to the entered (visible) grid; re-derived each iteration so
        // it reflects the tiles blocked by earlier mics. Falls back to the full set if the filter
        // is unexpectedly empty. copyOfCenters itself is left whole-room for blocking + speakers.
        let micSnapCenters = copyOfCenters;
        if (constrainToEnteredGrid) {
          const filtered = copyOfCenters.filter(inEnteredGrid);
          if (filtered.length > 0) micSnapCenters = filtered;
        }

        const nearest = findNearestUnblockedTile(micSnapCenters, originalLocation, maxMovementDistance, containmentTest);

        if (nearest) {
          const closestTile = nearest.tile;
          // PIG-295 AC13: a BMA360D placement only counts as valid if the chosen tile
          // is *inside the coverage box* (table for focused, room for full_room). When
          // every tile over the coverage box is blocked, the snap radius can still reach
          // an unblocked tile just outside the box — accepting that would defeat the AC
          // intent (the device wouldn't actually cover the intended area). For TCM-X
          // (non-CEILING_TILE) the previous behavior is preserved.
          //
          // micCoverageBox is computed unconditionally at the top of this selector
          // (coverageBoxes[findKey(microphones.coverage)]) and getCoverageBoxes always
          // returns at least `full_room`, so a missing box here would only happen if the
          // upstream selector is broken — guard defensively so a bad upstream state
          // doesn't NaN-out the snap loop. snapCoverageBox is micCoverageBox transformed to
          // the table's moved position (PIG-457) so tiles under a moved table read as in-coverage.
          //
          // PIG-525 / PIG-527: the coverage-box gate applies to AUTO-placement only. A mic the
          // user controls (isMicCustom) may land on ANY unblocked, unoccupied in-room tile —
          // gated only by blocked tiles + the room boundary, per PIG-295 — so its snap must be
          // accepted even when that tile sits outside the (untransformed) focused coverage box.
          //   - PIG-525: a manual drop must snap to the dropped tile rather than reverting to the
          //     raw drop position (PR #557).
          //   - PIG-527: when the ceiling grid is dragged, a customised mic re-snaps (via the
          //     relocation gate above) to the nearest tile of the moved grid instead of floating
          //     off-grid (PR #560).
          // Partial ceiling coverage is an intentional, allowed state. Auto-placement (isMicCustom
          // = false) still enforces AC13.
          const tileInsideCoverage = !isCeilingTileMic || isMicCustom ||
            (snapCoverageBox && isInside([closestTile.x, closestTile.y], snapCoverageBox));

          if (tileInsideCoverage) {
            // Snap to the found (half-)tile centre the same way the ceiling speaker does. Every
            // ceiling tile mic fits a 2'×2' / 600mm footprint — BMA360D, TCM-X, CBC and DCM-X —
            // i.e. one half of a rectangular (2'×4' / 4'×2') tile or a whole square tile, so they
            // all land on a (sub)tile centre, never the full-tile midpoint. closestTile is already
            // the chosen (containing) half for BMA and the nearest centre otherwise.
            if (nearest.distance > 0.01) {
              mic.location = { x: closestTile.x, y: closestTile.y };
            }
            // Reserve only the occupied (sub)tile — the other half of a rectangular tile stays
            // free for a second unit (matches the half-blocked-tile handling in the search).
            copyOfCenters = blockTiles(copyOfCenters, closestTile);
            validPlacementCount++;
          } else {
            // Outside-coverage snap rejected — leave the mic at originalLocation and
            // count this as a placement failure so the AC13 blocker can fire.
            micPlacementError++;
          }
        } else {
          // No available tiles within movement limits - keep original position
          // Increment solution counter for placement failure (per spec section 4.1)
          micPlacementError++;
          // Still block nearby tiles to prevent other devices from being placed too close
          const nearestTile = minBy(copyOfCenters, (center: TileCenter) => {
            if (!center || typeof center.x !== 'number' || typeof center.y !== 'number') return Infinity;
            return getDistanceBetween(center, originalLocation);
          });
          if (nearestTile && getDistanceBetween(nearestTile, originalLocation) < currentTileSize.x * 2) {
            // Only block if the nearest tile is reasonably close
            copyOfCenters = blockTiles(copyOfCenters, { ...nearestTile, x: originalLocation.x, y: originalLocation.y });
          }
        }
      };
      }
    };

    // PIG-295 AC13: When tiles are defined and NONE of the BMA360D mics can find a reachable
    // unblocked tile, block placement entirely (clear coords) so the canvas doesn't show
    // phantom un-snapped mics. The 1200 error message below is rewritten to explain why.
    //
    // PIG-438: AC13 only applies to *auto-placement* (initial layout, model/acoustics change, etc.).
    // Once the user has customized the layout — a manual drag sets `customized.microphones = true`
    // via calculateDevicesMiddleware — we keep the mic at its calculated (dropped) position rather
    // than wiping it. The existing snap loop above already leaves a mic at its `originalLocation`
    // when no local unblocked tile is reachable, and increments `micPlacementError`, which the
    // 1200 "could not center" message surfaces. Treating drag identically to auto-placement here
    // would silently delete the user's mic — exactly the bug PIG-438 fixes.
    const bma360dPlacementBlocked = isCeilingTileMic && hasTiles && micCoords.length > 0 && validPlacementCount === 0 && !isMicCustom;
    if (bma360dPlacementBlocked) {
      micCoords = [];
    }

    // BMA360D hard cap: Dante network and interface capacity limits placement to maxUnits.
    const bma360dMax = micEquipment.maxUnits;
    const bma360dUnitsExceeded = isCeilingTileMic && bma360dMax != null && micCoords.length > bma360dMax;
    if (bma360dUnitsExceeded) {
      micCoords = micCoords.slice(0, bma360dMax);
    }

    // Calculate speaker layout based on microphone placement
    const spkrModel = speakers.model;
    const { coverageAngle, isCeilingSpkr = false } = equipment.speakers[spkrModel];
    // PIG-612: coverage radius (and the placed z below) must reflect the speaker's ACTUAL mounting
    // height. For pendant/wall speakers that's the user-configured `area.speakers.height`, NOT the
    // model default — the old code always called the equipment height() (pendant: min(8', ceiling)),
    // so the layout ignored the pendant-height field and shifted with room height instead. Fall back
    // to the equipment height() only when no height is stored, keeping ceiling speakers (whose stored
    // height tracks the ceiling via setCeilingHeight) and legacy designs unchanged.
    const spkrHeightFn = equipment.speakers[spkrModel].height;
    const speakerHeight = speakers.height
      ?? (typeof spkrHeightFn === 'function' ? spkrHeightFn(area) : area.ceilingHeight.min);
    const spkrRadius = round((speakerHeight - talkerHeight) * Math.tan(coverageAngle * Math.PI / 180), 4);
    const coverageKey = findKey(speakers.coverage);
    const spkrCoverageBox = coverageBoxes[coverageKey];
    const spkrOverlaps = getOverlaps(spkrCoverageBox, spkrRadius, speakers.coverage)
    const spkrOverlap = spkrOverlaps.overlaps[0];

    // Calculate speaker placement considering microphone placement
    // IMPORTANT: If microphones are customized but speakers are not, preserve existing speaker layout
    let spkrCoords;
    // PIG-457: track whether the layout was freshly computed. Only a fresh layout is in
    // table-default-centred coords and needs the table transform applied; a preserved layout is
    // already in final room coords (re-transforming it marches speakers out of the room).
    let speakersRecalculated = false;
    if (isSpkrCustom && speakers.layout && speakers.layout.length > 0) {
      // Use existing speaker layout when speakers are customized
      // Deep clone the layout to avoid modifying frozen Redux state
      spkrCoords = speakers.layout.map(spkr => ({
        ...spkr,
        location: { ...spkr.location }
      }));
    } else if (isMicCustom && !isSpkrCustom && speakers.layout && speakers.layout.length > 0) {
      // Preserve existing speaker layout when only microphones are customized
      // Deep clone the layout to avoid modifying frozen Redux state
      spkrCoords = speakers.layout.map(spkr => ({
        ...spkr,
        location: { ...spkr.location }
      }));
    } else {
      // Recalculate speakers in all other cases
      spkrCoords = calculateSpeakerPlacement(area, spkrOverlap, spkrCoverageBox, spkrRadius, micQty, scale, constants);
      speakersRecalculated = true;
    }

    // Translate / Rotate speakers coordinates to align with the center of the table if speaker coverage is set to focused and table has been moved
    // BUT: Wall speakers are fixed to the wall and should NOT be transformed with the table
    const spkrEquipment = equipment.speakers[spkrModel];
    const { isWallSpkr = false } = spkrEquipment || {};
    const isSpkrFocused = speakers.coverage.focused;

    // Translate/rotate speakers to the table's moved position — but ONLY for a freshly-calculated
    // layout. calculateSpeakerPlacement returns table-default-centred coords that need the transform
    // applied once. A PRESERVED layout (customised speakers, or the mic-custom "keep speakers"
    // branch above) is already in final room coords; re-applying the transform on every recalc
    // re-adds the table offset and marches the speakers out of the room over successive passes —
    // the PIG-457 "speakers disappear after moving the table" root cause. Wall speakers never move.
    // Drop any speaker that falls outside the room, keeping at least one in the room centre so the
    // layout never empties on the canvas. Mirrors the mic "All microphones filtered out" fallback.
    const spkrCentreFallback = () => ({
      model: spkrModel,
      modelName: spkrEquipment?.modelName ?? speakers.modelName,
      location: {
        x: area.location.left + area.dimensions.length / 2,
        y: area.location.top + area.dimensions.width / 2,
        z: speakerHeight,
      },
    });

    if (isSpkrFocused && isTableTransformed && !isWallSpkr && speakersRecalculated) {
      spkrCoords = transformCoordinates(spkrCoords, transformer, 'location');
      // PIG-457: when the table is dragged toward a wall/corner, the focused speaker grid moves
      // with it and every speaker can land outside the room — filter them and keep a centre
      // fallback so a table move never empties the speaker layout entirely.
      spkrCoords = keepSpeakersInsideRoom(spkrCoords, coverageBoxes['full_room'], spkrCentreFallback);
    } else if (!isWallSpkr && !isSpkrCustom && speakersRecalculated) {
      // PIG-626: non-rectangular tables (keystone, V-shape, round) place ceiling speakers along an
      // SVG path offset OUTWARD from the table edge by the coverage radius. For a large table in a
      // modestly larger room that path can extend past the walls, and the transform branch above
      // only runs when the table has been MOVED — so on a pure RESIZE nothing clamped the coords
      // and speakers rendered outside the room. Mirror the mic room-boundary filter (the mic
      // "!isMicCustom" branch above) for freshly-calculated, non-custom, non-wall speakers.
      spkrCoords = keepSpeakersInsideRoom(spkrCoords, coverageBoxes['full_room'], spkrCentreFallback);
    };
    
    // If there are ceiling tiles and speakers are ceiling-mounted, attempt to center in tiles
    // This applies regardless of customization state to handle tile blocking/unblocking
    if (hasTiles && isCeilingSpkr && copyOfCenters && spkrCoords && Array.isArray(spkrCoords)) {
      const tileKey = hasTiles ? tile : "2'_x_2'";
      const currentTileSize = ceilingTiles?.[tileKey];

      // Safety check for tile size
      if (!currentTileSize || typeof currentTileSize.x !== 'number' || typeof currentTileSize.y !== 'number') {
        console.warn('Invalid tile size for speaker centering, key:', tileKey, currentTileSize);
      } else {
        // Snap radius — same relocation gate as the mic loop: a customised speaker only
        // relocates to a different tile on a tile-size change; otherwise it re-centres within
        // its own tile (~0.72×min). When relocation is allowed the search is uncapped so the
        // speaker can escape a multi-tile blocked cluster to the nearest unblocked tile.
        const allowRelocation = tileRelocationAllowed || !isSpkrCustom;
        const maxMovementDistance = allowRelocation ? Infinity : Math.min(currentTileSize.x, currentTileSize.y) * 0.72;

      for (const spkr of spkrCoords) {
        if (!every(spkr.location, objValue => objValue)) continue

        const originalLocation = { ...spkr.location };

        // Find the closest unblocked tile center for speakers within movement limits.
        // Speakers use plain nearest-Euclidean (no containment preference). copyOfCenters
        // already has the mic tiles blocked (from the mic loop above), so speakers avoid mic
        // tiles; reserving each speaker's own (sub)tile below keeps two speakers off one tile too.
        const nearest = findNearestUnblockedTile(copyOfCenters, originalLocation, maxMovementDistance);

        if (nearest) {
          // Only move if there's a meaningful change
          if (nearest.distance > 0.01) {
            spkr.location = { x: nearest.tile.x, y: nearest.tile.y };
          }
          // No-sharing: reserve this (sub)tile so no other tile device lands on it.
          nearest.tile.blocked = true;
        }
      };
      }
    };

    // Find collisions between microphones and speakers abnd fix only if devices have not been customized
    let collisions = 0, centerPoints = { microphones: [], speakers: [] }, movedDevices = 0;
    if ((isCeilingMic || isPendantMic) && !isMicCustom && !isSpkrCustom) {
      // Exclude wall AND pendant speakers from the mic-collision reposition. Wall speakers are fixed
      // to the wall. Pendant speakers hang well below the ceiling mics (≈7' at a 15' ceiling), so the
      // flat 0.86 m 2D proximity rule is a false positive for them — and the reposition snaps the
      // speaker to the ceiling-TILE grid, which is meaningless for a pendant and knocks its centred
      // grid off the mic column (PIG-612 review: the centre pendant speaker jumped off the two centre
      // mics to an adjacent tile). Neither type should cause mics/speakers to be relocated here.
      const isReposExemptSpkr = (spkr: GeometricDevice) => {
        const def = equipment.speakers[spkr.model];
        return Boolean(def?.isWallSpkr || def?.isPendantSpkr);
      };
      const movableSpkrCoords = spkrCoords.filter((spkr: GeometricDevice) => !isReposExemptSpkr(spkr));
      const movableSpkrLayout = speakers.layout ? speakers.layout.filter((spkr: GeometricDevice) => !isReposExemptSpkr(spkr)) : [];
      // Initial collision check
      ({ collisions, centerPoints } = getCollisions((isMicCustom ? microphones.layout : micCoords), (isSpkrCustom ? movableSpkrLayout : movableSpkrCoords)));

      if (collisions > 0 && !hasTiles) {
        copyOfCenters = centers ? centers.map(center => ({ ...center })) : null;
        for (const mic of micCoords) {
          if (some(centerPoints.microphones, point => isEqual(point, mic.location))) {
            const finalTile = findClosestTile(copyOfCenters, mic.location);
            if (finalTile) {
              // PIG-628: preserve z (height) — replacing the whole object dropped it, which
              // only surfaced once a lone mic + lone speaker could land on the exact same
              // point and both get repositioned here.
              mic.location = { ...mic.location, x: finalTile.x, y: finalTile.y };
              copyOfCenters = blockTiles(copyOfCenters, finalTile);
              movedDevices++;
            };
          };
        };

        for (const spkr of spkrCoords) {
          // Wall speakers are fixed to the wall; pendant speakers hang below the ceiling mics —
          // neither is relocated by collision resolution (see the movable-speaker filter above).
          const spkrDef = equipment.speakers[spkr.model];
          if (spkrDef?.isWallSpkr || spkrDef?.isPendantSpkr) continue;
          if (some(centerPoints.speakers, point => isEqual(point, spkr.location))) {
            const finalTile = findClosestTile(copyOfCenters, spkr.location);
            if (finalTile) {
              spkr.location = { ...spkr.location, x: finalTile.x, y: finalTile.y };
              copyOfCenters = blockTiles(copyOfCenters, finalTile);
              movedDevices++;
            };
          };
        };
        ({ collisions, centerPoints } = getCollisions(micCoords, movableSpkrCoords));
      };
    } else if (isCeilingMic || isPendantMic) {
      // Just check for collisions
      ({ collisions, centerPoints } = getCollisions(microphones.layout, speakers.layout));
    };

    // set the errors if any
    // Helper function to format device count and name
    const formatDeviceText = (count, singularName) => {
      const numberWords = {
        1: 'one',
        2: 'two',
        3: 'three'
      };
      return count > 0 ? `${numberWords[count] || count} ${pluralize(count, singularName)}` : '';
    };

    const errors = [];
    const hasMicHeightError = !isTableMic && !isBarMic && microphones.height > microphones.maxHeight;

    if (!hasMicHeightError)  {
      // Handle error 1200 - device centering issues
      // Don't show placement errors during tutorial to avoid confusion
      // Tutorial is only active when tutorialStep is >= 0 AND there are tutorial steps defined
      // tutorialStep is initialized to 0, so we need to check for actual tutorial content
      // TODO(tutorial-detection): tutorial-step detection is currently broken upstream, so
      // we hardcode `isGuideActive = false` here to keep error toasts visible. When the
      // tutorial system is re-wired (no ticket filed yet — see PIG-430 review thread for
      // context), restore the real `tutorialStep`-based check so 1200/1300/1400 errors
      // stay suppressed while a tutorial is active.
      const isGuideActive = false;
      
      if (bma360dPlacementBlocked && !isGuideActive) {
        // PIG-295 AC13: every viable tile under the layout is blocked or unreachable.
        // Placement is blocked entirely; surface a clear message explaining what to fix.
        errors.push({
          code: 1200,
          msg: `No unblocked ceiling tile is available for the BMA360D. Add or unblock tiles near the coverage area to place the microphone.`,
          reset: true,
        });
      } else if (micPlacementError && !isGuideActive) {
        const micText = formatDeviceText(micPlacementError, 'microphone');
        const errorMsg = `Could not center ${micText} to ceiling tiles. ${micPlacementError > 1 ? 'Microphones remain' : 'Microphone remains'} at ${micPlacementError > 1 ? 'their' : 'its'} calculated ${micPlacementError > 1 ? 'positions' : 'position'}. Consider adding more tiles or unblocking tiles near ${micPlacementError > 1 ? 'these devices' : 'this device'}.`;

        errors.push({
          code: 1200,
          msg: errorMsg,
          reset: true
        })
      } else if (!micPlacementError) {
        // Only clear the error if there are no microphone placement errors
        errors.push({ code: 1200 })
      };

      // PIG-295 AC2/AC3: code 1500 (BMA360D radius warning) is now handled by the
      // auto-lower flow in calculateDevicesMiddleware.maybeAutoLowerBma360d — by the time
      // layout runs here, the mic is at a height that keeps radius ≥ 1.0 m. Always clear
      // any previously-set 1500 error so a stale warning doesn't persist.
      errors.push({ code: 1500 });

      // BMA360D: unit count capped at maxUnits due to Dante network capacity
      if (bma360dUnitsExceeded) {
        errors.push({
          code: 1600,
          msg: `BMA360D placement is limited to ${bma360dMax} units due to Dante network and interface capacity. Consider improving room acoustics to extend per-unit coverage.`,
          reset: true
        });
      } else {
        errors.push({ code: 1600 });
      }

      // Handle collisions (1300) and moved devices (1400) together
      const hasCollisions = collisions > 0;
      const hasMovedDevices = movedDevices > 0;

      // Only show "devices were adjusted" warning if there were previously calculated devices
      // This prevents the warning from showing during initial placement
      const hadPreviousLayout = (microphones.layout && microphones.layout.length > 0) ||
                                (speakers.layout && speakers.layout.length > 0);
      const shouldShowMovedWarning = hasMovedDevices && hadPreviousLayout;

      if (hasCollisions && shouldShowMovedWarning) {
        // Combined message for both collisions and moved devices
        errors.push({
          code: 1300,
          msg: `Devices were moved to improve performance, but ${formatDeviceText(collisions, 'pair')} of microphones and speakers (highlighted) are still closer than recommended. Consider moving or deleting devices manually.`,
          reset: true
        });
        // Add empty code for 1400 since we've handled both issues with the 1300 message
        errors.push({ code: 1400 });
      } else {
        // Handle individual cases
        if (hasCollisions) {
          errors.push({
            code: 1300,
            msg: isMicCustom || isSpkrCustom ?
              `Distance between ${formatDeviceText(collisions, 'pair')} of microphones and speakers (highlighted) is closer than recommended and may impact system performance. Consider moving or deleting devices manually.` :
              `Could not find a solution with proper distance between ${formatDeviceText(collisions, 'pair')} of microphones and speakers (highlighted). Consider moving or deleting devices manually.`,
            reset: true
          });
        } else {
          errors.push({ code: 1300 });
        }

        if (shouldShowMovedWarning && !movedWarningActive) {
          movedWarningActive = true;
          errors.push({
            code: 1400,
            msg: 'Microphone and speaker positions were adjusted to maintain proper distance between them for optimum performance. This may have caused some device misalignments. Devices can be moved if required.',
            reset: true
          })
        } else {
          if (!shouldShowMovedWarning) movedWarningActive = false;
          errors.push({ code: 1400 })
        }
      }
    }

    // Calculate camera layout
    const cameraCoords = calculateCameraPlacement(area, constants);

    const finalJumpPoints = updatedJumpPoints ?? micOverlaps.jumpPoints;
    const finalOverlaps = micOverlaps.overlaps;
    // `microphones.coverage` is always the { full_room, focused } object, never a bare string —
    // the old strict-equality check against the literal word "fullroom" always evaluated false,
    // silently forcing the "focused" default overlap (1.3) even in full_room mode. Use the same
    // findKey(coverage) -> defOverlap[key] lookup as the grid-density path above (micCoverageKey).
    const targetOverlap = constants.defOverlap[micCoverageKey] ?? 1.3;
    const defaultCoverageDensityValue = finalJumpPoints[finalOverlaps.indexOf(closest(finalOverlaps, targetOverlap))] ?? finalJumpPoints[0] ?? 1;
    // PIG-535: for the ceiling/pendant GRID path (updatedJumpPoints === null) persist the same
    // resolved density that drove placement — the box-appropriate default after a geometry change,
    // or the preserved slider pick when the geometry is unchanged. Keeping this in lock-step with
    // the seed both fixes the resize stickiness and keeps the slider thumb aligned with the count.
    // The perimeter/table paths (updatedJumpPoints set) and custom layouts keep the prior value.
    const usesGridDensity = !isMicCustom && updatedJumpPoints === null && (isCeilingMic || isPendantMic);
    const coverageDensityValue = usesGridDensity ? micDensity : defaultCoverageDensityValue;

    return {
      // centers: copyOfCenters, // to display centers on ceiling uncomment this line and check ;ines to uncomment in ceiling
      microphones: {
        micDistanceLegendMax: ceil(micMaxDist * 2),
        micMaxRadius: micRadius,
        coverageDensity: {
          value: coverageDensityValue,
          jumpPoints: finalJumpPoints,
          overlaps: finalOverlaps,
          minMics: finalJumpPoints[0] ?? 1,
          maxMics: finalJumpPoints[finalJumpPoints.length - 1] ?? 1,
          // PIG-290: stamp the mic model this density was computed under so a later model switch
          // can tell the seed apart from a genuine slider pick (see resolveCeilingMicDensity).
          context: micModel,
        },
        // Ceiling-mounted tile mics (BMA360D and TCM-X / CBC / DCM-X) must always use micCoords
        // so the tile snap AND whole-tile centring are reflected — even in a custom/dragged state,
        // where microphones.layout still holds the raw, pre-snap drop point. Without this, moving
        // a TCM-X onto a tile would leave it at the off-centre drop position instead of centred.
        // Only non-tile mics (tabletop, bar, pendant) preserve their exact custom position.
        layout: (isMicCustom && !isCeilingMic && !tileRelocationAllowed) ? microphones.layout : micCoords,
        qty: (isMicCustom && !isCeilingMic && !tileRelocationAllowed) ? microphones.qty : micCoords.length,
        plotData: calculateMicPlotData(area.id, (isMicCustom && !isCeilingMic && !tileRelocationAllowed) ? microphones.layout : micCoords, grid),
      },
      speakers: {
        coverageDensity: {
          value: spkrCoords.length,
          jumpPoints: spkrOverlaps.jumpPoints,
          overlaps: spkrOverlaps.overlaps,
          minSpkrs: spkrOverlaps.jumpPoints?.[0] ?? 1,
          maxSpkrs: spkrOverlaps.jumpPoints?.[spkrOverlaps.jumpPoints.length - 1] ?? 1,
        },
        // Customised CEILING speakers still snap to the tile centre — spkrCoords is the
        // snap-loop-centred result (drag is honoured as the chosen tile, then re-centred on it).
        // Only non-tile speakers (wall) keep their exact custom drop position.
        layout: (isSpkrCustom && !isCeilingSpkr) ? speakers.layout : spkrCoords,
        qty: (isSpkrCustom && !isCeilingSpkr) ? speakers.qty : spkrCoords.length,
        // Propagate recalculated targetPoint for non-customized wall speakers so the Redux state
        // (area.speakers.targetPoint) stays in sync when room dimensions change.
        ...(!isSpkrCustom && spkrCoords[0]?.targetPoint !== undefined && { targetPoint: spkrCoords[0].targetPoint }),
        plotData: (() => {
          // All speakers: Skip local calculation, wait for HearSpace API results
          // If there's existing plotData from HearSpace (with _fromHearSpace flag), preserve it
          // Otherwise return undefined to signal no data available yet
          const existingPlotData = speakers.plotData;
          if (existingPlotData && existingPlotData._fromHearSpace) {
            // Keep existing HearSpace data
            return existingPlotData;
          }
          // No data yet - return undefined to skip heatmap rendering until HearSpace responds
          return undefined;
        })(),
      },
      cameras: (() => {
        // Get camera configuration for accurate coverage angles
        const cameraConfig = constants?.equipment?.cameras?.[cameras.model];
        const isDualImagerCamera = cameraConfig?.dualImager === true;
        const configCoverageAngle = isDualImagerCamera
          ? cameraConfig?.wideImager?.coverageAngle
          : cameraConfig?.coverageAngle;

        // Calculate camera plot data first to get the adjusted angle
        const cameraPlotData: { camera?: { horAngle?: number } } = (cameras.hasCamera && cameras.model !== 'none')
          ? calculateCameraPlotData(area.id, isCameraCustom ? cameras.layout : cameraCoords, grid, adjustAndAim, cameras.model, constants)
          : {};
        
        // Update camera layout with the adjusted angle from plotData
        const layoutToUse = isCameraCustom ? cameras.layout : cameraCoords;
        const finalCameraLayout = (layoutToUse || []).map((camera) => {
          // Get the adjusted camera data from plotData if available
          const adjustedCamera = cameraPlotData?.camera;
          if (adjustedCamera && adjustedCamera.horAngle !== undefined) {
            return {
              ...camera,
              horAngle: adjustedCamera.horAngle  // Use the adjusted angle from plotData
            };
          }
          return camera;
        });
        
        return {
          layout: finalCameraLayout,
          qty: isCameraCustom ? cameras.qty : (cameraCoords ? cameraCoords.length : 0),
          coverageAngle: configCoverageAngle || cameras.coverageAngle,
          coverageRadius: cameras.coverageRadius,
          plotData:  (cameras.hasCamera && cameras.model !== 'none') 
          ? calculateCameraPlotData(area.id, isCameraCustom ? cameras.layout : cameraCoords, grid, adjustAndAim, cameras.model, constants)
          : {}
        };
      })(),
      collisions: centerPoints,
      errors: errors,
    };
  }
)(
  // Cache key for performance - invalidates when device models or customization changes
  state => {
    const area = getAreaState(state);
    if (!area) return 'no-area';

    return `${area.microphones?.model || ''}-${area.microphones?.modelName || ''}-${area.microphones?.height || ''}-${area.speakers?.model || ''}-${area.cameras?.model || ''}-${area.customized?.microphones || false}-${area.customized?.speakers || false}-${area.customized?.cameras || false}-${area.acoustics || ''}-${JSON.stringify(area.dimensions || {})}-${JSON.stringify(area.table?.shape || '')}-${JSON.stringify(area.microphones?.coverage || {})}-${JSON.stringify(area.speakers?.coverage || {})}-${area.expansionSpeakerType || ''}-${area.expansionMicType || ''}`;

  }
);

/**
 * Selector to get tile quantities for the ceiling.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Object} An object containing actual, draw, and maximum tile quantities for x and y directions.
 */
export const getTileQuantities = createSelector(
  state => getCurrentValue2(state, 'dimensions'),
  state => getCurrentValue2(state, 'ceilingTiles'),
  state => toolIndex[state.container.currentTool]?.constants?.ceilingTiles,
  (area, tiles, constants) => {
    // Safety checks for input parameters
    if (!area || !tiles || !constants) {
      return {
        actualTileQty: { x: 0, y: 0 },
        drawTileQty: { x: 0, y: 0 },
        maxDrawTileQty: { x: 2, y: 2 }
      };
    }

    const { length, width } = area;
    const { tile, qty } = tiles;

    // Validate dimensions
    if (typeof length !== 'number' || typeof width !== 'number' || length <= 0 || width <= 0) {
      console.warn('getTileQuantities: Invalid room dimensions', { length, width });
      return {
        actualTileQty: { x: 0, y: 0 },
        drawTileQty: { x: 0, y: 0 },
        maxDrawTileQty: { x: 2, y: 2 }
      };
    }

    // Set tile size depending on selection. If no tile is selected, use 2x2 for ref grid
    const hasTiles = tile && tile !== "no_ceiling_tiles";
    const tileKey = hasTiles ? tile : "2'_x_2'";
    // Note: 'constants' here is already toolIndex[state.container.currentTool]?.constants?.ceilingTiles
    const tileSize = constants?.[tileKey];

    // Safety check for tile dimensions
    if (!tileSize || 
        typeof tileSize.x !== 'number' || 
        typeof tileSize.y !== 'number' || 
        tileSize.x <= 0 || 
        tileSize.y <= 0) {
      console.warn('getTileQuantities: Invalid tile dimensions', { tile: tileKey, tileSize });
      return {
        actualTileQty: { x: 0, y: 0 },
        drawTileQty: { x: 0, y: 0 },
        maxDrawTileQty: { x: 2, y: 2 }
      };
    }

    const maxX = calcMaxTilesFit(length, tileSize.x);
    const maxY = calcMaxTilesFit(width,  tileSize.y);

    // Check that qty is within max - with safety checks
    let x = 0, y = 0;
    if (qty && typeof qty === 'object') {
      x = typeof qty.x === 'number' ? qty.x : 0;
      y = typeof qty.y === 'number' ? qty.y : 0;
    }

    x = x ? clamp(x, Math.min(1, maxX), maxX) : hasTiles ? maxX : 0;
    y = y ? clamp(y, Math.min(1, maxY), maxY) : hasTiles ? maxY : 0;

    // Set draw quantities. If using full tile coverage, adds 1 tile to the perimeter so the grid can be moved
    const drawX = x + (x && x === maxX ? 2 : 0);
    const drawY = y + (y && y === maxY ? 2 : 0);

    // `actualTileQty` = the user-facing tile count (clamped to maxFit).
    // `drawTileQty`   = how many tiles to actually render — equals actualTileQty,
    //                   plus a +2 hidden-buffer ring at full coverage so the user
    //                   can drag the grid one tile in each direction.
    // `maxDrawTileQty` = the upper bound on drawTileQty (= maxFit + 2). This is
    //                   the value the grid-centering math in getCenters needs;
    //                   it is NOT the "max tiles that fit" (use calcMaxTilesFit
    //                   directly for that, or read `actualTileQty` at full coverage).
    return {
      actualTileQty: { x: x, y: y },
      drawTileQty:   { x: drawX, y: drawY },
      maxDrawTileQty: { x: maxX + 2, y: maxY + 2 },
    };
  }
);

/**
 * Selector to get the centers of ceiling tiles.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Array} An array of objects representing the centers of ceiling tiles, including their positions and states.
 */
const getCenters = createSelector(
  state => getAreaBoxes(state),
  state => getCurrentValue2(state, 'ceilingTiles'),
  getTileQuantities,
  state => toolIndex[state.container.currentTool].constants.ceilingTiles,
  (areaBoxes, ceilintTiles, tileQty, constants) => {
    const areaBox = areaBoxes[0];
    if (!areaBox) {
      return []; // Return empty array if no areaBox
    }

    const { length, width, top, left } = areaBox;
    const { tile, transform: ceilingTransform, checked } = ceilintTiles;
    const { actualTileQty: actual, drawTileQty: draw, maxDrawTileQty: max } = tileQty;

    // Set tile size depending on selection. If no tile is selected, use 2x2 for ref grid
    const hasTiles = tile !== "no_ceiling_tiles";
    // Note: 'constants' here is already toolIndex[state.container.currentTool].constants.ceilingTiles
    const tileSize = constants[hasTiles ? tile : "2'_x_2'"];
    
    // Safety check for tile size
    if (!tileSize || typeof tileSize.x !== 'number' || typeof tileSize.y !== 'number') {
      console.warn('Invalid tile size in getTileArtifacts for tile:', tile);
      return [];
    }

    // calculate tileoffset for cases where part of the tile is outside the room 
    const tileOffset = mapValues({ x: length, y: width }, (v, k) => clamp((v / (2 * tileSize[k]) - (max[k] - 2) / 2), -1, 0));

    // Set the general mask pattern for each tile - this also centers the tile in the room if there are no tiles
    // FIXED: Use the exact same logic as production - hasTiles boolean multiplication works in JavaScript
    const hasTilesNumber = hasTiles ? 1 : 0;
    let masks = [{ x: tileSize.x * (tileOffset.x + .5 * hasTilesNumber), y: tileSize.y * (tileOffset.y + .5 * hasTilesNumber), mask: 0 }];
    if (tileSize.x > tileSize.y) masks = [{ x: tileSize.x * (tileOffset.x + .25), y: tileSize.y * (tileOffset.y + .5), mask: -1 }, { x: tileSize.x * (tileOffset.x + .75), y: tileSize.y * (tileOffset.y + .5), mask: 1 }];
    if (tileSize.x < tileSize.y) masks = [{ x: tileSize.x * (tileOffset.x + .5), y: tileSize.y * (tileOffset.y + .25), mask: -1 }, { x: tileSize.x * (tileOffset.x + .5), y: tileSize.y * (tileOffset.y + .75), mask: 1 }];

    // Adjust the tile and set the maxxed flags 
    const maxedX = max.x === draw.x;
    const maxedY = max.y === draw.y;

    const adjX = maxedX ? 1 : max.x - 2 - draw.x;
    const adjY = maxedY ? 1 : max.y - 2 - draw.y;

    const gridOffsetX = ceil(adjX / 2) + ((max.x - actual.x) % 2 === 0 ? 0 : .5);
    const gridOffsetY = ceil(adjY / 2) + ((max.y - actual.y) % 2 === 0 ? 0 : .5);

    // Final tile qty
    const qtyX = min([draw.x, max.x]) + (2 * adjX);
    const qtyY = min([draw.y, max.y]) + (2 * adjY);

    const tileCenters = [];
    for (let index = 0; index < (qtyX * qtyY); index++) {
      const idY = Math.floor(index / qtyX);
      const idX = index - idY * qtyX;

      // Actual tile index 
      const rx = idX - (maxedX ? 0 : ceil(adjX / 2) * 2);
      const ry = idY - (maxedY ? 0 : ceil(adjY / 2) * 2);

      const blocked = find(checked, { x: rx, y: ry });

      // Adjust the mask to the current position
      const adjustedMasks = masks.map((mask) => {
        const dx = mask.x + tileSize.x * (idX - gridOffsetX);
        const dy = mask.y + tileSize.y * (idY - gridOffsetY);
        
        // Check if this specific part of the tile is blocked
        // A tile half is blocked if:
        // - The tile has been checked/blocked AND
        // - Either both halves are blocked (mask === 0) OR this specific half is blocked (mask matches)
        const isBlocked = blocked ? (blocked.mask === 0 || blocked.mask === mask.mask) : false;
        
        return {
          ...mask,
          x: dx + ceilingTransform.x,
          y: dy + ceilingTransform.y,
          rx,
          ry,
          id: `tile_${rx}_${ry}_${mask.mask}`,
          blocked: isBlocked
        }
      });
      // Filter masks that are outside the room 
      const roomFiltered = adjustedMasks.filter(mask => inRange(mask.x, left + .01, left + length) && inRange(mask.y, top + .01, top + width));

      // Add Centers
      tileCenters.push(...roomFiltered);
    }

    // if Tiles are vertical - sort by position
    return (tileSize.x < tileSize.y) ? sortBy(tileCenters, tile => tile.y) : tileCenters;
  }
);


/**
 * SINGLE SOURCE OF TRUTH for "can this tile device land here?" — used by every path that
 * places or moves a tile device (drag-release in MuiDevices, click-to-place/add in the action
 * thunk + ExpansionDialog). It assembles the inputs once from state — tile centers, tile size,
 * and the positions already occupied by other tile devices — then delegates to the pure
 * `evaluateTileLanding` rules (blocked → occupied → silent). Callers decide how to react to the
 * outcome (revert + toast on move, warn + skip on add).
 *
 * @param state    - Redux state.
 * @param location - Drop/placement point in ROOM coordinates.
 * @param opts.deviceType - 'microphones' | 'speakers' | 'expansionMicrophones' | 'expansionSpeakers'.
 * @param opts.model      - The device's model key.
 * @param opts.index      - For a MOVE, the index of the device being moved (excluded from the
 *                          occupancy set). Omit for an ADD (nothing to exclude).
 */
export const evaluateTileDrop = (
  state: RootState,
  location: { x: number; y: number },
  opts: { deviceType: string; model: string; index?: number },
): TileLandingOutcome => {
  const area = getAreaState(state);
  if (!area) return { outcome: 'no_tiles' };
  const constants = toolIndex[state.container?.currentTool]?.constants;
  if (!constants) return { outcome: 'no_tiles' };

  const tileKey = area.ceilingTiles?.tile;
  const hasTiles = Boolean(tileKey && tileKey !== 'no_ceiling_tiles');
  const tileCenters = getCenters(state);
  const tileSize = hasTiles ? constants.ceilingTiles?.[tileKey] : null;

  // Every other tile device's centre — mics + speakers, primary + expansion ceiling — excluding
  // the entry being moved. Non-tile devices (wall/bar) sit off the grid and never coincide with a
  // tile centre, so they're harmless to include.
  const occupiedTiles: { x: number; y: number }[] = [];
  ([
    ['microphones', area.microphones],
    ['speakers', area.speakers],
    ['expansionMicrophones', area.expansionMicrophones],
    ['expansionSpeakers', area.expansionSpeakers],
  ] as [string, { layout?: GeometricDevice[] } | undefined][]).forEach(([layerType, dev]) => {
    (dev?.layout ?? []).forEach((d: GeometricDevice, i: number) => {
      if (layerType === opts.deviceType && i === opts.index) return;
      if (d?.location && typeof d.location.x === 'number' && typeof d.location.y === 'number') {
        occupiedTiles.push({ x: d.location.x, y: d.location.y });
      }
    });
  });

  // PIG-525: no focused-coverage gate on manual placement/movement. A tile device may land on any
  // unblocked, unoccupied in-room tile — matching the no-ceiling-tiles behavior. The coverage box
  // is only the reference for AUTOMATIC grid placement (see getDeviceLayouts), not a manual-drop
  // constraint; PIG-295 limits manual placement to blocked tiles and the room boundary only.
  return evaluateTileLanding(location, tileCenters, tileSize, {
    revertOnBlockedDrop: true,
    occupiedTiles,
  });
};


// HELPER FUNCTIONS -
/**
 * Generates an array of jump points for microphone placement.
 * @function getJumpPoints
 * @param {number} maxMics - The maximum number of microphones that can be placed.
 * @param {boolean} [isFlat=false] - Indicates if the placement surface is flat.
 * @returns {number[]} An array of jump points for microphone quantity.
 */
const getJumpPoints = (minMics = 1, maxMics, isFlat = false) => {
  const jumpPoints = isFlat
    ? Array.from({ length: maxMics }, (_, i) => i + 1)
    : [1, ...Array.from({ length: Math.floor(maxMics / 2) }, (_, i) => (i + 1) * 2)];

  const trimIndex = jumpPoints.indexOf(closest(jumpPoints, minMics));
  return jumpPoints.slice(trimIndex, jumpPoints.length)
};

/**
 * Calculates microphone placement based on room parameters and microphone types.
 * @function calculateMicPlacement
 * @param {Object} area - The area object containing room and microphone properties.
 * @param {Object} area.microphones - Microphone configuration for the area.
 * @param {string} area.microphones.model - The model of the microphone.
 * @param {Object} area.microphones.coverage - Coverage settings for the microphones.
 * @param {number} area.microphones.qty - Total number of microphones (if pre-defined).
 * @param {Object} area.customized - Customization flags for the area.
 * @param {boolean} area.customized.microphones - Whether microphone placement has been customized.
 * @param {Object} area.location - Location of the area within the room.
 * @param {Object} area.dimensions - Dimensions of the area.
 * @param {Object} area.table - Table configuration (for table microphones).
 * @param {Object} micOverlaps - Object containing overlap and jump point data for microphones.
 * @param {number[]} micOverlaps.overlaps - Array of overlap values.
 * @param {number[]} micOverlaps.jumpPoints - Array of jump points for microphone quantity.
 * @param {Object} coverageBox - The coverage box dimensions.
 * @param {number} coverageRadius - The coverage radius of the microphone.
 * @param {number} scale - The scale factor for coordinate calculations.
 * @param {Object} constants - Constants used in the calculation.
 * @param {Object} constants.equipment - Equipment-specific constants.
 * @param {Object} constants.equipment.microphones - Microphone-specific constants.
 * @param {Object} constants.defOverlap - Default overlap settings.
 * @param {Object} constants.tables - Table-specific constants.
 * 
 * @returns {Object} An object containing the calculated quantity and coordinates of microphones.
 * @property {Object} qty - The quantity of microphones in x and y directions (for ceiling mics).
 * @property {Array<Object>} coords - Array of microphone coordinates and models.
 * @property {number[]} [tableJumpPoints] - Jump points for table microphones (only for table mics).
 * 
 * @description
 * This function calculates microphone placement based on the type of microphone (ceiling, table, or bar)
 * and the room configuration. It handles different scenarios:
 * - For ceiling microphones, it calculates a grid placement.
 * - For table microphones, it calculates placement along a path on the table.
 * - For bar microphones, it places a single microphone at a fixed position.
 * The function also handles customized placements and ensures minimum distance between table microphones.
 */
export const calculateMicPlacement = (
  area, micOverlaps, coverageBox, coverageRadius, scale, constants, gridDensity,
  // Optional override so a caller can share this grid/shape-dispatch core for a DIFFERENT
  // mic device (e.g. the bar-expansion ceiling mic) than area.microphones, while still using
  // the same physical table (area.table) for shape dispatch. When omitted, behaviour is 100%
  // unchanged: model/coverage/height/qty/customized all come from area.microphones as before.
  // Mirrors calculateSpeakerPlacement's speakerConfig pattern exactly.
  micConfig?: {
    model: string; coverage: any; height: number; qty?: number; isCustomized?: boolean;
    // Confines round/keystone/vshape perimeter-path sampling to the portion of the table whose
    // room-space x-coordinate is >= restrictToBox.left (e.g. the residual area beyond a bar's own
    // coverage, for the bar-expansion ceiling mic). When omitted, the whole table path is sampled
    // — 100% unchanged primary-mic behaviour.
    restrictToBox?: { left: number; length: number };
  }
) => {
  const cfg: {
    model: string; coverage: any; height: number; qty?: number; isCustomized?: boolean;
    restrictToBox?: { left: number; length: number };
  } = micConfig ?? {
    model: area?.microphones?.model,
    coverage: area?.microphones?.coverage,
    height: area?.microphones?.height,
    qty: area?.microphones?.qty,
    isCustomized: area?.customized?.microphones,
  };
  // Safety check for the (possibly overridden) mic model
  if (!cfg?.model) return { qty: { x: 1, y: 1 }, coords: [] };
  const { model, coverage } = cfg;
  const totalMics = cfg.qty;
  const isCustomized = cfg.isCustomized;
  const { location: { top, left }, dimensions: { length, width }, table } = area;
  const { overlaps, jumpPoints } = micOverlaps;
  const { area: { table2wall }, defOverlap, tables, equipment } = constants;
  const { BAR, CEILING_MOUNTED, CEILING_TILE, TABLETOP, PENDANT } = equipment.micCategories;
  const micEquipment = equipment.microphones[model];
  const isCeilingMic = CEILING_MOUNTED.has(model);
  const isCeilingTileMic = CEILING_TILE.has(model);
  const isPendantMic = PENDANT.has(model);
  const isTableMic = TABLETOP.has(model);
  const isBarMic = BAR.has(model);
  // PIG-595/PIG-594: stamp placed ceiling/table mics with the family-aware name
  // (DCM-X on a Devio UCX / Android design) instead of the constants default, so the
  // Microphone-locations tab and the modelName written back to state (updateDevice)
  // agree with the equipment list. Devio SCX (Windows), Tesira, and non-ceiling/table
  // models keep the Parlé / constants name.
  const modelName = familyMicModelName(model, isDevioUcxDesign(area), micEquipment?.modelName);
  const isEndTable = tables[table.shape]?.endTable ?? false;

  let coords = [];
  let qty = { x: 1, y: 1 };
  let lineQty;
  let updatedJumpPoints = null;
  let micSpacing;
  let micQty; // Declare micQty at the outer scope

  const areaCenter = {
    x: (left + length) / 2,
    y: (top + width) / 2
  };
  const tableCenter = {
    x: table.size.length / 2,
    y: table.size.width / 2
  };




  if (isCeilingMic || isPendantMic) {
    const micCoverageKey = findKey(coverage);
    // PIG-535: the density seed is resolved by the caller (getDeviceLayouts) — it is the
    // box-appropriate default after a room/coverage/acoustics change, or a preserved
    // coverage-density slider pick while the geometry is unchanged. Resolving it there (rather
    // than re-seeding from the persisted microphones.qty here) is what stops a count carried
    // over from a larger room sticking when the room shrinks. Fall back to the box default if a
    // caller ever omits it.
    const defaultMicQty = jumpPoints[overlaps.indexOf(closest(overlaps, defOverlap[micCoverageKey]))];
    micQty = (gridDensity > 0) ? gridDensity : defaultMicQty;
  const ceilingMicPath = document.getElementById('micPath') as unknown as SVGPathElement | null;
  // Safely handle potential null references with fallbacks
  const micPathLength = ceilingMicPath ? ceilingMicPath.getTotalLength() : 0;
  // Use a try-catch block to handle potential getBBox() errors
  let width = 0, height = 0;
  try {
    if (ceilingMicPath) {
      const bbox = ceilingMicPath.getBBox();
      if (bbox) {
        width = bbox.width || 0;
        height = bbox.height || 0;
      }
    }
  } catch (e) {
    console.warn("Error getting bounding box for micPath:", e);
    // Continue with default values
  }
    const isFlat = width * height === 0;

    // A caller can restrict placement to a sub-region of the table by passing `restrictToBox`.
    //
    // ⚠️ NO PRODUCTION CALLER PASSES THIS AS OF 2026-08-13. It existed for the bar-expansion
    // ceiling mic, confined to the residual area beyond the bar's own coverage. That is not what
    // the spec asks for — Step 7 is a rectangular centred grid over the expansion box, with no
    // table-shape path tracing (tree-frog-product-specifications.md §5.5). Restricting the arc to
    // the residual centred it on a keystone/vshape table's narrow end, where the two legs of the
    // path converge, so an even mic count produced a mirror-image pair only centimetres apart.
    // Kept (not deleted) because the sub-arc search below is non-trivial and may be wanted if
    // shape-aware expansion placement is ever specified — but do not re-enable it for expansion
    // mics without a spec change. See Documentation/BAR_EXPANSION_CEILING_MIC_CHANGES_VS_SPEC.md §A2.
    //
    // Rather than sampling the full table then filtering out-of-region points
    // afterward — which leaves the right mics but spaces/counts them as if the WHOLE table were
    // available — find where the path's room-space x crosses `restrictToBox.left` and treat only
    // that sub-arc as the table length for the qty/spacing math below. The path runs out from one
    // bar-side corner, past the far point/edge, and back to the other bar-side corner, so x rises
    // then falls across the arc length: find the peak (coarse sample), then binary-search the
    // crossing on each monotonic leg to get a single contiguous residual arc [tStart, tEnd].
    let effectivePathLength = micPathLength;
    let pathStartOffset = 0;
    if (cfg.restrictToBox && ceilingMicPath && micPathLength > 0) {
      const xThreshold = cfg.restrictToBox.left;
      const roomXAtLen = (len: number): number => {
        const p = ceilingMicPath.getPointAtLength(len);
        return p.x / scale - tableCenter.x + areaCenter.x;
      };
      const SAMPLES = 200;
      let peakLen = 0, peakX = -Infinity;
      for (let i = 0; i <= SAMPLES; i++) {
        const len = (i / SAMPLES) * micPathLength;
        const x = roomXAtLen(len);
        if (x > peakX) { peakX = x; peakLen = len; }
      }
      if (peakX >= xThreshold) {
        const findCrossingLen = (lowLen: number, highLen: number, increasing: boolean): number => {
          let lo = lowLen, hi = highLen;
          for (let i = 0; i < 30; i++) {
            const mid = (lo + hi) / 2;
            const above = roomXAtLen(mid) >= xThreshold;
            if (increasing ? above : !above) hi = mid; else lo = mid;
          }
          return (lo + hi) / 2;
        };
        const tStart = findCrossingLen(0, peakLen, true);
        const tEnd = findCrossingLen(peakLen, micPathLength, false);
        if (tEnd > tStart) {
          pathStartOffset = tStart;
          effectivePathLength = tEnd - tStart;
        }
      }
      // If the threshold is never reached (peakX < xThreshold), fall back to the unrestricted
      // full-table length computed above — this keeps the math safe rather than silently
      // producing 0 mics.
    }

    let maxMics = Math.max(1, Math.floor((effectivePathLength / scale) / (coverageRadius)));
    maxMics = isFlat ? Math.ceil((isEndTable ? 1 : 2) + maxMics) / 2 : maxMics;
    const minMics = Math.max(1, round((effectivePathLength * (isFlat ? .5 : 1)) / (2 * coverageRadius * scale) + (isFlat ? 1 : 0)));
    updatedJumpPoints = [minMics, ...Array.from({ length: maxMics - minMics }, (_, i) => i + minMics + 1)];
    lineQty = (isCustomized && totalMics > 0) ? totalMics : (effectivePathLength * (isFlat ? .5 : 1)) / (constants.defTableOverlap * coverageRadius * scale) + (isFlat ? 1 : 0);
    lineQty = closest(updatedJumpPoints, lineQty);

    if (cfg.restrictToBox) {
      // The "isEndTable ? subtract 1" anchor-at-both-ends formula below is right for the WHOLE
      // table (its two path endpoints are the two real bar-side corners, worth a mic each) but
      // wrong for a restricted residual arc: tStart/tEnd are BOTH just the point where room-x
      // crosses restrictToBox.left — an arbitrary cutoff where the bar's own coverage ends, not a
      // seating corner. Anchoring samples there (as the unrestricted formula does) wastes mics on
      // the near edge, which the bar already covers, and — for a small lineQty like 2 — puts EVERY
      // sample at that same near edge (i=0 at tStart, i=lineQty-1 at tStart+effectivePathLength =
      // tEnd, both by definition at room-x == restrictToBox.left) while never reaching toward the
      // table's actual far end/point, leaving it uncovered. Center the lineQty samples evenly
      // within the residual arc instead (mirroring calculateCoordinates' centered-grid formula),
      // so even lineQty=2 reaches partway toward the far end instead of clustering both mics on
      // top of the bar's own coverage boundary.
      micSpacing = lineQty > 0 ? (effectivePathLength * (isFlat ? .5 : 1)) / lineQty : 0;
      pathStartOffset += micSpacing / 2;
    } else {
      micSpacing = lineQty > 1 ? (effectivePathLength * (isFlat ? .5 : 1)) / (lineQty - ((isFlat || isEndTable) ? 1 : 0)) : 0;
    }

    if (!isCustomized) {
      // PIG-295 AC9: BMA360D (CEILING_TILE) always uses a centered grid over the coverage
      // box regardless of table shape — shape-aware placement is deferred to the Phase 2
      // preset-based beam-visualization epic. Pole-mounted ceiling mics (TCM-X) keep the
      // existing round/keystone/vshape perimeter-path placement.
      if (['round', 'keystone', 'vshape'].includes(table.shape) && coverage.focused && !isCeilingTileMic) {
        // set the coords
        if (lineQty >= 1) {
          if (lineQty === 1) {
            coords.push({
              model,
              modelName,
              location: cfg.restrictToBox
                ? { x: cfg.restrictToBox.left + cfg.restrictToBox.length / 2, y: areaCenter.y }
                : { x: areaCenter.x + (table2wall / 2), y: areaCenter.y }
            })
          } else if (!ceilingMicPath || micPathLength <= 0) {
            // micPath not in DOM yet (e.g. recalculating on the way back from a view that
            // unmounts the floorplan canvas, like the equipment table) — getPointAtLength isn't
            // available to sample positions. The old fallback returned areaCenter for every one
            // of the `lineQty` mics, stacking them exactly on top of each other (looked like a
            // single re-centred mic, but was actually duplicates at one point). A freshly
            // computed grid fixes the stacking but still looks different from the real
            // path-based placement, causing a double-jump (once here, again once micPath mounts
            // and the real calc runs). Since the geometry hasn't changed, prefer reusing the
            // already-placed layout verbatim when its count still matches — no visible movement.
            // Only compute a fresh grid when there's nothing to preserve or the count changed.
            // micConfig means this call is for the bar-expansion ceiling mic, not area.microphones —
            // comparing against the PRIMARY mic's layout here (wrong device, almost always a
            // different count) would defeat this preserve check for the expansion mic, the same
            // bug fixed for expansion speakers just above (see calculateSpeakerPlacement).
            // Also: `lineQty` itself is derived from `effectivePathLength` (= micPathLength, which
            // is exactly 0 here because micPath is missing — that's WHY we're in this branch), so
            // it snaps to whatever the smallest valid jump-point is, not the true previously-placed
            // count. Comparing against it could never actually confirm "nothing changed" — trust
            // whatever's already placed instead.
            const existingMicLayout = micConfig ? area.expansionMicrophones?.layout : area.microphones?.layout;
            if (Array.isArray(existingMicLayout) && existingMicLayout.length > 0) {
              coords = existingMicLayout;
            } else {
              const fittedMicSpacing = lineQty > 1 ? coverageBox.length / lineQty : 0;
              coords = calculateCoordinates(model, modelName, coverageBox, { x: lineQty, y: 1 }, { x: fittedMicSpacing, y: fittedMicSpacing });
            }
            if (isFlat) {
              qty = { x: lineQty, y: 1 };
            };
          } else {
            coords = Array.from({ length: lineQty }, (_, i) => {
              const point = ceilingMicPath.getPointAtLength(pathStartOffset + i * micSpacing);
              return {
                model,
                modelName,
                location: {
                  x: point.x / scale - tableCenter.x + areaCenter.x,
                  y: point.y / scale - tableCenter.y + areaCenter.y
                }
              };
            });
            if (isFlat) {
              qty = { x: lineQty, y: 1 };
            };
          }
        }
      } else {
        // calculate microphone spacing and quantity
        updatedJumpPoints = null;
        // PIG-535: fall back to the coverage mode's default overlap (not a hard-coded 1.5) if the
        // lookup ever misses — keeps spacing consistent with the chosen coverage instead of an
        // arbitrary tighter value that inflated counts for stale, off-box mic quantities.
        const overlapFactor = overlaps[jumpPoints.indexOf(micQty)] ?? defOverlap[micCoverageKey] ?? 1.5;
        micSpacing = mapValues({ x: 0, y: 0 }, () => Math.max(coverageRadius * overlapFactor, coverageRadius));
        
        // Ensure qty doesn't exceed reasonable bounds
        qty = {
          x: max([1, min([10, round(coverageBox.length / micSpacing.x)])]),
          y: max([1, min([10, round(coverageBox.width / micSpacing.y)])])

        };

        // Calculate the mics coordinates 
        coords = calculateCoordinates(model, modelName, coverageBox, qty, micSpacing);

        // Optimize the grid based on table shape if we have a table
        if (table && table.shape && coverage.focused && qty.x >= 2 && qty.y >= 3) {
          // Remove center mics for large tables (similar to original logic)
          const endAdj = isEndTable ? 1 : 0;
          const spliceSize = Math.max(0, qty.x - (2 - endAdj));
          for (let y = qty.y - 2; y >= 1; y--) {
            const startIndex = y * qty.x - endAdj + 1;
            coords.splice(startIndex, spliceSize);
          }
        };
      }
      if (isPendantMic || isCeilingMic) coords.forEach(device => device.location.z = cfg.height);
      // PIG-448: BMA360D entries need location.z set to the mic mount height so the
      // positioning-detail tooltip (BubbleContent) can show Z alongside X/Y. The
      // mount height is `cfg.height` (area.microphones.height by default), kept in sync with
      // the auto-lower adjustment from PIG-295 (calculateDevicesMiddleware.maybeAutoLowerBma360d).
      if (isCeilingTileMic) coords.forEach(device => {
        device.rotation = 0;
        device.location.z = cfg.height;
      });
    };
  } else if (isTableMic) {
  const tableMicPath = document.getElementById('micPath') as unknown as SVGPathElement | null;
  // Safely handle potential null references with fallbacks
  const micPathLength = tableMicPath ? tableMicPath.getTotalLength() : 0;
  // Use a try-catch block to handle potential getBBox() errors
  let width = 0, height = 0;
  try {
    if (tableMicPath) {
      const bbox = tableMicPath.getBBox();
      if (bbox) {
        width = bbox.width || 0;
        height = bbox.height || 0;
      }
    }
  } catch (e) {
    console.warn("Error getting bounding box for micPath in tableMic:", e);
    // Continue with default values
  }
    const isFlat = width * height === 0;

    // Estimate the maximum number of mics
    let maxMics = Math.max(1, Math.floor(micPathLength / scale));
    maxMics = isFlat ? Math.ceil((isEndTable ? 1 : 2) + maxMics) / 2 : maxMics;

    let minMics = (micPathLength * (isFlat ? .5 : 1)) / (2 * coverageRadius * scale) + (isFlat ? 1 : 0);
    minMics += isEndTable && !isFlat ? 1 : 0;

    // Get the jumpPoints
    updatedJumpPoints = getJumpPoints(minMics, maxMics, isFlat || isEndTable);
    // Estimate ideal starting number 
    let micQty = totalMics > 0 ? totalMics : (micPathLength * (isFlat ? .5 : 1)) / (constants.defTableOverlap * coverageRadius * scale) + (isFlat ? 1 : 0);
    micQty = closest(updatedJumpPoints, micQty);

    // Calculagte microphone spacing
    micSpacing = micQty > 1 ? (micPathLength * (isFlat ? .5 : 1)) / (micQty - ((isFlat || isEndTable) ? 1 : 0)) : 0;

    if (micPathLength >= 0) {
      if (micQty === 1) {
        if (!tableMicPath) {
          coords.push({
            model,
            modelName,
            location: {
              x: areaCenter.x,
              y: areaCenter.y
            }
          });
        } else {
          const halfWayPoint = tableMicPath.getPointAtLength(micPathLength / 2);
          const middlePoint = {
            x: halfWayPoint.x / scale - tableCenter.x + areaCenter.x,
            y: halfWayPoint.y / scale - tableCenter.y + areaCenter.y
          }
          coords.push({
            model,
            modelName,
            location: {
              x: isFlat ? areaCenter.x : middlePoint.x,
              y: isFlat ? areaCenter.y : middlePoint.y
            }
          })
        }
      } else {
        coords = Array.from({ length: micQty }, (_, i) => {
          if (!tableMicPath) {
            return {
              model,
              modelName,
              location: {
                x: areaCenter.x,
                y: areaCenter.y
              }
            };
          }
          const point = tableMicPath.getPointAtLength(i * micSpacing);
          return {
            model,
            modelName,
            location: {
              x: point.x / scale - tableCenter.x + areaCenter.x,
              y: point.y / scale - tableCenter.y + areaCenter.y
            }
          };
        });
      };
    };

    // Merge close mics, TTM mics can't be closer than 1 meter
    coords.forEach((elem, index, array) => {
      const other = array.find(test =>
        Math.abs(test.location.x - elem.location.x) < 1 &&
        Math.abs(test.location.y - elem.location.y) < 1 &&
        test !== elem
      );

      if (other) {
        if (Math.abs(elem.location.x - other.location.x) <= 0.001) {
          elem.location.x = other.location.x = (elem.location.x + other.location.x) / 2;
        };
        if (Math.abs(elem.location.y - other.location.y) <= 0.001) {
          elem.location.y = other.location.y = (elem.location.y + other.location.y) / 2;
        };
      };
    });

    // Clean up duplicates
    coords = coords.filter((elem, index, array) =>
      index === array.findIndex(test =>
        Math.abs(test.location.x - elem.location.x) <= 0.001 && Math.abs(test.location.y - elem.location.y) <= 0.001
      )
    );

    // qty = coords.length; // For table mics, qty is stored as a number, not an object
  } else if (isBarMic) {
    if (!isCustomized) {
      coords = [
        {
          model,
          modelName,
          location: {
            x: left + .134,
            y: top + width / 2
          }
        }];
    };
  };
  return { qty, coords, updatedJumpPoints };
};

/**
* @function calculateMicPlotData
* @description Calculates distance-based plot data for microphone coverage visualization.
* 
* @param {string} id - Area identifier
* @param {Array} [layout=[]] - Array of microphone positions
* @param {Object} grid - Grid parameters for calculations
* @param {number} grid.gridSpacing - Distance between grid points
* @param {number} grid.gridXOffset - X-axis grid offset
* @param {number} grid.gridYOffset - Y-axis grid offset
* @param {number} grid.gridX - Number of grid points in X direction
* @param {number} grid.gridY - Number of grid points in Y direction
* 
* @returns {Object|undefined} Plot data for heatmap visualization
* @property {string} id - Area identifier
* @property {number} receiverSpacing - Grid spacing used for calculations
* @property {number} receiverXOffset - X offset of the grid
* @property {number} receiverYOffset - Y offset of the grid
* @property {Array<Array<number>>} mapArray - 2D array of distances to nearest microphone
* 
* @description
* For each grid point:
* 1. Calculates distance to each microphone
* 2. Takes minimum distance (closest microphone)
* 3. Builds 2D array of distances for heatmap
* 
* @example
* const plotData = calculateMicPlotData(
*   'area1',
*   [{ location: { x: 10, y: 20 } }],
*   { gridSpacing: 1, gridXOffset: 0, gridYOffset: 0, gridX: 20, gridY: 20 }
* );
* 
* @note
* - Returns undefined if layout is empty
* - Uses Euclidean distance calculation
* - Maintains grid parameters in return data for visualization
*/
const calculateMicPlotData = (id, layout = [], grid, maxRadius?: number) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  const mapArray = [];
  if (layout.length === 0) return;
  // When a per-mic max coverage radius is provided (table-expansion case), two adjustments
  // make the gradient match the standalone TTM-X look inside the combined bar plot:
  //   1. Scale raw distance by (5.6 / maxRadius), mirroring calculateBarMicPlotData's
  //      acoustic-scaling reference (good-acoustics bar reach = 5.6m). With legendMax = 12m
  //      the radius now lands ~47% along the gradient instead of ~14% (which read as flat
  //      green inside the radius) — so the inner gradient is visible like a standalone TTM-X.
  //   2. Beyond ~2× the effective radius (matching standalone TTM-X's legendMax = ceil(2×radius)),
  //      collapse to Infinity so combinePlotData lets the bar pattern (or nothing) show through
  //      instead of merging an out-of-range expansion distance and smearing coverage across
  //      the room.
  const hasClamp = typeof maxRadius === 'number' && maxRadius > 0;
  const scaleFactor = hasClamp ? 5.6 / (maxRadius as number) : 1;
  const cutoff = hasClamp ? (maxRadius as number) * 2 : Infinity;
  for (let y = 0; y <= gridY; y++) {
    const lineArray = [];
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const distanceAtPoint = layout.reduce((minDist, mic) => {
        const dx = mic.location.x - point.x;
        const dy = mic.location.y - point.y;
        const dist = Math.hypot(dx, dy);
        return Math.min(minDist, dist)
      }, Infinity);
      lineArray.push(distanceAtPoint > cutoff ? Infinity : distanceAtPoint * scaleFactor);
    };
    mapArray.push(lineArray);
  };
  const data = {
    id: `${id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
  };
  return data;
};

/**
* @function calculateBarMicPlotData
* @description Calculates acoustic-aware plot data for bar microphone coverage visualization.
* Takes into account room acoustics to adjust microphone coverage patterns.
* 
* @param {Object} area - Area information including microphone model and acoustics
* @param {Array} [layout=[]] - Array of microphone positions
* @param {Object} grid - Grid parameters for calculations
* @param {Object} constants - System constants including microphone properties and acoustic settings
* 
* @returns {Object|undefined} Plot data for microphone coverage visualization
* @property {string} id - Area identifier with 'full' suffix
* @property {number} receiverSpacing - Grid spacing for plot
* @property {number} receiverXOffset - X offset of grid
* @property {number} receiverYOffset - Y offset of grid
* @property {Array<Array<number>>} mapArray - 2D array of acoustic-adjusted distances
* 
* @description
* Calculation process:
* 1. Gets current acoustic condition and corresponding microphone max distance
* 2. For each grid point, calculates distance to closest microphone
* 3. Normalizes distances based on acoustic conditions (poor acoustics = shorter effective range)
* 4. Returns plot data showing acoustic-aware coverage patterns
* 
* @example
* const plotData = calculateBarMicPlotData(
*   {
*     id: 'area1',
*     microphones: { model: 'bar' },
*     acoustics: 'fair'
*   },
*   [{ location: { x: 0, y: 7.5 } }],
*   { gridSpacing: 0.1, ... },
*   constants
* );
* 
* @note
* - Returns undefined if layout is empty
* - Uses acoustic-aware distance scaling based on micData array
* - Bar microphones: [3.5, 4.3, 5.6, 7.0, 9.8] meters for [poor, fair, good, great, perfect]
*/
export const calculateBarMicPlotData = (area, layout = [], grid, constants) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  const { microphones: { model, modelName }, acoustics: currentAcoustics } = area;
  if (layout.length === 0) return;

  const { equipment, acoustics, area: { talkerHeight } } = constants;
  const micConfig = equipment.microphones[model];
  if (!micConfig) {
    console.error(`[calculateBarMicPlotData] No mic config found for model "${model}" — skipping plot calculation`);
    return;
  }

  // CBC bars have different coverage radii than VBC/ABC bars
  const isCBCBar = equipment.micCategories.CBC.has(model);
  const micData = isCBCBar
    ? [1.0, 1.3, 1.6, 2.1, 2.9] // CBC bars: smaller focused coverage with extended fade
    : micConfig.micData; // VBC/ABC bars: use config values

  const acousticIndex = acoustics.indexOf(currentAcoustics);
  let maxDistance = micData[acousticIndex]; // Get max distance for current acoustic condition

  // VBC 2500a, VBC 2800, ABC 2500a use circular (omnidirectional) coverage
  // CBC variants use racetrack/capsule coverage aligned to the bar orientation
  const useCircularCoverage = ['Parlé VBC 2500a', 'Parlé VBC 2800', 'Parlé ABC 2500a'].includes(modelName);

  // Racetrack params (only used for CBC models)
  const barRotation = layout[0]?.horAngle || 0;
  const angleRad = (barRotation * Math.PI) / 180;

  // For CBC bars, calculate coverage dimensions based on mounting height
  // Reference: CBC-2500 Coverage Engine (Section 5 & 7.3)
  let barLength = 2.103; // Default: 6.9ft at 12ft height
  const perpBarMultiplier = 1.0;

  if (isCBCBar && layout[0]?.location?.z !== undefined) {
    // Use the actual mic height from layout (updates when user changes bar height)
    const micHeight = layout[0].location.z;
    const heightAboveTalker = (micHeight - talkerHeight) * 3.28084; // Convert to feet

    // Lookup table from CBC-2500 Coverage Engine (Section 7.3)
    // [h (ft), halfLength (ft), shortRadius (ft)]
    const heightTable = [
      [5.249, 4.317, 5.896],  // 9.2ft ceiling
      [5.906, 4.162, 5.686],  // 9.8ft ceiling
      [7.054, 3.829, 5.232],  // 11ft ceiling
      [8.071, 3.452, 4.717],  // 12ft ceiling (default)
      [9.186, 2.912, 3.977],  // 13.1ft ceiling
      [10.073, 2.330, 3.184], // 14ft ceiling
      [10.827, 1.617, 2.209]  // 14.8ft ceiling
    ];

    // Find the two nearest heights for interpolation
    let lowerIdx = 0;
    let upperIdx = heightTable.length - 1;

    for (let i = 0; i < heightTable.length - 1; i++) {
      if (heightAboveTalker >= heightTable[i][0] && heightAboveTalker <= heightTable[i + 1][0]) {
        lowerIdx = i;
        upperIdx = i + 1;
        break;
      }
    }

    // Linear interpolation
    const [h1, halfLen1, shortRad1] = heightTable[lowerIdx];
    const [h2, halfLen2, shortRad2] = heightTable[upperIdx];

    let halfLength, shortRadius;
    if (lowerIdx === upperIdx) {
      halfLength = halfLen1;
      shortRadius = shortRad1;
    } else {
      const t = (heightAboveTalker - h1) / (h2 - h1);
      halfLength = halfLen1 + t * (halfLen2 - halfLen1);
      shortRadius = shortRad1 + t * (shortRad2 - shortRad1);
    }

    // Convert to meters and set parameters
    barLength = halfLength * 2 * 0.3048; // 2 * halfLength, feet to meters
    const shortRadiusMeters = shortRadius * 0.3048;

    // Scale maxDistance based on shortRadius vs reference (4.717ft at 12ft height)
    const referenceShortRadius = 4.717 * 0.3048; // meters
    maxDistance = maxDistance * (shortRadiusMeters / referenceShortRadius);
  }

  const mapArray = [];

  for (let y = 0; y <= gridY; y++) {
    const lineArray = [];
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const distanceAtPoint = layout.reduce((minDist, mic) => {
        const dx = point.x - mic.location.x;
        const dy = point.y - mic.location.y;

        let dist: number;
        if (useCircularCoverage) {
          dist = Math.hypot(dx, dy);
        } else {
          const localX = dx * Math.cos(-angleRad) - dy * Math.sin(-angleRad);
          const localY = dx * Math.sin(-angleRad) + dy * Math.cos(-angleRad);
          const clampedX = Math.max(-barLength / 2, Math.min(barLength / 2, localX));
          dist = Math.hypot(localX - clampedX, localY / perpBarMultiplier);
        }

        return Math.min(minDist, dist);
      }, Infinity);

      // Apply acoustic scaling to the distance
      // In poor acoustics, the effective range is shorter, so we scale up the perceived distance
      // This makes the coverage appear smaller in the plot
      const acousticScaleFactor = 5.6 / maxDistance; // Use "good" acoustics (5.6m) as reference
      const scaledDistance = distanceAtPoint * acousticScaleFactor;
      lineArray.push(scaledDistance);
    };
    mapArray.push(lineArray);
  };

  const data = {
    id: `${area.id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
  };
  return data;
};

/** Sampling grid parameters shared by the plot-data calculators. */
interface Grid {
  gridSpacing: number;
  gridXOffset: number;
  gridYOffset: number;
  gridX: number;
  gridY: number;
}

/**
 * Camera layout entry with the base Device shape plus the dynamic aim/angle fields
 * (aimPoint, aimX, aimY, coverageAngle, horAngle, ...) the camera-plot helpers below
 * attach and reshuffle at runtime — genuinely dynamic, so kept as an index signature
 * rather than an exhaustive interface.
 */
type CameraLayoutDevice = GeometricDevice & Record<string, any>;

/** Distance-grid plot data, as produced by calculateMicPlotData / calculateBarMicPlotData. */
interface MicPlotData {
  id: string;
  receiverSpacing: number;
  receiverXOffset: number;
  receiverYOffset: number;
  mapArray: number[][];
}

/**
 * Merges bar mic and expansion ceiling mic plot data into a single coverage map.
 * For each grid point, takes the minimum distance across both sources so the
 * heatmap reflects the best coverage at every position.
 */
const combinePlotData = (barPlot: MicPlotData | null | undefined, expansionPlot: MicPlotData | null | undefined) => {
  if (!barPlot || !expansionPlot) return barPlot ?? expansionPlot;
  return {
    ...barPlot,
    mapArray: barPlot.mapArray.map((row: number[], y: number) =>
      row.map((barDist: number, x: number) => Math.min(barDist, expansionPlot.mapArray[y]?.[x] ?? Infinity))
    ),
  };
};

/**
 * @function calculateWallSpeakerTargetPoint
 * @description Calculates the target point for wall-mounted speakers based on room configuration and table/chair positions.
 *
 * Logic:
 * 1. No table in room: Target point 2ft (0.6096m) from back wall at centerline and listener height (1.2m)
 * 2. Table at default position: Target point at furthest chair from front wall (where speakers are mounted)
 * 3. Table has been moved: Furthest chair distance but at centerline
 *
 * @param {Object} area - The area object containing room and table configuration.
 * @param {Object} constants - Constants containing chair size and listener height.
 * @returns {Object} Target point with {x, y} coordinates in room space.
 */
export const calculateWallSpeakerTargetPoint = (area, constants) => {
  const { location: { top, left }, dimensions: { length, width }, table } = area;
  const { chair } = constants;

  // Case 1: No table in room - target 2ft from back wall at centerline
  if (!table?.shape) {
    return {
      x: left + length - 0.6096,  // 2ft from back wall
      y: top + width / 2           // Centerline
    };
  }

  // Table exists - calculate chair positions
  const tableShape = table.shape;
  const tableSize = table.size;

  // Get table center position (accounting for transform if moved)
  const tableCenterX = left + length / 2 + (table.transform?.x || 0);
  const tableCenterY = top + width / 2 + (table.transform?.y || 0);

  // Get chair configuration from table constants
  const tableConfig = MuiTableConstants.tables[tableShape];
  if (!tableConfig || !tableConfig.chairs) {
    // Fallback to 2ft from back wall if table config not found
    return {
      x: left + length - 0.6096,
      y: top + width / 2
    };
  }

  // Get chair array (positions relative to table center)
  const chairConfig = chair || { size: 0.6096, clearance: 0.3048 };
  const { chairArray } = tableConfig.chairs(tableSize, chairConfig, table.slope);

  if (!chairArray || chairArray.length === 0) {
    // No chairs - fallback to 2ft from back wall
    return {
      x: left + length - 0.6096,
      y: top + width / 2
    };
  }

  // Transform chair positions to room coordinates
  const chairsInRoomCoords = chairArray.map(chair => ({
    id: chair.id,
    x: tableCenterX + chair.x,
    y: tableCenterY + chair.y
  }));

  // Find the chair furthest from front wall (largest x value)
  // Front wall is at x = left (speakers are mounted there)
  const furthestChair = chairsInRoomCoords.reduce((furthest, chair) => {
    return chair.x > furthest.x ? chair : furthest;
  }, chairsInRoomCoords[0]);

  // Target x: furthest chair from front wall. Target y: always room centreline.
  // Using furthestChair.y causes off-centre aiming for keystone/vshape/ushape because
  // their back-of-table chairs share the same x, so reduce picks the first one which
  // starts at a negative y offset from table centre.
  return {
    x: furthestChair.x,
    y: top + width / 2
  };
};

/**
 * PIG-594: round a ceiling-speaker grid up to an even total. Desono C-IC6 (and
 * the LP / LP-TAA variants) are sold in pairs, so a multi-speaker layout must
 * never resolve to an odd count. A genuine single-speaker grid (total === 1) is
 * left untouched — PIG-628: that's a legitimate, centered single speaker, not
 * an odd pair-grid that needs rounding. A grid product is odd only when BOTH
 * axes are odd; round UP by growing a single-row/column grid along its
 * populated axis, and otherwise by adding a row. Returns a new `{ x, y }`
 * (input left untouched).
 *
 * @param {{ x: number, y: number }} qty
 * @returns {{ x: number, y: number }}
 */
export const evenCeilingSpeakerGrid = ({ x, y }: { x: number; y: number }): { x: number; y: number } => {
  const total = x * y;
  if (total <= 1 || total % 2 === 0) return { x, y };
  if (y === 1) return { x: x + 1, y };
  if (x === 1) return { x, y: y + 1 };
  return { x, y: y + 1 };
};

/**
 * PIG-626: drop speakers that fall outside the room, guaranteeing the layout never
 * empties. Non-rectangular tables (keystone, V-shape, round) place ceiling speakers
 * along an SVG path offset outward from the table edge, which for a large table in a
 * modestly larger room can extend past the walls. This mirrors the microphone
 * room-boundary filter (and its centre fallback) so speakers stay inside for every
 * table shape.
 *
 * @param coords         computed speaker coordinates (each with a `location`)
 * @param fullRoomBox    the room bounding box (`coverageBoxes['full_room']`)
 * @param makeFallback   builds a single centre-of-room speaker when all are filtered out
 * @returns the in-room speakers, or a single centre speaker if none remained
 */
export const keepSpeakersInsideRoom = <T extends { location: any }>(
  coords: T[],
  fullRoomBox: any,
  makeFallback: () => T,
): T[] => {
  const inside = coords.filter(spkr => isInside(values(spkr.location), fullRoomBox));
  if (inside.length === 0 && coords.length > 0) {
    console.warn('All speakers filtered out, placing one in room center as fallback');
    return [makeFallback()];
  }
  return inside;
};

/**
 * Calculates loudspeaker placement based on room parameters and constants.
 * @function
 * @param {Object} area - The area object containing room and speaker properties.
 * @param {number} spkrOverlap - The overlap value for speakers.
 * @param {Object} coverageBox - The coverage box dimensions.
 * @param {number} coverageRadius - The coverage radius of the speaker.
 * @param {Object} micQty - The quantity of microphones.
 * @param {Object} constants - Constants used in the calculation.
 * @returns {Array} An array of calculated speaker coordinates.
 */
export const calculateSpeakerPlacement = (
  area, spkrOverlap, coverageBox, coverageRadius, micQty, scale, constants,
  // Optional override so a caller can share this grid/shape-dispatch core for a DIFFERENT
  // speaker device (e.g. the bar-expansion ceiling/pendant speaker) than area.speakers, while
  // still using the same physical table (area.table) for shape dispatch. When omitted, behaviour
  // is 100% unchanged: model/coverage/height/customized all come from area.speakers as before.
  // referenceBox: the PRIMARY speaker's own coverage box (before any bar-reach carve-out), passed
  // by expansion callers only. Used solely to scale the spacing cap below — see the comment at the
  // default: grid branch. Omitted entirely for the primary itself, which is what keeps the
  // primary's placement 100% unchanged (ratio defaults to 1, i.e. no scaling).
  speakerConfig?: {
    model: string; coverage: any; height: number; isCustomized?: boolean;
    referenceBox?: { length: number; width: number };
    // Confines round/keystone/vshape perimeter-path sampling to the portion of the table whose
    // room-space x-coordinate is >= restrictToBox.left (e.g. the residual area beyond a bar's own
    // coverage, for the bar-expansion ceiling/pendant speaker). Mirrors calculateMicPlacement's
    // restrictToBox exactly. When omitted, the whole table path is sampled — 100% unchanged
    // primary-speaker behaviour.
    restrictToBox?: { left: number; length: number };
  }
) => {
  const cfg: {
    model: string; coverage: any; height: number; isCustomized?: boolean;
    referenceBox?: { length: number; width: number };
    restrictToBox?: { left: number; length: number };
  } = speakerConfig ?? {
    model: area?.speakers?.model,
    coverage: area?.speakers?.coverage,
    height: area?.speakers?.height,
    isCustomized: area?.customized?.speakers,
  };

  // Safety check for the (possibly overridden) speaker model
  if (!cfg?.model) return [];

  // Safety check for coverageBox. The existing-layout fallback only makes sense for the default
  // (area.speakers) config — an explicit speakerConfig caller has its own layout elsewhere.
  if (!coverageBox || typeof coverageBox !== 'object') {
    console.warn('calculateSpeakerPlacement: coverageBox is undefined or invalid, returning existing layout');
    return speakerConfig ? [] : (area.speakers?.layout || []);
  }

  const { location: { top, left }, dimensions: { length, width }, table: { shape, size } } = area;
  const { model, coverage, height: speakerHeight } = cfg;
  const isSpeakerCustom = cfg.isCustomized;

  // If speakers are manually placed (customized), return existing layout without recalculation.
  // Only applies to the default config — a caller passing an explicit speakerConfig (e.g. bar
  // expansion) already gates customization itself before ever calling in here, so this core stays
  // a pure grid calculation for that path (no customization branching inside it).
  if (isSpeakerCustom && !speakerConfig && area.speakers?.layout?.length > 0) {
    return area.speakers.layout;
  }
  // const { model, coverage } = area.speakers;
  // const { speakers: isSpeakerCustom } = customized;
  // const { table: { shape, size } } = area;
  const { equipment, area: { speakerAiming }, tables } = constants;
  const { isCeilingSpkr = false, isBarSpkr = false, isPendantSpkr = false, modelName } = equipment.speakers[model];
  const isEndTable = tables[shape]?.endTable ?? false;
  const isCeilingTileMic = equipment?.micCategories?.CEILING_TILE?.has(area.microphones?.model) ?? false;
  // Devio UCX (Tree Frog) ceiling expansion mic design — scoped to the EXPANSION speaker caller
  // (speakerConfig is only ever passed for that path, see its doc comment above) so this doesn't
  // also change the PRIMARY bar speaker's own grid, which is unrelated to whether the room
  // happens to have ceiling expansion mics active. See the xMics*yMics === 1 branch below: the
  // expansion speaker caller always passes the fixed { x: 1, y: 1 } "micQty" shape (there's no
  // real mic-count analogue for it), so without this it can collapse to a genuinely lone speaker
  // whenever the residual coverage box computes a 1x1 grid — reported for a bar + ceiling
  // expansion mic room whose single auto-placed pendant expansion speaker landed off-centerline
  // (from the separate avoidNearbyMics nudge) with no partner to pair it with.
  const isExpansionCeilingMic = Boolean(speakerConfig) && area.expansionMicType === 'ceiling';
  // BMA360D: normalise ceiling/pendant grid after adjustment — 1–2 → 2×1 (landscape) or 1×2
  // (portrait), 3–4 → 2×2, >4 unchanged.
  // PIG (orientation fix): the 1–2 case previously hardcoded { x: 1, y: 2 } regardless of the
  // room/table's orientation, which forced a vertical (stacked) pair of speakers even for a
  // wide (landscape) room/table — the exact bug reported for the ceiling-tile mic (BMA360D)
  // layout. It now mirrors the orientation used elsewhere in this function (landscape ->
  // wider on x, portrait -> taller on y).
  const applyBmaGridNormalisation = (qty, spacing) => {
    const total = qty.x * qty.y;
    const isPortraitBox = coverageBox.width > coverageBox.length;
    const target = (total === 1 || total === 2) ? (isPortraitBox ? { x: 1, y: 2 } : { x: 2, y: 1 })
      : (total === 3 || total === 4) ? { x: 2, y: 2 }
      : null;
    if (target) {
      qty.x = target.x;
      qty.y = target.y;
      if (findKey(coverage) === 'focused') {
        spacing.x = coverageBox.length / qty.x;
        spacing.y = coverageBox.width / qty.y;
      }
    }
  };

  /**
   * Expansion speakers: split a grid that collapsed to a SINGLE LINE along the box's major axis
   * into two lines across its minor axis (the "straddle the table" rule already stated for the
   * 1×1 case in `adjustSpeakerQuantity` below, extended to every count).
   *
   * Why this is needed at all: `computeExpansionSpeakerCoords` has no mic-grid analogue for a bar
   * mic + scattered expansion mics, so it always calls in with the fixed `micQty = { x: 1, y: 1 }`.
   * That pins `adjustSpeakerQuantity` to its `xMics * yMics === 1` branch, which only ever fixes a
   * degenerate 1×1 grid. Every "round a single row up to a balanced grid" rule (2×1 → 2×2,
   * 3×1 → 2×2, and PIG-524's 4×1 → second row) lives in the `=== 2` branch and is therefore
   * unreachable for expansion speakers. A narrow focused box — anything table-width + 2 × table2wall
   * against the C-IC6's ~1.37 m radius — yields one row, so the whole grid landed strung out along
   * the room centerline instead of over the seating on each side of the table.
   * Reported for 40' × 30' / rectangle / Good: a 4 × 1 row at y = 4.57.
   *
   * FOCUSED ONLY, and applied in the `default:` grid branch only (a sibling of
   * `applyBmaGridNormalisation` above), which is what keeps it off the round/keystone/vshape
   * perimeter-path branches and out of full_room. In full_room the box spans the whole room, so a
   * single line there is a real coverage answer rather than a collapsed one — e.g. a round table's
   * 3.07 × 9.14 residual band correctly gets 1 × 4 across the width, and splitting that to 2 × 2
   * would open a 4.57 m gap between rows against a 2.74 m coverage diameter.
   *
   * Total is PRESERVED, not grown: an even count halves exactly (4 × 1 → 2 × 2), an odd one rounds
   * up by one (5 × 1 → 3 × 2), which also keeps `evenCeilingSpeakerGrid`'s even-count guarantee
   * intact since `ceil(n / 2) * 2` is always even. This deliberately differs from PIG-524's
   * ceiling-tile-mic rule, which ADDS a row (4 × 1 → 3 × 2 = 6) to keep the along-length density;
   * here the quantity the residual box asks for is already the right quantity and only its
   * arrangement was wrong.
   */
  const applyExpansionStraddleNormalisation = (qty, spacing) => {
    if (findKey(coverage) !== 'focused') return;
    // Orientation comes from the TABLE, not from `coverageBox`. Everywhere else in this function
    // "portrait" is judged on the box, but here the box is the bar-expansion RESIDUAL — a slice off
    // the far end of the table, whose aspect ratio is a function of how far the BAR reaches, not of
    // which way the table (and therefore the seating) runs. Sweeping 14–60' × 10–40' rooms at six
    // ceiling heights and both speaker mounts, reading the box instead of the table changes the
    // final layout in 271 of 28,128 cases, and always for the worse — 18' × 10' at an 8' ceiling
    // is the clearest: the residual is 1.62 × 2.31, so the box test says "portrait" and leaves the
    // pair at (3.446, 1.524) and (4.257, 1.524), both on the room centerline and 0.81 m apart,
    // under a 2.44 × 0.91 table that is plainly landscape. Reading the table straddles them to
    // (3.852, 0.750) and (3.852, 2.298).
    // Falls back to the box only when there is no table to read (focused coverage, no table shape).
    const isPortraitTable = (shape && size?.length && size?.width)
      ? size.width > size.length
      : coverageBox.width > coverageBox.length;
    if (!isPortraitTable && qty.y === 1 && qty.x >= 2) {
      // Landscape table: seating runs along the two long sides, so the second line is a second ROW.
      qty.x = Math.ceil(qty.x / 2);
      qty.y = 2;
    } else if (isPortraitTable && qty.x === 1 && qty.y >= 2) {
      // Portrait table (taller than it is wide): same rule, transposed.
      qty.y = Math.ceil(qty.y / 2);
      qty.x = 2;
    } else {
      return;
    }
    // Even cell-centre distribution across the box, the same convention `updateSpacing` uses.
    spacing.x = coverageBox.length / qty.x;
    spacing.y = coverageBox.width / qty.y;
  };
    const spacing = mapValues({ x: 0, y: 0 }, () => coverageRadius * spkrOverlap);

  /**
   * Calculates the initial quantity of speakers based on coverage box dimensions and spacing.
   * @function
   * @inner
   * @returns {Object} An object with x and y properties representing the number of speakers in each direction.
   */
  const getSpeakerQuantity = () => {
    const box = mapKeys(pick(coverageBox, ['length', 'width']), (_, k) => k === 'length' ? 'x' : 'y');
    return mapValues(box, (dim, key) => getSpeakerAxisCount(dim, spacing[key], coverageBox.spkrAdj));
  };

  /**
   * Adjusts the speaker quantity and spacing based on microphone quantity and room characteristics.
   * @function
   * @inner
   * @param {Object} spkrQty - The initial speaker quantity object with x and y properties.
   * @returns {Object} An object containing the adjusted quantity and spacing for speakers.
   * @property {Object} qty - The adjusted quantity of speakers with x and y properties.
   * @property {Object} spacing - The adjusted spacing between speakers with x and y properties.
   */
  const adjustSpeakerQuantity = (spkrQty) => {
    // Handle cases where micQty might be a number instead of an object
    let xMics, yMics;
    if (typeof micQty === 'object' && micQty !== null && 'x' in micQty && 'y' in micQty) {
      ({ x: xMics, y: yMics } = micQty);
    } else {
      // When micQty is a number (e.g., from custom microphone count), assume 1x1 grid
      const micCount = typeof micQty === 'number' ? micQty : 1;
      xMics = micCount;
      yMics = 1;
    }
    let { x: xSpkrs, y: ySpkrs } = spkrQty;

    // Determine if table is in portrait orientation (taller than wide)
    const isPortrait = coverageBox.width > coverageBox.length;

    // Helper to adjust spacing after changing speaker count
    const updateSpacing = (dimension) => {
      if (shape && findKey(coverage) === "focused") {
        spacing[dimension] = coverageBox[dimension === 'x' ? 'length' : 'width'] / (dimension === 'x' ? xSpkrs : ySpkrs);
      }
    };

    // PIG-628 follow-up: a single mic whose speaker grid has genuinely collapsed to one
    // centred speaker (e.g. a tall ceiling growing the speaker's own coverage radius, same
    // root cause as the 2-mic case below) must stay a single speaker, not be forced into a
    // pair — the xMics*yMics === 2 branch below already leaves a total-1 grid untouched for
    // the same reason. Ceiling-tile mics (BMA360D) are one exception: they always need a pair
    // regardless of collapse, which applyBmaGridNormalisation enforces independently of this
    // branch, so only bump here for that case. The Devio UCX ceiling expansion mic + expansion
    // speaker case (isExpansionCeilingMic) is the other: the expansion speaker caller's fixed
    // { x: 1, y: 1 } "micQty" has no real mic-count meaning (see its computation above), so a
    // lone auto-placed expansion speaker there is never a deliberate 1-speaker design decision
    // the way it can be for the primary speaker — bump it to a pair the same as BMA360D.
    if (xMics * yMics === 1) {
      if (xSpkrs === 1 && ySpkrs === 1 && isExpansionCeilingMic) {
        // Expansion speakers sit above a table with seating on both long sides — the pair must
        // straddle the table (two rows split across the box's WIDTH), not sit side by side along
        // its length, regardless of the box's aspect ratio. Unlike the BMA360D case just below,
        // this is not a portrait/landscape choice: a wide box's extra speaker still belongs in a
        // second row over the other side of the table, never a second column further down the
        // same side.
        ySpkrs += 1;
        updateSpacing('x');
      } else if (xSpkrs === 1 && ySpkrs === 1 && isCeilingTileMic) {
        if (isPortrait) {
          ySpkrs += 1;
          updateSpacing('x');
        } else {
          xSpkrs += 1;
          updateSpacing('y');
        }
      }
    }

    // For two microphone case
    else if (xMics * yMics === 2) {
      const spkrCount = xSpkrs * ySpkrs;
      if (spkrCount === 2) {
        // Matches stage-build: landscape table grows the Y axis (2×1 → 2×2), portrait
        // grows the X axis (1×2 → 2×2) — a wide table's extra speaker belongs in a
        // second row alongside the pair, not stretched into a 3×1 single row.
        if (isPortrait) {
          xSpkrs += 1;
          updateSpacing('y');
        } else {
          ySpkrs += 1;
          updateSpacing('x');
        }
      }
      else if (spkrCount === 3) {
        // Matches stage-build: a raw 3×1 (or 1×3) grid rounds up to a balanced 2×2
        // rather than staying a single row of 3 (PIG-476 round-up-to-even behavior).
        if (isPortrait) {
          ySpkrs -= 1;
          xSpkrs += 1;
          updateSpacing('y');
        } else {
          xSpkrs -= 1;
          ySpkrs += 1;
          updateSpacing('x');
        }
      }
      // PIG-524: BMA360D ceiling-tile mic grids of 4×1+ (or 1×4+) — a long-room case unique
      // to tile mics — still get a second row, trimmed back by mic count. This is additive to
      // stage-build and does not affect the 2-count/3-count cases above.
      else if (!isPortrait && ySpkrs === 1 && xSpkrs >= 4 && isCeilingTileMic) {
        ySpkrs += 1;
        if (xSpkrs > xMics) xSpkrs -= 1;
        updateSpacing('x');
      }
      else if (isPortrait && xSpkrs === 1 && ySpkrs >= 4 && isCeilingTileMic) {
        xSpkrs += 1;
        if (ySpkrs > yMics) ySpkrs -= 1;
        updateSpacing('y');
      }
    }

    // For all other cases
    else {
      if (!isPortrait && yMics * ySpkrs === 1) { // yMics * ySpkrs === 1
        ySpkrs += 1;
        if (xSpkrs > xMics) {
          xSpkrs -= 1;
        };
        updateSpacing('x');
      } else if (isPortrait && xMics * xSpkrs === 1) { // if (xMics * xSpkrs === 1)
        xSpkrs += 1;
        if (ySpkrs > yMics) {
          ySpkrs -= 1;
        };
        updateSpacing('y');
      };
    };

    return {
      qty: { x: xSpkrs, y: ySpkrs },
      spacing
    };
  };

  const areaCenter = {
    x: (left + length) / 2,
    y: (top + width) / 2
  };
  const tableCenter = {
    x: size.length / 2,
    y: size.width / 2
  }

  let coords = [];
  // Ceiling AND pendant speakers share the same overhead placement (grid, even-count pairing, and
  // perimeter-path for shaped tables). They differ only in physical props (coverage angle, mount
  // height) which come from equipment.speakers[model]. Ceiling-tile mounting specifics (tile-snap on
  // mic collision) stay ceiling-only — a pendant hangs below the ceiling and can't sit in a tile.
  if (isCeilingSpkr || isPendantSpkr) {
    if (!isSpeakerCustom) {
      const initialQty = getSpeakerQuantity();
      const { qty: adjustedQty, spacing: adjustedSpacing } = adjustSpeakerQuantity(initialQty);
      // PIG-594: ceiling loudspeakers (Desono C-IC6 family) are sold in pairs, so
      // the placed grid must resolve to an even total. Feed the even count into
      // calculateCoordinates below so the layout — and the equipment list, which
      // counts layout.length — never shows an odd quantity. (The round-table
      // branch already rounds its own path-based count to even.)
      const evenQty = evenCeilingSpeakerGrid(adjustedQty);
      if (evenQty.x !== adjustedQty.x || evenQty.y !== adjustedQty.y) {
        adjustedQty.x = evenQty.x;
        adjustedQty.y = evenQty.y;
        if (findKey(coverage) === 'focused') {
          adjustedSpacing.x = coverageBox.length / adjustedQty.x;
          adjustedSpacing.y = coverageBox.width / adjustedQty.y;
        }
      }
      const spkrPath = document.getElementById('spkrPath') as unknown as SVGPathElement | null;
      let spkrLength = (spkrPath?.getTotalLength() ?? 0) - (isEndTable ? coverageRadius * scale : 0);
      let startingPoint = isEndTable ? coverageRadius * scale / 2 : 0;

      // A caller can restrict placement to a sub-region of the table (currently: the
      // bar-expansion ceiling/pendant speaker, confined to the residual area beyond the bar's
      // own coverage) by passing `restrictToBox`. Without this, the round/keystone/vshape
      // branches below sample the WHOLE table's #spkrPath — the same physical DOM path element
      // the primary speaker uses — regardless of how small/offset the expansion box is. That
      // made the expansion speakers' positions drift across the entire table perimeter whenever
      // qty/spacing changed (e.g. an acoustics change altering the auto ceiling height, which
      // feeds the expansion speaker's own coverage radius), instead of staying confined to the
      // residual area. Mirrors calculateMicPlacement's restrictToBox handling exactly: find where
      // the path's room-space x crosses `restrictToBox.left` and treat only that sub-arc as the
      // table length for the qty/spacing math below.
      if (cfg.restrictToBox && spkrPath && spkrLength > 0) {
        const fullPathLength = spkrPath.getTotalLength();
        const xThreshold = cfg.restrictToBox.left;
        const roomXAtLen = (len: number): number => {
          const p = spkrPath.getPointAtLength(len);
          return p.x / scale - tableCenter.x + areaCenter.x;
        };
        const SAMPLES = 200;
        let peakLen = 0, peakX = -Infinity;
        for (let i = 0; i <= SAMPLES; i++) {
          const len = (i / SAMPLES) * fullPathLength;
          const x = roomXAtLen(len);
          if (x > peakX) { peakX = x; peakLen = len; }
        }
        if (peakX >= xThreshold) {
          const findCrossingLen = (lowLen: number, highLen: number, increasing: boolean): number => {
            let lo = lowLen, hi = highLen;
            for (let i = 0; i < 30; i++) {
              const mid = (lo + hi) / 2;
              const above = roomXAtLen(mid) >= xThreshold;
              if (increasing ? above : !above) hi = mid; else lo = mid;
            }
            return (lo + hi) / 2;
          };
          const tStart = findCrossingLen(0, peakLen, true);
          const tEnd = findCrossingLen(peakLen, fullPathLength, false);
          if (tEnd > tStart) {
            startingPoint = tStart;
            spkrLength = tEnd - tStart;
          }
        }
        // If the threshold is never reached (peakX < xThreshold), fall back to the unrestricted
        // full-table length computed above — resolveExpansionSpeakerBox already guards against
        // this in practice, but this keeps the math safe rather than silently producing 0 speakers.
      }
      const { width = 0, height = 0 } = spkrPath?.getBoundingClientRect() ?? {};
      const isFlat = width * height === 0;

      switch (true) {
        case (shape === 'round' && coverage.focused):
          if (adjustedQty.x * adjustedQty.y === 1) {
            coords.push({
              model,
              modelName,
              location: cfg.restrictToBox
                ? { x: cfg.restrictToBox.left + cfg.restrictToBox.length / 2, y: areaCenter.y, z: speakerHeight }
                : { x: areaCenter.x, y: areaCenter.y, z: speakerHeight }
            })
          } else {
            let qty = Math.floor(spkrLength / (spacing.x * scale))
            qty = qty % 2 === 0 ? qty : qty + 1
            if (!spkrPath || spkrLength <= 0) {
              // spkrPath not in DOM yet (e.g. recalculating on the way back from a view that
              // unmounts the floorplan canvas, like the equipment table). Any freshly computed
              // placement here — even one fitted to the coverage box — uses a different
              // algorithm than the real path-based sampling below, so it looks different from
              // what was already on screen: speakers would jump to the fallback layout on the
              // way back, then jump AGAIN once spkrPath mounts and the real calc runs next.
              // Since the room/table geometry hasn't changed, the already-placed layout is
              // still correct — reuse it verbatim when its count matches what we'd place, so
              // navigation causes no visible movement at all. Only compute a fresh grid when
              // there's genuinely nothing to preserve (e.g. the very first calculation, or the
              // required count actually changed).
              // speakerConfig means this call is for the bar-expansion speaker, not area.speakers.
              // Also: `adjustedQty.x * adjustedQty.y` is a GRID-shaped reference count — it has no
              // relationship to this round-table branch's own qty (derived from spkrLength, which is
              // exactly the thing unavailable here because spkrPath is missing). Comparing the
              // existing layout's length against it was really comparing against a placeholder that's
              // 0 whenever this fallback runs, so the "count matches" check below could never pass —
              // it fell through to a freshly-fitted rectangular grid every time the canvas remounted
              // (e.g. navigating back from the equipment table), landing speakers off the table edge.
              // There's no way to know the "true" path-based count without the path itself, so just
              // trust whatever's already placed — the room/table geometry hasn't changed.
              const existingLayout = speakerConfig ? area.expansionSpeakers?.layout : area.speakers?.layout;
              if (Array.isArray(existingLayout) && existingLayout.length > 0) {
                coords = existingLayout;
              } else {
                const fittedSpacing = {
                  x: adjustedQty.x > 1 ? coverageBox.length / adjustedQty.x : 0,
                  y: adjustedQty.y > 1 ? coverageBox.width / adjustedQty.y : 0,
                };
                coords = calculateCoordinates(model, modelName, coverageBox, adjustedQty, fittedSpacing, speakerHeight);
              }
            } else {
              // When circumference < required spacing (e.g. high ceiling → large coverage radius),
              // use minimum 2 speakers placed symmetrically on the round path
              if (qty <= 0) qty = 2;
              const space = spkrLength / qty
              coords = Array.from({ length: qty }, (_, i) => {
                const point = spkrPath.getPointAtLength(startingPoint + i * space);
                return {
                  model,
                  modelName,
                  location: {
                    x: point.x / scale - tableCenter.x + areaCenter.x,
                    y: point.y / scale - tableCenter.y + areaCenter.y,
                    z: speakerHeight
                  }
                }
              })
            }
          }
          break;
        case (['keystone', 'vshape'].includes(shape) && coverage.focused && size.width >= spacing.y):
          if (initialQty.x * initialQty.y === 1) {
            coords.push({
              model,
              modelName,
              location: cfg.restrictToBox
                ? { x: cfg.restrictToBox.left + cfg.restrictToBox.length / 2, y: areaCenter.y, z: speakerHeight }
                : { x: areaCenter.x, y: areaCenter.y, z: speakerHeight }
            })
          } else if (!spkrPath || spkrLength <= 0) {
            // Same DOM-timing gap as the round-table branch above: spkrPath isn't mounted yet
            // (e.g. navigating back from the equipment table). Prefer reusing the already-placed
            // layout (see the comment in the round-table branch above — a freshly computed grid
            // looks different from the real path-based placement and causes a double-jump).
            // Without this guard `qty` computes to 0 here, wiping the speaker layout entirely.
            // speakerConfig means this call is for the bar-expansion speaker, not area.speakers —
            // see the matching comment in the round-table branch above for why comparing against
            // the wrong device's layout defeated this preserve check for expansion speakers.
            // Also (see the fuller explanation in the round-table branch above): `adjustedQty.x *
            // adjustedQty.y` is a grid-shaped reference with no relationship to this branch's own
            // path-based qty, which is unknowable here since spkrPath (the thing that qty is
            // derived from) is exactly what's missing. Trust whatever's already placed instead of
            // requiring it to match an unrelated count.
            const existingLayout = speakerConfig ? area.expansionSpeakers?.layout : area.speakers?.layout;
            if (Array.isArray(existingLayout) && existingLayout.length > 0) {
              coords = existingLayout;
            } else {
              const fittedSpacing = {
                x: adjustedQty.x > 1 ? coverageBox.length / adjustedQty.x : 0,
                y: adjustedQty.y > 1 ? coverageBox.width / adjustedQty.y : 0,
              };
              coords = calculateCoordinates(model, modelName, coverageBox, adjustedQty, fittedSpacing, speakerHeight);
            }
          } else {
            // Force an even count, same as the round-table branch above (`qty % 2 === 0 ? qty :
            // qty + 1`) — a keystone/vshape table is symmetric top-to-bottom, and an odd count
            // puts one speaker right on the table's point/apex instead of matching top/bottom
            // pairs. That apex speaker also tends to land on top of whatever mic is anchored at
            // the same point (mics converge to the apex too), which is what looked like a
            // stray, out-of-place speaker. Rounding up to the next even count restores the
            // symmetric pairing instead of ever placing a lone apex speaker.
            let qty = Math.floor(spkrLength / (spacing.x * scale))
            qty = qty % 2 === 0 ? qty : qty + 1
            if (qty <= 0) qty = 2;
            let space: number;
            let sampleStart = startingPoint;
            if (cfg.restrictToBox) {
              // Anchoring samples at both arc endpoints (the unrestricted formula's `-1`
              // denominator below) is right for the WHOLE table — its two path endpoints are
              // real bar-side seating corners, each worth a speaker — but wrong for a
              // restricted residual arc, whose endpoints are just where the bar's own coverage
              // ends (resolveExpansionSpeakerBox's cutoff), not seating corners. Anchoring there
              // wastes a speaker on the near edge the bar already covers and, for a small qty,
              // can put every sample at that same near edge. Center the qty samples evenly
              // within the residual arc instead, mirroring calculateMicPlacement's
              // restrictToBox handling.
              space = qty > 0 ? (spkrLength * (isFlat ? .5 : 1)) / qty : 0;
              sampleStart = startingPoint + space / 2;
            } else {
              space = (spkrLength * (isFlat ? .5 : 1)) / (qty - ((isFlat || isEndTable) ? 1 : 0));
            }
            coords = Array.from({ length: qty }, (_, i) => {
              const point = spkrPath.getPointAtLength(sampleStart + i * space);
              return {
                model,
                modelName,
                location: {
                  x: point.x / scale - tableCenter.x + areaCenter.x,
                  y: point.y / scale - tableCenter.y + areaCenter.y,
                  z: speakerHeight
                }
              }
            })
          }
          break;

        default:
          if (isCeilingTileMic) applyBmaGridNormalisation(adjustedQty, adjustedSpacing);
          // Expansion speakers only (speakerConfig is passed by that caller alone) — see the
          // function's own doc comment for why the shared `adjustSpeakerQuantity` can't do this.
          if (speakerConfig) applyExpansionStraddleNormalisation(adjustedQty, adjustedSpacing);
          // calculateCoordinates centers `qty` points `spacing` apart around the box's midpoint
          // unconditionally — it has no awareness of the box's own dimensions. adjustedSpacing is
          // normally derived from coverageRadius * overlap (a function of the speaker's coverage
          // circle, not the box), so when qty is small relative to the box (e.g. a narrow
          // bar-expansion residual box needing only 2 speakers), spacing * (qty - 1) can exceed
          // the box's length/width and the outermost speakers land outside the box entirely.
          //
          // Rather than clamping straight to a flush fit (0 margin — speakers sitting exactly on
          // the box edges), scale spacing down by how much smaller this box is than the PRIMARY
          // speaker's own coverage box (cfg.referenceBox, passed only by expansion callers). That
          // reproduces the SAME spacing-to-box-length ratio the primary speaker already has in its
          // own (larger) box, so the expansion pair keeps a proportional margin from the edges
          // instead of being pushed flush — for the primary itself referenceBox is absent, so the
          // ratio is 1 and nothing changes.
          //
          // Only engage this when the natural (unscaled) spacing would actually overflow the box
          // — i.e. only as an overflow correction, same as the old flush-fit cap it replaces. A
          // pendant's smaller coverage radius already yields a smaller natural spacing than a
          // ceiling speaker's, so a 2-speaker pendant grid often already fits its box with room to
          // spare; scaling it down unconditionally shrank it further than necessary and pulled it
          // right on top of a nearby mic. Leaving already-fitting spacing untouched avoids that.
          const referenceBox = speakerConfig?.referenceBox;
          const lengthRatio = referenceBox?.length ? coverageBox.length / referenceBox.length : 1;
          const widthRatio = referenceBox?.width ? coverageBox.width / referenceBox.width : 1;
          const naturalSpanX = adjustedSpacing.x * (adjustedQty.x - 1);
          const naturalSpanY = adjustedSpacing.y * (adjustedQty.y - 1);
          if (adjustedQty.x > 1 && naturalSpanX > coverageBox.length) {
            adjustedSpacing.x *= lengthRatio;
          }
          if (adjustedQty.y > 1 && naturalSpanY > coverageBox.width) {
            adjustedSpacing.y *= widthRatio;
          }
          // Flush-fit cap still applies afterward as a hard safety net in case the scaled value
          // alone isn't enough to bring the grid back inside the box.
          adjustedSpacing.x = adjustedQty.x > 1
            ? Math.min(adjustedSpacing.x, coverageBox.length / (adjustedQty.x - 1))
            : adjustedSpacing.x;
          adjustedSpacing.y = adjustedQty.y > 1
            ? Math.min(adjustedSpacing.y, coverageBox.width / (adjustedQty.y - 1))
            : adjustedSpacing.y;
          coords = calculateCoordinates(model, modelName, coverageBox, adjustedQty, adjustedSpacing, speakerHeight);
          // Optimize the grid based on table shape if we have a table
          if (shape && coverage.focused && adjustedQty.x >= 2 && adjustedQty.y >= 3) {
            // Remove center spkrs for large tables (similar to original logic)
            const endAdj = isEndTable ? 1 : 0;
            const spliceSize = Math.max(0, adjustedQty.x - (2 - endAdj));
            for (let y = adjustedQty.y - 2; y >= 1; y--) {
              const startIndex = y * adjustedQty.x - endAdj + 1;
              coords.splice(startIndex, spliceSize);
            }
          };

      };

      // Calculate aiming angles for ceiling speakers
      // Each aims downward from its position with consistent horizontal orientation
      coords = coords.map((coord) => {
        // Calculate vertical angle for downward aim (straight down = -90°)
        const listenerHeight = speakerAiming.listenerHeight;
        const heightDiff = speakerHeight - listenerHeight;
        const minDistance = 0.001; // Very small distance for nearly vertical aim
        let verAngle = -(Math.atan2(heightDiff, minDistance) * 180) / Math.PI;

        // Constrain vertical angle
        verAngle = Math.max(
          speakerAiming.minVerticalAngle,
          Math.min(speakerAiming.maxVerticalAngle, verAngle)
        );

        return {
          ...coord,
          horAngle: 90,
          verAngle: Math.round(verAngle * 10) / 10,
          height: speakerHeight
        };
      });
    };
  } else if (isBarSpkr) {
    if (!isSpeakerCustom) {
      const micModel = area.microphones.model;
      const modelName = constants.equipment.microphones[micModel]?.modelName;
      coords = [
        {
          model,
          modelName,
          location: {
            x: left + .134,
            y: top + width / 2,
            z: speakerHeight
          }
        }];
    };
  } else {
    // Handle wall speakers (pendant now shares the ceiling/overhead branch above)
    const { isWallSpkr = false } = constants.equipment.speakers[model];
    if (isWallSpkr && !isSpeakerCustom) {
      // Wall speakers - two speakers on front wall (left side of floorplan)
      // Use user-set height if available, otherwise use default from equipment config
      const speakerHeight = area.speakers.height || constants.equipment.speakers[model].height();

      // Default: 1/8 distance from corners on front wall (left wall)
      // Front wall (left side) spans the WIDTH (vertical dimension)
      const offsetFromCorner = width / 8;
      // Offset from wall to position speaker inside room (prevents icon from overlapping border)
      const wallOffset = 0.15;  // meters - positions speaker slightly inside room

      // Always recalculate target point from current room dimensions for non-customized speakers.
      // area.speakers.targetPoint is stale after dimension changes since it is only set on
      // initialization and mounting-type changes, so it cannot be used here.
      const targetPoint = calculateWallSpeakerTargetPoint(area, constants);


      // Position two speakers on front wall (left side), offset slightly inward
      const positions = [
        {
          location: {
            x: left + wallOffset,  // Front wall (left side) with offset into room
            y: top + offsetFromCorner  // Spread along width (vertical)
          },
          height: speakerHeight
        },
        {
          location: {
            x: left + wallOffset,  // Front wall (left side) with offset into room
            y: top + width - offsetFromCorner  // Spread along width (vertical)
          },
          height: speakerHeight
        }
      ];


      // Create speaker layout with aiming angles and rotation
      coords = positions.map((pos, index) => {
        const angles = calculateAimingAngles(pos.location, targetPoint, pos.height, constants);
        const rotation = calculateSpeakerRotation(pos.location, length, width);

        return {
          id: `wall-speaker-${index + 1}`,
          model,
          modelName,
          location: pos.location,
          height: pos.height,
          horAngle: angles.horAngle,
          verAngle: angles.verAngle,
          targetPoint: { ...targetPoint },  // Individual target (for unlinked mode)
          linked: true,
          rotation  // Rotate to face center of room based on wall position
        };
      });
    } else if (isWallSpkr && isSpeakerCustom && area.speakers.layout) {
      // Wall speakers exist and are customized - recalculate angles only (preserve positions)

      // Get current height from speakers state
      const speakerHeight = area.speakers.height || constants.equipment.speakers[model].height();

      // Determine target point based on table/chair configuration
      // Use custom target point if user has manually set one, otherwise calculate automatically
      const sharedTargetPoint = area.speakers.targetPoint || calculateWallSpeakerTargetPoint(area, constants);


      // Preserve existing positions but recalculate angles and rotation
      coords = area.speakers.layout.map((speaker) => {
        // Use individual target point if unlinked and speaker has one, otherwise use shared
        const targetPoint = (!area.speakers.linkedSpeakers && speaker.targetPoint)
          ? speaker.targetPoint
          : sharedTargetPoint;


        const angles = calculateAimingAngles(speaker.location, targetPoint, speakerHeight, constants);
        const rotation = calculateSpeakerRotation(speaker.location, length, width);


        return {
          ...speaker,
          height: speakerHeight,
          horAngle: angles.horAngle,
          verAngle: angles.verAngle,
          targetPoint: { ...targetPoint },
          rotation  // Update rotation based on current wall position
        };
      });
    }
  }
  return coords
};

/**
* @function calculagteCeilingRelativePloptData
* @description Calculates relative SPL plot data for ceiling-mounted speakers.
* 
* @param {Object} area - Area information and dimensions
* @param {Array} [layout=[]] - Array of speaker positions
* @param {Object} grid - Grid parameters for calculations
* @param {Object} constants - System constants including speaker properties
* 
* @returns {Object|undefined} Plot data for relative SPL visualization
* @property {string} id - Area identifier
* @property {number} receiverSpacing - Grid spacing
* @property {number} receiverXOffset - X offset of grid
* @property {number} receiverYOffset - Y offset of grid
* @property {Array<Array<number>>} mapArray - 2D array of adjusted SPL values
* @property {number} averageTarget - Target average SPL level
* 
* @description
* Calculation process:
* 1. Defines average box based on speaker coverage radius
* 2. For each grid point:
*    - Calculates distance to each speaker
*    - Applies polar pattern attenuation
*    - Computes distance attenuation
*    - Combines into total SPL
* 3. Post-processes SPL values:
*    - Calculates average target level
*    - Applies non-linear scaling above target + 3dB
* 
* SPL Calculation Components:
* - Direct sound level based on sensitivity
* - Distance attenuation (-20*log10(distance))
* - Angular attenuation from polar response
* - Combined using logarithmic addition
* 
* @example
* const plotData = calculagteCeilingRelativePloptData(
*   {
*     id: 'area1',
*     speakers: { model: 'SP100', height: 3 },
*     location: { top: 0, left: 0 },
*     dimensions: { length: 20, width: 15 }
*   },
*   [{ location: { x: 10, y: 7.5 } }],
*   { gridSpacing: 0.5, ... },
*   constants
* );
* 
* @note
* - Returns undefined if layout is empty
* - Uses 5-degree steps for polar pattern interpolation
* - Applies special scaling to levels above target + 3dB
* - Average calculated only within coverage radius of speakers
*/
export const calculagteCeilingRelativePloptData = (area, layout = [], grid, constants) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  const { speakers: { model, height }, location: { top, left }, dimensions: { length, width } } = area;
  if (layout.length === 0) return;
  const { equipment: { speakers: { [model]: { coverageAngle, sensitivity, polarData } } }, area: { talkerHeight } } = constants;

  // Calculate the average box - this is the area that will be considered to calculate average to keep variation constrained 
  const coverageRadius = (height - talkerHeight * Math.tan(coverageAngle * Math.PI / 180));
  const averageBox = {
    x0: Math.max(left, (layout.reduce((min, speaker) => Math.min(min, speaker.location.x), layout[0].location.x)) - coverageRadius) - left,
    y0: Math.max(top, (layout.reduce((min, speaker) => Math.min(min, speaker.location.y), layout[0].location.y)) - coverageRadius) - top,
    x1: Math.min((layout.reduce((max, speaker) => Math.max(max, speaker.location.x), layout[0].location.x)) + coverageRadius, length - left) - left,
    y1: Math.min((layout.reduce((max, speaker) => Math.max(max, speaker.location.y), layout[0].location.y)) + coverageRadius, width - top) - top
  };

  let mapArray = [];
  let runningTotal = 0, runningCount = 0;
  let minSPL = Infinity, maxSPL = 0;
  for (let y = 0; y <= gridY; y++) {
    const lineArray = []
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const pointSum = layout.reduce((sum, spkr) => {
        const dx = spkr.location.x - point.x;
        const dy = spkr.location.y - point.y;
        const dist = Math.hypot(dx, dy, height);
        const angle = Math.acos(height / dist) * (180 / Math.PI);
        // Directly calculate the enclosing angles in 5-degree steps
        const lowerAngle = floor(angle / 5) * 5;
        const upperAngle = ceil(angle / 5) * 5;
        // Calculate the polar attenuation 
        const interpPercentage = angle === 0 ? 1 : (angle - lowerAngle) / (upperAngle - lowerAngle);
        const angleAttenuation = polarData[lowerAngle] + (polarData[upperAngle] - polarData[lowerAngle]) * interpPercentage;
        // Calculate distance attenuation
        const distanceAttenuation = -20 * Math.log10(dist);
        // Add the resulting level at distance 
        const pointLevel = Math.pow(10, (sensitivity + distanceAttenuation + angleAttenuation) * .1);
        return sum + pointLevel;
      }, 0);
      const pointTotal = 10 * Math.log10(pointSum)
      lineArray.push(pointTotal);


      maxSPL = Math.max(maxSPL, pointTotal);
      // Sum values only if they're inside the averageBox
      if (x >= averageBox.x0 && x <= averageBox.x1 && y >= averageBox.y0 && y <= averageBox.y1) {
        runningTotal += pointSum;
        runningCount++;
        minSPL = Math.min(minSPL, pointTotal);
      };
    };

    mapArray.push(lineArray);
  };
  const averageTarget = 10 * Math.log10(runningTotal / runningCount);

  const yellowLevel = averageTarget + 3;
  const factor = Math.sqrt((3.5 - 3) / Math.pow(maxSPL - yellowLevel, 2));
  mapArray = mapArray.map(line => {
    return line.map(point => {
      if (point >= yellowLevel) {
        const aboveYellow = point - yellowLevel;
        const scaledValue = Math.pow(aboveYellow * factor, 2) + 3;
        return averageTarget + scaledValue;
      } else {
        return point
      }
    })
  })


  const data = {
    id: `${area.id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
    averageTarget
  };
  return data
};

/**
* @function calculateWallRelativePlotData
* @description Calculates relative SPL plot data for wall-mounted speakers.
*
* @param {Object} area - Area information including speaker model and acoustics
* @param {Array} [layout=[]] - Array of speaker objects with location, height, horAngle, verAngle
* @param {Object} grid - Grid parameters for calculations
* @param {Object} constants - System constants including speaker properties
*
* @returns {Object|undefined} Plot data for relative SPL visualization
* @property {string} id - Area identifier
* @property {number} receiverSpacing - Grid spacing
* @property {number} receiverXOffset - X offset of grid
* @property {number} receiverYOffset - Y offset of grid
* @property {Array<Array<number>>} mapArray - 2D array of adjusted SPL values
* @property {number} averageTarget - Target SPL level based on acoustics
*
* @description
* Calculation process:
* 1. Reconstructs each speaker's 3D aim unit vector from horAngle + verAngle
* 2. For each grid point at listener height:
*    - Computes 3D vector from speaker to point
*    - Derives off-axis angle via dot product with aim vector
*    - Applies polar pattern attenuation at that angle
*    - Applies distance attenuation (-20*log10(dist))
*    - Combines all speaker contributions logarithmically
* 3. Post-processes SPL values using acoustics-based averageTarget with
*    non-linear scaling above target + 3dB (same as bar speakers)
*
* SPL Calculation Components:
* - Direct sound level based on sensitivity
* - Distance attenuation (-20*log10(3D distance to listener ear))
* - Angular attenuation from polar response (angle from speaker aim axis)
* - Combined using logarithmic addition
*
* @note
* - Returns undefined if layout is empty
* - Listener height taken from speakerAiming.listenerHeight
* - averageTarget comes from targetSPL[acoustics] (not computed from data)
* - Off-axis angle clamped to 90° (beyond rear hemisphere treated as max attenuation)
*/
export const calculateWallRelativePlotData = (area, layout = [], grid, constants) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  const { speakers: { model }, acoustics: currentAcoustics } = area;
  if (layout.length === 0) return;
  const { equipment: { speakers: { [model]: { sensitivity, polarData, targetSPL = [] } } }, acoustics, area: { speakerAiming: { listenerHeight } } } = constants;

  // Precompute each speaker's 3D aim unit vector from stored horAngle / verAngle
  const speakersWithAim = layout.map(spkr => {
    const horRad = spkr.horAngle * Math.PI / 180;
    const verRad = spkr.verAngle * Math.PI / 180;
    return {
      ...spkr,
      aimX: Math.cos(verRad) * Math.cos(horRad),
      aimY: Math.cos(verRad) * Math.sin(horRad),
      aimZ: Math.sin(verRad),
    };
  });

  let mapArray = [];
  let minSPL = Infinity, maxSPL = 0;

  for (let y = 0; y <= gridY; y++) {
    const lineArray = [];
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const pointSum = speakersWithAim.reduce((sum, spkr) => {
        const toPointX = point.x - spkr.location.x;
        const toPointY = point.y - spkr.location.y;
        const toPointZ = listenerHeight - spkr.height;
        const dist = Math.hypot(toPointX, toPointY, toPointZ);

        // Off-axis angle: angle between aim vector and vector from speaker to point
        const dot = (spkr.aimX * toPointX + spkr.aimY * toPointY + spkr.aimZ * toPointZ) / dist;
        const angle = Math.min(90, Math.acos(clamp(dot, -1, 1)) * (180 / Math.PI));

        const lowerAngle = floor(angle / 5) * 5;
        const upperAngle = ceil(angle / 5) * 5;
        let angleAttenuation;
        if (lowerAngle === upperAngle) {
          angleAttenuation = polarData[lowerAngle];
        } else {
          const interpPercentage = (angle - lowerAngle) / (upperAngle - lowerAngle);
          angleAttenuation = polarData[lowerAngle] + (polarData[upperAngle] - polarData[lowerAngle]) * interpPercentage;
        }

        const distanceAttenuation = -20 * Math.log10(dist);
        const pointLevel = Math.pow(10, Math.min(sensitivity, sensitivity + distanceAttenuation + angleAttenuation) * 0.1);
        return sum + pointLevel;
      }, 0);

      const pointTotal = 10 * Math.log10(pointSum);
      lineArray.push(pointTotal);
      minSPL = Math.min(minSPL, pointTotal);
      maxSPL = Math.max(maxSPL, pointTotal);
    }
    mapArray.push(lineArray);
  }

  const averageTarget = targetSPL[indexOf(acoustics, currentAcoustics)];
  const yellowLevel = averageTarget + 3;
  const factor = Math.sqrt((3.5 - 3) / Math.pow(maxSPL - yellowLevel, 2));

  mapArray = mapArray.map(line =>
    line.map(point => {
      if (point >= yellowLevel) {
        const aboveYellow = point - yellowLevel;
        const scaledValue = Math.pow(aboveYellow * factor, 2) + 3;
        return averageTarget + scaledValue;
      }
      return point;
    })
  );

  return {
    id: `${area.id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
    averageTarget,
  };
};

/**
 * @function getCameraPosition
 * @description Determines optimal camera position based on room dimensions.
 * Places camera on shorter wall, centered, for optimal coverage.
 * 
 * @param {number} length - Room length in meters
 * @param {number} width - Room width in meters  
 * @param {number} left - Room left offset
 * @param {number} top - Room top offset
 * @returns {Object} Camera position with x and y coordinates
 */
const getCameraPosition = (length, width, left, top, wallOffset = 0.134) => {
  // Camera should always be on the left wall, centered vertically
  // This is the standard default position
  return {
    x: left + wallOffset,     // On the left wall
    y: top + width / 2        // Centered vertically
  };
};

/**
 * Calculates camera placement based on room parameters and constants.
 * @function
 * @param {Object} area - The area object containing room and microphone properties.
 * @param {Object} constants - Constants used in the calculation.
 * @returns {Object} An object containing the calculated quantity and coordinates of microphones.
 */
const calculateCameraPlacement = (area, constants, micCoords = null) => {
  const { model, hasCamera } = area.cameras;
  const isCameraCustom = area.customized.cameras;
  const { location: { top, left }, dimensions: { length, width }, table } = area;
  
  
  let coords = [];
  
  // Camera placement calculation starts here
  
  if (hasCamera && !isCameraCustom && model && model !== 'none') {
    const cameraEquipment = constants.equipment.cameras[model];
    // Camera equipment lookup
    if (cameraEquipment) {
      // Get coverage values from camera equipment configuration
      const {
        modelName,
        coverageAngle,
        coverageRadius,
        isBarCamera = false
      } = cameraEquipment;
      
      // For bar cameras, use the actual microphone position if available.
      // Exception: ceiling-mounted bar mics (CBC) are placed at the room center,
      // so their camera should use the default wall position instead.
      const micModel = area?.microphones?.model;
      const isCeilingMountedBarMic = constants?.equipment?.micCategories?.CBC?.has(micModel);
      let cameraLocation;
      if (isBarCamera && !isCeilingMountedBarMic) {
        // First try to use provided micCoords (for real-time calculations)
        if (micCoords && micCoords.length > 0) {
          cameraLocation = {
            x: micCoords[0].location.x,
            y: micCoords[0].location.y
          };
        }
        // Fallback to area.microphones.layout if available
        else if (area.microphones?.layout?.length > 0) {
          const micPosition = area.microphones.layout[0].location;
          cameraLocation = {
            x: micPosition.x,
            y: micPosition.y
          };
        }
        // Final fallback to default position
        else {
          cameraLocation = getCameraPosition(length, width, left, top, constants.equipment.wallMountedBarOffset);
        }
      } else {
        // Use default position for all standalone cameras
        cameraLocation = getCameraPosition(length, width, left, top, constants.equipment.wallMountedBarOffset);
      }
      
      // Calculate the aim point - camera should aim at room center
      // or table center if table exists
      const aimPoint = {
        x: left + length / 2 + (table?.shape && table?.transform ? (table.transform.x || 0) : 0),  // Room center X + table offset
        y: top + width / 2 + (table?.shape && table?.transform ? (table.transform.y || 0) : 0)     // Room center Y + table offset
      };
      
      
      // Calculate the horAngle here for the camera
      const deltaX = aimPoint.x - cameraLocation.x;
      const deltaY = aimPoint.y - cameraLocation.y;
      
      // Calculate angle in standard math coordinates
      const mathAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
      
      // Convert to rotation convention (0° = east/right)
      const horAngle = Math.round((mathAngle + 360) % 360);
      
      
      coords = [
        {
          model,
          modelName,
          location: cameraLocation,
          aimPoint: aimPoint,
          // Also include flat properties for compatibility with Canvas component
          x: cameraLocation.x,
          y: cameraLocation.y,
          aimX: aimPoint.x,
          aimY: aimPoint.y,
          coverageAngle,
          coverageRadius,
          horAngle  // Add the calculated angle!
        }]
    }
  }
  
  return coords;
};


/**
 * @function buildCameraPlotData
 * @description Shared helper: builds the mapArray and output data structure from a
 * pre-resolved cameraLayout (aimPoint already set by the caller).
 */
const buildCameraPlotData = (id: string, cameraLayout: CameraLayoutDevice[], grid: Grid, cameraConfig: any) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;

  const mapArray: number[][] = [];
  for (let y = 0; y <= gridY; y++) {
    const lineArray: number[] = [];
    for (let x = 0; x <= gridX; x++) {
      const point = { x: gridXOffset + x * gridSpacing, y: gridYOffset + y * gridSpacing };
      const distanceAtPoint = cameraLayout.reduce((minDist, cam) => {
        const dx = cam.location.x - point.x;
        const dy = cam.location.y - point.y;
        return Math.min(minDist, Math.hypot(dx, dy));
      }, Infinity);
      lineArray.push(distanceAtPoint);
    }
    mapArray.push(lineArray);
  }

  const coverageAngle = cameraConfig.coverageAngle || cameraLayout[0]?.coverageAngle;

  return {
    id: `${id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
    camera: {
      ...cameraLayout[0],
      aimPoint: {
        x: cameraLayout[0]?.aimX || cameraLayout[0]?.aimPoint?.x || 0,
        y: cameraLayout[0]?.aimY || cameraLayout[0]?.aimPoint?.y || 0,
      },
      coverageAngle,
    },
    // Strip functions to keep Redux state serializable
    cameraConfig: Object.keys(cameraConfig).reduce((acc, key) => {
      if (typeof cameraConfig[key] !== 'function') acc[key] = cameraConfig[key];
      return acc;
    }, {} as Record<string, unknown>),
  };
};

/**
 * @function calculateBarCameraPlotData
 * @description Camera plot data for VBC bar cameras.
 * Direction is derived purely from the stored `horAngle` (wall-snap: 0°/90°/180°).
 * No adjustAndAim / room-centre fallback needed.
 */
const calculateBarCameraPlotData = (id: string, layout: CameraLayoutDevice[] = [], grid: Grid, cameraModel = 'barCamera', constants: any = {}) => {
  if (layout.length === 0) return {};

  const cameraConfig = constants?.equipment?.cameras?.[cameraModel] || {};

  const cameraLayout = layout.map(camera => {
    const horAngle = camera.horAngle ?? 0;
    const rad = horAngle * Math.PI / 180;
    const aimPoint = {
      x: camera.location.x + Math.cos(rad),
      y: camera.location.y + Math.sin(rad),
    };
    return {
      ...camera,
      aimPoint,
      aimX: aimPoint.x,
      aimY: aimPoint.y,
      x: camera.location.x,
      y: camera.location.y,
    };
  });

  return buildCameraPlotData(id, cameraLayout, grid, cameraConfig);
};

/**
 * @function calculateVidiCameraPlotData
 * @description Camera plot data for Vidi 250 / Vidi 280 standalone cameras.
 * Uses adjustAndAim to compute the aim point (room/table centre with coordinate
 * transform fallback) — the original logic unchanged.
 */
const calculateVidiCameraPlotData = (id: string, layout: CameraLayoutDevice[] = [], grid: Grid, deviceAdjustment: (deviceType: string, device: CameraLayoutDevice, render: boolean) => CameraLayoutDevice, cameraModel = 'vidi250', constants: any = {}) => {
  if (layout.length === 0) return {};

  const cameraConfig = constants?.equipment?.cameras?.[cameraModel] || {};

  const cameraLayout = layout.map(camera => {
    const adjusted = deviceAdjustment('cameras', camera, true);
    const finalCamera = { ...camera, ...adjusted };
    if ((adjusted.aimX === 0 && adjusted.aimY === 0) || (adjusted.aimPoint?.x === 0 && adjusted.aimPoint?.y === 0)) {
      // Only fall back to original aim values if they exist and are within reasonable room bounds
      const hasValidOriginalAim =
        (camera.aimX && camera.aimY &&
          camera.aimX > 0 && camera.aimY > 0 &&
          camera.aimX < 50 && camera.aimY < 50) ||
        (camera.aimPoint?.x && camera.aimPoint?.y &&
          camera.aimPoint.x > 0 && camera.aimPoint.y > 0 &&
          camera.aimPoint.x < 50 && camera.aimPoint.y < 50);

      if (hasValidOriginalAim) {
        if (camera.aimX && camera.aimY) {
          finalCamera.aimX = camera.aimX;
          finalCamera.aimY = camera.aimY;
          finalCamera.aimPoint = { x: camera.aimX, y: camera.aimY };
        } else if (camera.aimPoint?.x && camera.aimPoint?.y) {
          finalCamera.aimX = camera.aimPoint.x;
          finalCamera.aimY = camera.aimPoint.y;
          finalCamera.aimPoint = { x: camera.aimPoint.x, y: camera.aimPoint.y };
        }
      }
    }
    return finalCamera;
  });

  return buildCameraPlotData(id, cameraLayout, grid, cameraConfig);
};

/**
 * @function calculateCameraPlotData
 * @description Dispatcher: routes to calculateBarCameraPlotData (VBC) or
 * calculateVidiCameraPlotData (Vidi 250/280) based on the isBarCamera flag in constants.
 */
const calculateCameraPlotData = (id: string, layout: CameraLayoutDevice[] = [], grid: Grid, deviceAdjustment: (deviceType: string, device: CameraLayoutDevice, render: boolean) => CameraLayoutDevice, cameraModel = 'vidi250', constants: any = {}) => {
  const isBarCamera = constants?.equipment?.cameras?.[cameraModel]?.isBarCamera;
  if (isBarCamera) {
    return calculateBarCameraPlotData(id, layout, grid, cameraModel, constants);
  }
  return calculateVidiCameraPlotData(id, layout, grid, deviceAdjustment, cameraModel, constants);
};


/**
 * Transforms an array of coordinates using a transformer function.
 * @function
 * @param {Array} coordinates - The array of coordinates to transform.
 * @param {Function} transformer - The function to use for transformation.
 * @param {string} [coordProperty=null] - The property name of the coordinate object, if applicable.
 * @returns {Array} The array of transformed coordinates.
 */
const transformCoordinates = (coordinates, transformer, coordProperty = null) => {
  return coordinates.map(item => {
    if (coordProperty && typeof item === 'object' && item.hasOwnProperty(coordProperty)) {
      return {
        ...item,
        [coordProperty]: transformer(item[coordProperty])
      };
    } else if (typeof item === 'object' && 'x' in item && 'y' in item) {
      return {
        ...item,
        ...transformer(item)
      };
    } else {
      return item; // Return unchanged if it doesn't match expected structure
    }
  });
};

/**
 * Finds the closest ceiling tile to a given set of coordinates.
 * @function
 * @param {Array} centers - An array of tile center coordinates.
 * @param {Object} coordinates - The x and y coordinates to find the closest tile to.
 * @returns {Object|undefined} The closest tile object or undefined if no suitable tile is found.
 */
const findClosestTile = (centers: TileCenter[], { x, y }: { x: number; y: number }) => {
  // Safety checks for input parameters
  if (!Array.isArray(centers) || centers.length === 0) {
    return undefined;
  }

  if (typeof x !== 'number' || typeof y !== 'number') {
    return undefined;
  }

  // Get the grid size with safety checks - need to account for rectangular tiles having multiple centers
  // Get unique tile positions by using rx and ry values
  const uniqueTiles: { [key: string]: TileCenter } = centers.reduce((acc: { [key: string]: TileCenter }, center: TileCenter) => {
    if (center && typeof center.rx === 'number' && typeof center.ry === 'number') {
      const key = `${center.rx}_${center.ry}`;
      if (!acc[key]) {
        acc[key] = center;
      }
    }
    return acc;
  }, {});

  const uniqueTileArray = Object.values(uniqueTiles);
  if (uniqueTileArray.length === 0) {
    return undefined;
  }

  // Get grid dimensions from unique tiles
  const uniqueX = [...new Set(uniqueTileArray.map((t: TileCenter) => t.rx))].sort((a, b) => (a ?? 0) - (b ?? 0));
  const uniqueY = [...new Set(uniqueTileArray.map((t: TileCenter) => t.ry))].sort((a, b) => (a ?? 0) - (b ?? 0));

  // Find the closest tile with safety check
  const tile = minBy(centers, (center: TileCenter) => {
    if (!center || typeof center.x !== 'number' || typeof center.y !== 'number') {
      return Infinity;
    }
    return Math.hypot((center.x - x), (center.y - y));
  });

  // Safety check for tile
  if (!tile) {
    return undefined;
  }

  // get the tile grid position using rx and ry
  const idX = tile.rx !== undefined ? tile.rx : 0;
  const idY = tile.ry !== undefined ? tile.ry : 0;

  /**
   * Calculates the center point of a set of tile centers and adds it to the array.
   * This is particularly useful for rectangular tiles.
   * @function
   * @inner
   * @param {Array} tileCenters - An array of tile center objects, each with x and y coordinates.
   * @returns {Array} The input array with an additional center point added.
   */
  const getCenter = (tileCenters) => {
    if (!Array.isArray(tileCenters) || tileCenters.length === 0) {
      return [];
    }

    const validTileCenters = tileCenters.filter(coord => coord && typeof coord.x === 'number' && typeof coord.y === 'number');
    if (validTileCenters.length === 0) {
      return tileCenters;
    }

    const sumX = validTileCenters.reduce((sum, coord) => sum + coord.x, 0);
    const sumY = validTileCenters.reduce((sum, coord) => sum + coord.y, 0);
    const middleX = sumX / validTileCenters.length;
    const middleY = sumY / validTileCenters.length;
    tileCenters.push({
      x: middleX,
      y: middleY,
      blocked: validTileCenters.some(tile => tile && tile.blocked === true)
    });
    return tileCenters
  };

  // If tile is blocked, find closest tile around
  if (tile && tile.blocked === true) {
    // calculate the offset between mic and tile center
    const offsetX = x - (tile?.x || 0);
    const offsetY = y - (tile?.y || 0);

    // Determine which surrounding tiles to check, 0.24384 is 40% of the mask size (i.e. .4 * 0.6096). Thi determines what adjacent tiles to consider
    const xAdj = Math.abs(offsetX) > .24384 ? (offsetX > 0 ? [0, 1] : [-1, 0]) : [-1, 1];
    const yAdj = Math.abs(offsetY) > .24384 ? (offsetY > 0 ? [0, 1] : [-1, 0]) : [-1, 1];

    // Create array of tiles to check using rx/ry coordinates
    const tilesToCheck = [];
    // Get min and max rx/ry values from uniqueX and uniqueY
    const minRx = Math.min(...uniqueX);
    const maxRx = Math.max(...uniqueX);
    const minRy = Math.min(...uniqueY);
    const maxRy = Math.max(...uniqueY);

    for (let ry = max([idY + yAdj[0], minRy]); ry <= min([idY + yAdj[1], maxRy]); ry++) {
      for (let rx = max([idX + xAdj[0], minRx]); rx <= min([idX + xAdj[1], maxRx]); rx++) {
        // Find all centers for this tile position (could be multiple for rectangular tiles)
        const tileCenters = centers.filter(c => c && c.rx === rx && c.ry === ry && c.blocked !== true);
        tilesToCheck.push(...tileCenters);
      };
    };

    // If there are no tiles to check, there's no solution, return
    if (tilesToCheck.length === 0) return undefined;

    // find the closest tile in tilesToCheck that is not blocked
    const validTilesToCheck = tilesToCheck.filter(tile => tile && tile.blocked !== true);
    if (validTilesToCheck.length === 0) return undefined;

    const targetTile = minBy(validTilesToCheck, tile => {
      if (!tile || typeof tile.x !== 'number' || typeof tile.y !== 'number') {
        return Infinity;
      }
      return Math.hypot((tile.x - x), (tile.y - y));
    });

    if (!targetTile) return undefined;

    // Check how many centers does the tile have. This is to refine position in rectangular tiles
    let tileCenters = tilesToCheck.filter(tile =>
      tile &&
      tile.rx === targetTile.rx &&
      tile.ry === targetTile.ry &&
      tile.blocked !== true
    );

    // If there are more than one center it implies is  a rectangular tile. Add the center point and check which is closest
    if (tileCenters.length === 1) {
      return tileCenters[0];
    } else if (tileCenters.length > 1) {
      tileCenters = getCenter(tileCenters);
      const validCenters = tileCenters.filter(tile => tile && tile.blocked !== true);
      if (validCenters.length === 0) return undefined;

      return minBy(validCenters, (tile: TileCenter) => {
        if (!tile || typeof tile.x !== 'number' || typeof tile.y !== 'number') {
          return Infinity;
        }
        return Math.hypot((tile.x - x), (tile.y - y));
      });
    }
    return undefined;
  }

  // If the tile is not blocked check correct center if tile is rectangular
  if (tile && typeof tile.mask === 'number' && tile.mask === 0) {
    return tile;
  } else if (tile && tile.id) {
    const validCenters = centers.filter(center =>
      center &&
      center.id &&
      trimEnd(center.id, "_-10") === trimEnd(tile.id, "_-10")
    );

    if (validCenters.length === 0) return tile;

    const closeCenters = getCenter(validCenters);
    const unblocked = closeCenters.filter(tile => tile && tile.blocked !== true);

    if (unblocked.length === 0) return tile;

    return minBy(unblocked, (centerTile: TileCenter) => {
      if (!centerTile || typeof centerTile.x !== 'number' || typeof centerTile.y !== 'number') {
        return Infinity;
      }
      return Math.hypot((centerTile.x - x), (centerTile.y - y));
    });
  }

  return tile;
};

/**
 * Blocks tiles based on the specified rules for square and rectangular tiles.
 * @function
 * @param {Array} centers - An array of tile center objects.
 * @param {Object} tile - The tile object that was just placed, used to determine blocking rules.
 * @returns {Array} The updated array of tile centers with blocked tiles marked.
 */
export const blockTiles = (centers: TileCenter[], tile: TileCenter): TileCenter[] => {
  // Defensive checks to prevent crashes
  if (!centers || !Array.isArray(centers) || centers.length === 0) {
    console.warn('blockTiles called with invalid centers:', centers);
    return centers;
  }

  // Validate tile parameter
  if (!tile || typeof tile.x !== 'number' || typeof tile.y !== 'number') {
    console.warn('blockTiles called with invalid tile:', tile);
    return centers;
  }

  // Resolve the placed (sub)tile's logical coords (rx/ry/mask). Most callers pass an actual
  // tile-centre object; a couple pass {...centre, x, y} (x/y overridden) or a bare {x, y}. When
  // the logical fields are missing, fall back to the geometrically nearest centre.
  let rx = tile.rx;
  let ry = tile.ry;
  let mask = tile.mask;
  if (rx === undefined || ry === undefined || typeof mask !== 'number') {
    const nearest = minBy(centers, (c: TileCenter) => Math.hypot(c.x - tile.x, c.y - tile.y));
    if (!nearest) return centers;
    rx = nearest.rx; ry = nearest.ry; mask = typeof nearest.mask === 'number' ? nearest.mask : 0;
  }

  const blockMatching = (pred: (c: TileCenter) => boolean) => {
    centers.forEach((c: TileCenter) => { if (c && pred(c)) c.blocked = true; });
  };

  // Always reserve the placed (sub)tile itself.
  blockMatching((c) => c.rx === rx && c.ry === ry && c.mask === mask);

  // 4.2 "center" (square tile, mask 0): block all 4 orthogonally adjacent tiles completely.
  // Non-existent neighbours (off-grid rx/ry) simply match nothing, so no row wrap-around.
  if (mask === 0) {
    blockMatching((c) => Math.abs((c.rx ?? 0) - rx) + Math.abs((c.ry ?? 0) - ry) === 1);
    return centers;
  }

  // 4.2 "left" / "right" (rectangular half tiles, mask +/-1): reserve the complementary (facing)
  // half of the tile across the boundary the occupied half sits on, PLUS both halves of the
  // tile adjacent along the perpendicular axis (the spec's "diagonal partial blocks" clause).
  // The far half of the facing neighbour AND the other half of THIS tile stay usable - the
  // efficient packing the spec intends. The split axis is inferred from the sibling half's
  // position (wide tile -> split along x -> left/right; tall tile -> split along y ->
  // top/bottom).
  const placed = centers.find((c: TileCenter) => c.rx === rx && c.ry === ry && c.mask === mask);
  const sibling = centers.find((c: TileCenter) => c.rx === rx && c.ry === ry && c.mask === -mask);
  const splitAlongX = (placed && sibling)
    ? Math.abs(sibling.x - placed.x) >= Math.abs(sibling.y - placed.y)
    : true;
  const facingRx = splitAlongX ? rx + (mask < 0 ? -1 : 1) : rx;
  const facingRy = splitAlongX ? ry : ry + (mask < 0 ? -1 : 1);
  blockMatching((c) => c.rx === facingRx && c.ry === facingRy && c.mask === -mask);

  // Perpendicular-axis neighbours (both halves) sit at the same tile pitch as the facing
  // neighbour above, just along the axis this tile ISN'T split on - left unblocked, a second
  // device could snap in right next to this one (PIG-627).
  //
  // Diagonal partial blocks: the near (mirrored-mask) half of the tile that is BOTH a
  // perpendicular neighbour AND the facing neighbour's row/column sits exactly one tile-pitch
  // away in both axes (≈2×tile Euclidean) - close enough that leaving it open still reproduces
  // PIG-627 via the diagonal instead of the perpendicular. Only the near half is blocked; the
  // diagonal tile's far half stays usable, matching the spec's "partial" wording.
  if (splitAlongX) {
    blockMatching((c) => c.rx === rx && c.ry === ry - 1);
    blockMatching((c) => c.rx === rx && c.ry === ry + 1);
    blockMatching((c) => c.rx === facingRx && c.ry === ry - 1 && c.mask === -mask);
    blockMatching((c) => c.rx === facingRx && c.ry === ry + 1 && c.mask === -mask);
  } else {
    blockMatching((c) => c.rx === rx - 1 && c.ry === ry);
    blockMatching((c) => c.rx === rx + 1 && c.ry === ry);
    blockMatching((c) => c.rx === rx - 1 && c.ry === facingRy && c.mask === -mask);
    blockMatching((c) => c.rx === rx + 1 && c.ry === facingRy && c.mask === -mask);
  }

  return centers;
};

// Layout filtering logic has been extracted to layoutFilter.ts
